"use client";

import {
  useState,
  useEffect,
  useCallback,
  FormEvent,
  useMemo,
  useRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import { useLanguage } from "@/components/LanguageProvider";
import {
  ArtifactCanvas,
  type RiskAssessmentReportComponent,
} from "./ArtifactCanvas";
import { AssistantMessageActions } from "./AssistantMessageActions";
import { UserMessageActions, UserMessageEditor } from "./UserMessageActions";
import ReactMarkdown, {
  type Components as MarkdownComponents,
} from "react-markdown";
import remarkGfm from "remark-gfm";
import { ensureAccessToken, fetchWithAuth } from "@/lib/fetchWithAuth";
import {
  AGENT_HUB_ITEMS,
  AGENT_LABEL_KEYS,
  getAgentSessionHref,
  isAgentHubItemId,
} from "@/lib/agentHub";
import { unwrapApiData } from "@/lib/apiResponse";
import {
  appendUniqueArtifactSelection,
  fetchRecentArtifactSelectionItems,
  isArtifactSelectionImage,
  isArtifactSelectionSourceDocument,
  type ArtifactSelectionItem,
} from "@/lib/artifactSelection";
import { fetchAllProjects } from "@/lib/projects";
import {
  buildAgentRunPayload,
  cancelAgentRun,
  createAgentRun,
  createAgentSession,
  createTbmAgentRun,
  loadAgentSession,
  openAgentRunEvents,
  updateAgentSession,
  uploadAgentAttachment,
} from "@/lib/agentRunTransport";
import { ArtifactSelectorModal } from "./ArtifactSelectorModal";
import { RecentArtifactThumbnail } from "./RecentArtifactThumbnail";
import { ProjectAvatar } from "@/components/common/ProjectAvatar";
import {
  Brain,
  CheckCircle2,
  FileText,
  FlaskConical,
  Globe,
  ImageIcon,
  PenLine,
  Search,
  SquareTerminal,
  TriangleAlert,
} from "lucide-react";
import type {
  ChatClassicAdapter,
  RiskSiteEvidence,
} from "./chatClassicContract";
import { getSafetyPromptExample, pickSafetyHeadline } from "./safetyHeadlines";
import { stripArtifactMarkupForControlParsing } from "./chatControlMarkup";
import { deriveTbmArtifactTitle } from "./tbmArtifactTitle";
import {
  getQuickFastReasoningPresentation,
  getQuickFastReasoningStatusText,
  isQuickFastReasoningStage,
} from "@/lib/quickFastReasoningPresentation";
import { shouldHideRiskSearchDetails } from "@/lib/riskProgressVisibility";
import { waitForGeneratedSessionTitle } from "@/lib/generatedSessionTitle";
import { APP_RESUME_EVENT, APP_SUSPEND_EVENT } from "@/lib/appLifecycle";
import {
  mergeLiveTextDelta,
  type LiveTextCursor,
  type LiveTextDeltaEvent,
} from "@/lib/liveTextStream";
import {
  MAX_SITE_PHOTO_BYTES,
  MAX_SITE_PHOTOS,
  MAX_SOURCE_DOCUMENT_BYTES,
  MAX_SOURCE_DOCUMENTS,
  SITE_PHOTO_COUNT_ERROR,
  SITE_PHOTO_SIZE_ERROR,
  SOURCE_DOCUMENT_SIZE_ERROR,
} from "@/lib/riskAttachmentLimits";
import { getServicePromptPlaceholder } from "@/lib/promptExamples";
import { useAppPreferences } from "@/components/AccentColorProvider";
import { getDeviceContextSnapshot } from "@/lib/deviceContext";
import { ReasoningStyleSelector } from "./ReasoningStyleSelector";

type ClassicFormConfig = Pick<
  ChatClassicAdapter,
  | "title"
  | "fields"
  | "processCategories"
  | "multiBadgeOptions"
  | "numberLimits"
>;

type ImgSrc = { kind: "url"; url: string } | { kind: "file"; fileId: string };

type RiskAttachmentKind = "site_photo" | "source_document";
type DurableRunTextEvent = {
  sequence?: unknown;
  attempt?: unknown;
  type?: string;
  textOffset?: unknown;
  text?: unknown;
  scope?: string;
  agentId?: string;
  actionId?: string;
  content?: unknown;
};
type RiskAttachment = {
  fileId: string;
  kind: RiskAttachmentKind;
  name?: string;
  mimeType?: string;
  size?: number;
  format?: string;
  characterCount?: number;
  readiness?: "ready";
};

type PendingAttachment = {
  localId: string;
  file: File;
  name: string;
  mimeType: string;
  previewUrl?: string;
  // 'local': 브라우저에만 보관 (전송 전). 'uploading': 전송 순간 /api/files로 업로드 중.
  // 'ready': fileId 발급 완료. 'failed': 업로드 실패 — 사용자 재시도 대상.
  kind: RiskAttachmentKind;
  status: "local" | "uploading" | "parsing" | "ready" | "failed";
  fileId?: string;
  format?: string;
  characterCount?: number;
  error?: string;
};

// Source documents are also supported by the home Supervisor. It preserves
// Core's untrusted-document boundary and forwards the material only when the
// user's intent requires the safety/risk-assessment specialist.
const SOURCE_DOCUMENT_AGENT_IDS = new Set([
  "supervisorAgent",
  "risk-assessment",
  "demolition-risk-assessment",
]);
const RISK_SITE_PHOTO_LIMIT_AGENT_IDS = new Set([
  "risk-assessment",
  "object-risk-assessment",
  "demolition-risk-assessment",
]);
const SOURCE_DOCUMENT_EXTENSIONS = new Set([
  "pdf",
  "doc",
  "docx",
  "hwp",
  "hwpx",
  "xls",
  "xlsx",
]);
const SOURCE_DOCUMENT_ACCEPT = ".pdf,.doc,.docx,.hwp,.hwpx,.xls,.xlsx";
const fileExtension = (name: string) =>
  name.toLowerCase().split(".").pop() ?? "";
const isSourceDocumentFile = (file: Pick<File, "name" | "type">) =>
  !file.type.startsWith("image/") &&
  SOURCE_DOCUMENT_EXTENSIONS.has(fileExtension(file.name));

const normalizeRiskAttachment = (value: unknown): RiskAttachment | null => {
  if (!value || typeof value !== "object") return null;
  const item = value as Record<string, unknown>;
  const fileId = item.fileId ?? item.file_id ?? item.id;
  const kind = item.kind ?? item.attachmentKind ?? item.attachment_kind;
  if (
    (typeof fileId !== "string" && typeof fileId !== "number") ||
    (kind !== "site_photo" && kind !== "source_document")
  )
    return null;
  return {
    fileId: String(fileId),
    kind,
    name:
      typeof item.name === "string"
        ? item.name
        : typeof item.originalName === "string"
          ? item.originalName
          : undefined,
    mimeType: typeof item.mimeType === "string" ? item.mimeType : undefined,
    size: typeof item.size === "number" ? item.size : undefined,
    format: typeof item.format === "string" ? item.format : undefined,
    characterCount:
      typeof item.characterCount === "number" ? item.characterCount : undefined,
    readiness: item.readiness === "ready" ? "ready" : undefined,
  };
};

const normalizeRiskAttachmentList = (
  values: unknown,
  legacyImageIds?: unknown,
): RiskAttachment[] => {
  const parsed = Array.isArray(values)
    ? values
        .map(normalizeRiskAttachment)
        .filter((item): item is RiskAttachment => item !== null)
    : [];
  if (parsed.length > 0) return parsed;
  return Array.isArray(legacyImageIds)
    ? legacyImageIds
        .filter(
          (id): id is string | number =>
            typeof id === "string" || typeof id === "number",
        )
        .map((fileId) => ({
          fileId: String(fileId),
          kind: "site_photo" as const,
        }))
    : [];
};

const isSourceDocumentAttachment = (
  attachment: RiskAttachment,
): attachment is RiskAttachment & { kind: "source_document" } =>
  attachment.kind === "source_document";

const attachmentStatusLabel = (
  attachment: Pick<PendingAttachment, "kind" | "status">,
) => {
  if (attachment.status === "uploading") return "업로드 중";
  if (attachment.status === "parsing") return "문서 분석 중";
  if (attachment.status === "ready") return "분석 완료";
  if (attachment.status === "failed") return "실패";
  return attachment.kind === "source_document" ? "분석 대기" : "업로드 대기";
};

/**
 * Full-screen image viewer modal. Mirrors ArtifactCanvas's image viewer:
 * portal to body, backdrop click + Esc to close, prev/next navigation, counter.
 */
const ChatImageViewer = ({
  images,
  index,
  onClose,
  onIndexChange,
}: {
  images: ImgSrc[];
  index: number;
  onClose: () => void;
  onIndexChange: (i: number) => void;
}) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft" && index > 0) onIndexChange(index - 1);
      else if (e.key === "ArrowRight" && index < images.length - 1)
        onIndexChange(index + 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, images.length, onClose, onIndexChange]);

  if (typeof document === "undefined") return null;
  const cur = images[index];
  const imgClass =
    "max-w-full max-h-[85vh] rounded-xl shadow-2xl object-contain";

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className="relative max-w-[90vw] max-h-[90vh] flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center transition-colors shadow-lg z-10"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {index > 0 && (
          <button
            onClick={() => onIndexChange(index - 1)}
            className="absolute -left-14 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition-colors shadow-lg max-md:left-2"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
        )}

        {cur.kind === "url" ? (
          <img src={cur.url} alt="" className={imgClass} />
        ) : (
          <AuthImage fileId={cur.fileId} className={imgClass} />
        )}

        {index < images.length - 1 && (
          <button
            onClick={() => onIndexChange(index + 1)}
            className="absolute -right-14 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition-colors shadow-lg max-md:right-2"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="9 6 15 12 9 18" />
            </svg>
          </button>
        )}

        {images.length > 1 && (
          <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-black/60 text-white text-xs font-bold">
            {index + 1} / {images.length}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

/**
 * Renders an authenticated image fetched from /api/files/:id.
 * Uses fetch + blob URL because <img src> can't send Authorization headers.
 */
const AuthImage = ({
  fileId,
  className,
}: {
  fileId: string;
  className?: string;
}) => {
  const [src, setSrc] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let createdUrl: string | null = null;
    const apiUrl = process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000";
    fetchWithAuth(`${apiUrl}/api/files/${fileId}/content`, {
      skipRedirectOn401: true,
    })
      .then((res) => {
        if (!res.ok) throw new Error(`status ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setSrc(createdUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [fileId]);

  if (failed) {
    return (
      <div
        className={`${className ?? ""} flex items-center justify-center bg-gray-200 dark:bg-gray-800 text-[10px] text-gray-500`}
      >
        로드 실패
      </div>
    );
  }
  if (!src) {
    return (
      <div
        className={`${className ?? ""} bg-gray-200 dark:bg-gray-800 animate-pulse`}
      />
    );
  }
  return <img src={src} className={className} alt="" />;
};

const formatFileSize = (bytes?: number) => {
  if (!bytes || bytes < 1) return "";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(bytes >= 10 * 1024 * 1024 ? 0 : 1)}MB`;
};

const DocumentAttachmentCard = ({
  attachment,
}: {
  attachment: RiskAttachment;
}) => {
  const [metadata, setMetadata] = useState<RiskAttachment>(attachment);
  const [isDownloading, setIsDownloading] = useState(false);
  const apiUrl = process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000";

  useEffect(() => {
    if (attachment.name && attachment.mimeType && attachment.size !== undefined)
      return;
    let cancelled = false;
    fetchWithAuth(`${apiUrl}/api/files/${attachment.fileId}`, {
      skipRedirectOn401: true,
    })
      .then(async (response) => (response.ok ? response.json() : null))
      .then((payload) => {
        if (!payload || cancelled) return;
        const unwrapped = unwrapApiData<unknown>(payload);
        if (!unwrapped || typeof unwrapped !== "object") return;
        const record = unwrapped as Record<string, unknown>;
        const source =
          record.file && typeof record.file === "object"
            ? (record.file as Record<string, unknown>)
            : record;
        const extraction =
          source.extraction && typeof source.extraction === "object"
            ? (source.extraction as Record<string, unknown>)
            : {};
        const rawSize = source.size ?? source.fileSize ?? source.file_size;
        const normalizedSize =
          typeof rawSize === "number"
            ? rawSize
            : typeof rawSize === "string" && Number.isFinite(Number(rawSize))
              ? Number(rawSize)
              : undefined;
        setMetadata((previous) => ({
          ...previous,
          name:
            typeof source.originalName === "string"
              ? source.originalName
              : typeof source.name === "string"
                ? source.name
                : previous.name,
          mimeType:
            typeof source.mimeType === "string"
              ? source.mimeType
              : previous.mimeType,
          size: normalizedSize ?? previous.size,
          format:
            typeof source.format === "string"
              ? source.format
              : typeof extraction.format === "string"
                ? extraction.format
                : previous.format,
          characterCount:
            typeof source.characterCount === "number"
              ? source.characterCount
              : typeof extraction.characterCount === "number"
                ? extraction.characterCount
                : previous.characterCount,
        }));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [
    apiUrl,
    attachment.fileId,
    attachment.mimeType,
    attachment.name,
    attachment.size,
  ]);

  const download = async () => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      const response = await fetchWithAuth(
        `${apiUrl}/api/files/${attachment.fileId}/content`,
      );
      if (!response.ok) throw new Error(`download failed (${response.status})`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = metadata.name || "source-document";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch {
      alert("문서를 다운로드하지 못했습니다. 잠시 후 다시 시도해주세요.");
    } finally {
      setIsDownloading(false);
    }
  };

  const detail = [
    metadata.format?.toUpperCase() ||
      fileExtension(metadata.name || "").toUpperCase(),
    formatFileSize(metadata.size),
    "분석 완료",
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="flex min-w-[230px] max-w-[300px] items-center gap-3 rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-left shadow-sm dark:border-[#2a2a2e] dark:bg-[#1f1f22]">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-seexr-solid/10 text-seexr-primary">
        <svg
          width="19"
          height="19"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
        </svg>
      </div>
      <div className="min-w-0 flex-1">
        <p
          className="truncate text-[12px] font-bold text-gray-800 dark:text-gray-200"
          title={metadata.name}
        >
          {metadata.name || "참고 문서"}
        </p>
        <p className="mt-0.5 text-[10px] font-semibold text-gray-400">
          {detail}
        </p>
      </div>
      <button
        type="button"
        onClick={download}
        disabled={isDownloading}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 hover:text-seexr-primary disabled:opacity-50 dark:hover:bg-[#2a2a2e]"
        aria-label={`${metadata.name || "문서"} 다운로드`}
      >
        {isDownloading ? (
          <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
        ) : (
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.25"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
        )}
      </button>
    </div>
  );
};

const copyText = async (text: string) => {
  if (typeof navigator === "undefined" || !navigator.clipboard) return false;
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

const ToggleChevron = ({
  open,
  className = "",
}: {
  open: boolean;
  className?: string;
}) => (
  <span
    className={`inline-flex shrink-0 text-gray-400 transition-[color,transform] duration-200 group-hover:text-gray-800 dark:group-hover:text-white ${open ? "rotate-90" : ""} ${className}`}
    aria-hidden="true"
  >
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m9 18 6-6-6-6" />
    </svg>
  </span>
);

type ActionToggleRowProps = {
  label: ReactNode;
  icon?: ReactNode;
  toggleable?: boolean;
  open?: boolean;
  onToggle?: () => void;
  loading?: boolean;
  className?: string;
  labelClassName?: string;
};

const ActionToggleRow = ({
  label,
  icon,
  toggleable = false,
  open = false,
  onToggle,
  loading = false,
  className = "",
  labelClassName = "",
}: ActionToggleRowProps) => {
  const baseClass = `flex min-h-9 w-full items-center gap-2 rounded-md px-2.5 text-left text-[15px] transition-colors ${toggleable ? "group cursor-pointer hover:text-gray-800 dark:hover:text-white" : "cursor-default"} ${loading ? "agent-action-loading" : ""} ${className}`;
  const contents = (
    <>
      {icon && (
        <span
          className={`inline-flex shrink-0 ${loading ? "agent-action-loading-icon" : ""}`}
        >
          {icon}
        </span>
      )}
      <span
        className={`min-w-0 truncate transition-colors ${toggleable ? "group-hover:text-gray-800 dark:group-hover:text-white" : ""} ${loading ? "agent-action-loading-text" : ""} ${labelClassName}`}
      >
        {label}
      </span>
      {toggleable && <ToggleChevron open={open} />}
    </>
  );
  if (!toggleable) return <div className={baseClass}>{contents}</div>;
  return (
    <button
      type="button"
      aria-expanded={open}
      onClick={onToggle}
      className={baseClass}
    >
      {contents}
    </button>
  );
};

const ActionToggleContent = ({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) => (
  <div className={`mb-2 ml-2.5 mt-1.5 min-w-0 ${className}`}>{children}</div>
);

const ActionTogglePanel = ({
  open,
  children,
  className = "",
}: {
  open: boolean;
  children: ReactNode;
  className?: string;
}) => (
  <div
    className={`action-toggle-panel ${open ? "action-toggle-panel-open" : ""} ${className}`}
    aria-hidden={!open}
  >
    <div className="min-h-0 overflow-hidden">{children}</div>
  </div>
);

const ActionBlock = ({
  title,
  content,
  type = "action",
}: {
  title: string;
  content: string;
  type?: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const cleanContent = stripXmlForDisplay(content);
  const summary = truncatePreview(cleanContent, 110);
  const isThinking = type === "think";

  const handleCopy = async () => {
    if (!(await copyText(cleanContent))) return;
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div
      className={`mb-2 overflow-hidden rounded-xl border ${isThinking ? "border-gray-200/80 bg-gray-50/70 dark:border-gray-800 dark:bg-gray-900/30" : "border-seexr-100 bg-seexr-50/50 dark:border-seexr-900/50 dark:bg-seexr-950/15"}`}
    >
      <button
        type="button"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
        className="flex w-full items-start gap-3 px-3.5 py-3 text-left transition-colors hover:bg-white/60 dark:hover:bg-white/[0.03]"
      >
        <span
          className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${isThinking ? "bg-gray-200/70 text-gray-600 dark:bg-gray-800 dark:text-gray-300" : "bg-seexr-solid/10 text-seexr-primary"}`}
        >
          {isThinking ? "✦" : "·"}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className="truncate text-[13px] font-bold text-gray-800 dark:text-gray-100">
              {isThinking ? "응답 준비 과정" : title}
            </span>
            <ToggleChevron open={isOpen} />
            <span className="rounded-full bg-gray-200/70 px-2 py-0.5 text-[10px] font-bold text-gray-500 dark:bg-gray-800 dark:text-gray-400">
              {isThinking ? "처리 내용" : "완료"}
            </span>
          </span>
          {!isOpen && summary && (
            <span className="mt-1 block truncate text-[12px] text-gray-500 dark:text-gray-400">
              {summary}
            </span>
          )}
        </span>
      </button>
      {isOpen && (
        <div className="border-t border-inherit px-3.5 pb-3.5 pt-3">
          <div className="max-h-72 overflow-y-auto whitespace-pre-wrap rounded-lg bg-white/80 px-3 py-2.5 text-[13px] leading-relaxed text-gray-700 dark:bg-black/15 dark:text-gray-300 custom-scrollbar">
            {cleanContent}
          </div>
          {cleanContent && (
            <button
              type="button"
              onClick={handleCopy}
              className="mt-2 rounded-md px-2 py-1 text-[11px] font-semibold text-gray-500 hover:bg-white hover:text-gray-800 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100"
            >
              {copied ? "복사됨" : "내용 복사"}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

type ToolCallType = {
  toolCallId: string;
  toolName: string;
  status: "running" | "success" | "error";
  inputPreview?: string;
  resultPreview?: string;
};
type AgentActionResultType = {
  hazard?: string;
  task?: string;
  cause?: string;
  currentMeasure?: string;
  reductionMeasure?: string;
  frequency?: string;
  severity?: string;
  riskLevel?: string;
  basis?: string;
};
type AgentSubtaskType = {
  id: string;
  title: string;
  durationMs?: number;
  results?: AgentActionResultType[];
  resultSummary?: string;
};
type AgentActionTextType = {
  actionId?: string;
  content: string;
  attempt?: number;
};
type WebReferenceType = { title: string; url: string; snippet?: string };
type WebSearchResultGroupType = { query: string; results: WebReferenceType[] };
type PipelineStepType = {
  agentId: string;
  agentName: string;
  step: number;
  totalSteps: number;
  status: "pending" | "running" | "complete" | "error";
  durationMs?: number;
  accumulatedText?: string;
  actions?: AgentActionTextType[];
  toolCalls?: ToolCallType[];
  subtasks?: AgentSubtaskType[];
  resultSummary?: string;
  webSearches?: WebSearchResultGroupType[];
  references?: WebReferenceType[];
};
type ComposerPlanStepType = {
  id: string;
  title: string;
  status: "pending" | "running" | "complete" | "error";
  detail?: string;
};

const normalizeWebReference = (value: unknown): WebReferenceType | null => {
  if (!value || typeof value !== "object") return null;
  const source = value as Record<string, unknown>;
  if (typeof source.url !== "string") return null;
  try {
    const url = new URL(source.url);
    if (!["http:", "https:"].includes(url.protocol)) return null;
    return {
      title:
        typeof source.title === "string" && source.title.trim()
          ? source.title.trim()
          : url.hostname,
      url: url.href,
      ...(typeof source.snippet === "string" && source.snippet.trim()
        ? { snippet: source.snippet.trim() }
        : {}),
    };
  } catch {
    return null;
  }
};

const normalizeWebSearch = (
  value: unknown,
): WebSearchResultGroupType | null => {
  if (!value || typeof value !== "object") return null;
  const source = value as Record<string, unknown>;
  if (typeof source.query !== "string" || !Array.isArray(source.results))
    return null;
  return {
    query: source.query.trim(),
    results: source.results
      .map(normalizeWebReference)
      .filter((reference): reference is WebReferenceType => reference !== null),
  };
};

const appendWebSearchToSteps = (
  steps: PipelineStepType[],
  agentId: string,
  value: unknown,
) => {
  const search = normalizeWebSearch(value);
  if (!search) return steps;
  return steps.map((step) =>
    step.agentId === agentId
      ? { ...step, webSearches: [...(step.webSearches ?? []), search] }
      : step,
  );
};

const appendWebReferenceToSteps = (
  steps: PipelineStepType[],
  agentId: string,
  value: unknown,
) => {
  const reference = normalizeWebReference(value);
  if (!reference) return steps;
  return steps.map((step) => {
    if (step.agentId !== agentId) return step;
    const references = step.references ?? [];
    return references.some((item) => item.url === reference.url)
      ? step
      : { ...step, references: [...references, reference] };
  });
};

const getReferenceSiteName = (url: string) => {
  try {
    const parts = new URL(url).hostname
      .replace(/^www\./, "")
      .toLowerCase()
      .split(".")
      .filter(Boolean);
    const last = parts[parts.length - 1];
    const secondLast = parts[parts.length - 2];
    if (
      parts.length >= 3 &&
      last?.length === 2 &&
      ["ac", "co", "go", "ne", "or", "re"].includes(secondLast ?? "")
    ) {
      return parts[parts.length - 3] ?? parts[0] ?? "출처";
    }
    return parts.length >= 2
      ? (secondLast ?? parts[0] ?? "출처")
      : (parts[0] ?? "출처");
  } catch {
    return "출처";
  }
};

const getReferenceHostname = (url: string) => {
  try {
    return new URL(url).hostname;
  } catch {
    return "";
  }
};

const collectWebReferences = (steps?: PipelineStepType[]) => {
  if (!steps?.length) return [];
  const searched = steps
    .flatMap(
      (step) => step.webSearches?.flatMap((search) => search.results) ?? [],
    )
    .map(normalizeWebReference)
    .filter((reference): reference is WebReferenceType => reference !== null);
  const read = steps
    .flatMap((step) => step.references ?? [])
    .map(normalizeWebReference)
    .filter((reference): reference is WebReferenceType => reference !== null);
  const candidates = read.length > 0 ? read : searched.slice(0, 3);
  const seen = new Set<string>();
  return candidates.filter((reference) => {
    if (seen.has(reference.url)) return false;
    seen.add(reference.url);
    return true;
  });
};
const getClassicSuggestions = (
  options: string[],
  query: string,
  limit = 10,
) => {
  const normalized = query.trim().toLowerCase();
  const filtered = normalized
    ? options.filter((option) => option.toLowerCase().includes(normalized))
    : options;
  return filtered.slice(0, limit);
};

const ClassicSuggestionPanel = ({
  isOpen,
  options,
  selectedValue,
  onSelect,
}: {
  isOpen: boolean;
  options: string[];
  selectedValue?: string;
  onSelect: (value: string) => void;
}) => {
  if (!isOpen || options.length === 0) return null;

  return (
    <div className="absolute left-0 right-0 top-[calc(100%+6px)] z-40 max-h-56 overflow-y-auto rounded-xl border border-gray-200 bg-white py-1 shadow-[0_10px_30px_rgba(15,23,42,0.12)] dark:border-[#2a2a2e] dark:bg-[#1a1a1c]">
      {options.map((option) => {
        const isSelected = selectedValue === option;
        return (
          <button
            key={option}
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onSelect(option)}
            className={`flex w-full items-center px-3.5 py-2 text-left text-[13px] font-semibold transition-colors ${
              isSelected
                ? "bg-seexr-solid/10 text-seexr-primary"
                : "text-gray-700 hover:bg-gray-50 hover:text-seexr-primary dark:text-gray-200 dark:hover:bg-[#242428]"
            }`}
          >
            <span className="min-w-0 truncate">{option}</span>
          </button>
        );
      })}
    </div>
  );
};

const parseClassicTokens = (value?: string) =>
  value
    ? value
        .split(",")
        .map((token) => token.trim())
        .filter(Boolean)
    : [];

const joinClassicTokens = (tokens: string[]) => tokens.join(", ");
const sanitizeClassicToken = (value: string) =>
  value.trim().replace(/,/g, "，");

const TOOL_LABELS: Record<string, string> = {
  accidentSearchTool: "사고사례 검색",
  similarCasesTool: "유사 사례 분석",
  riskAssessmentDbTool: "위험요인 검색",
  "risk-assessment-db": "위험요인 검색",
  playwrightTool: "웹 데이터 수집",
  browserSearchTool: "웹 검색",
  "browser-search": "웹 검색",
  browserReadTool: "웹 원문 확인",
  "browser-read": "웹 원문 확인",
  askUserTool: "추가 정보 확인",
  "ask-user": "추가 정보 확인",
  callTbmScenarioAgentTool: "TBM 시나리오 구성",
  "call-tbm-scenario-agent": "TBM 시나리오 구성",
  callTbmRecordAgentTool: "TBM 기록지 작성",
  "call-tbm-record-agent": "TBM 기록지 작성",
  finalizeTbmScenarioArtifactTool: "TBM 시나리오 최종 확인",
  "finalize-tbm-scenario-artifact": "TBM 시나리오 최종 확인",
  finalizeTbmRecordArtifactTool: "TBM 기록지 최종 확인",
  "finalize-tbm-record-artifact": "TBM 기록지 최종 확인",
};

const stripXmlForDisplay = (text: string) =>
  text
    .replace(/<\/?[\w\s="'/:.-]+>/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

const extractXmlTagForDisplay = (tag: string, text: string) => {
  const match = text.match(
    new RegExp(`<${tag}(?:\\s+[^>]*)?>([\\s\\S]*?)</${tag}>`, "i"),
  );
  return match ? stripXmlForDisplay(match[1]) : "";
};

const truncatePreview = (text: string, max = 150) => {
  const clean = text.replace(/\s+/g, " ").trim();
  return clean.length > max ? `${clean.slice(0, max - 1)}…` : clean;
};

const eventValuePreview = (value: unknown, max = 500) => {
  if (value == null) return "";
  if (typeof value === "string")
    return truncatePreview(stripXmlForDisplay(value), max);
  try {
    return truncatePreview(JSON.stringify(value, null, 2), max);
  } catch {
    return "";
  }
};

const toolEventPreview = (
  event: Record<string, unknown>,
  kind: "input" | "result",
) => {
  const candidates =
    kind === "input"
      ? [event.input, event.args, event.arguments, event.parameters]
      : [event.result, event.output, event.data, event.error, event.message];
  return eventValuePreview(candidates.find((value) => value != null));
};

const ToolCallCard = ({ toolCall }: { toolCall: ToolCallType }) => {
  const [isOpen, setIsOpen] = useState(false);
  const hasDetails = Boolean(toolCall.inputPreview || toolCall.resultPreview);
  const label = TOOL_LABELS[toolCall.toolName] ?? toolCall.toolName;
  const statusLabel =
    toolCall.status === "running"
      ? "실행 중"
      : toolCall.status === "error"
        ? "실패"
        : "완료";
  const ToolIcon = /^browser(?:SearchTool|ReadTool|-search|-read)$/i.test(
    toolCall.toolName,
  )
    ? Globe
    : /tbm/i.test(toolCall.toolName)
      ? SquareTerminal
      : label.includes("검색") || label.includes("확인")
        ? Search
        : label.includes("분석")
          ? FlaskConical
          : PenLine;

  return (
    <div className="my-0.5 overflow-hidden">
      <ActionToggleRow
        toggleable={hasDetails}
        open={isOpen}
        onToggle={() => setIsOpen((open) => !open)}
        loading={toolCall.status === "running"}
        className="text-gray-500 dark:text-gray-400"
        icon={
          toolCall.status === "error" ? (
            <TriangleAlert
              size={18}
              strokeWidth={1.8}
              className="shrink-0 text-gray-400 transition-colors group-hover:text-gray-600 dark:group-hover:text-gray-200"
            />
          ) : (
            <ToolIcon
              size={18}
              strokeWidth={1.8}
              className="shrink-0 text-gray-400 transition-colors group-hover:text-gray-600 dark:group-hover:text-gray-200"
            />
          )
        }
        label={`${label} ${statusLabel}`}
      />
      <ActionTogglePanel open={isOpen && hasDetails}>
        <ActionToggleContent className="space-y-2 px-2.5 py-2 text-[11px]">
          {toolCall.inputPreview && (
            <div>
              <p className="mb-0.5 font-bold text-gray-500">요청한 내용</p>
              <p className="whitespace-pre-wrap text-gray-600 dark:text-gray-400">
                {toolCall.inputPreview}
              </p>
            </div>
          )}
          {toolCall.resultPreview && (
            <div>
              <p className="mb-0.5 font-bold text-gray-500">확인한 결과</p>
              <p className="whitespace-pre-wrap text-gray-600 dark:text-gray-400">
                {toolCall.resultPreview}
              </p>
            </div>
          )}
        </ActionToggleContent>
      </ActionTogglePanel>
    </div>
  );
};

const getPhotoAnalysisPreview = (text?: string) => {
  if (!text) return "";

  const taggedSummary =
    extractXmlTagForDisplay("photo_analysis_summary", text) ||
    extractXmlTagForDisplay("summary", text);
  if (taggedSummary) return truncatePreview(taggedSummary);

  const markerMatch = text.match(/\[사진 분석 요약\]\s*([\s\S]*)/i);
  if (markerMatch?.[1])
    return truncatePreview(stripXmlForDisplay(markerMatch[1]));

  const hazardBlocks = [
    ...text.matchAll(/<hazard\b[^>]*>([\s\S]*?)<\/hazard>/gi),
  ].map((m) => m[1]);
  const hazards = hazardBlocks
    .map((block) => {
      const type = extractXmlTagForDisplay("type", block);
      const desc = extractXmlTagForDisplay("description", block);
      return [type, desc].filter(Boolean).join(": ");
    })
    .filter(Boolean);

  if (hazards.length > 0) {
    return truncatePreview(
      `사진에서 ${hazards.slice(0, 3).join(", ")} 위험요인이 식별되었습니다.`,
    );
  }

  return "";
};

const AgentResultPreview = ({ step }: { step: PipelineStepType }) => {
  if (step.agentId !== "photoAgent") return null;
  const preview = getPhotoAnalysisPreview(step.accumulatedText);
  if (!preview) return null;

  return (
    <div className="mb-1.5 rounded-md bg-seexr-50/70 dark:bg-seexr-950/20 border border-seexr-100 dark:border-seexr-900/40 px-2 py-1.5">
      <p className="text-[11px] leading-snug text-seexr-700 dark:text-seexr-300 font-medium">
        사진 분석: {preview}
      </p>
    </div>
  );
};

const AGENT_FRIENDLY_NAMES: Record<string, string> = {
  riskAgent: "위험요인 분석",
  photoAgent: "현장 사진 분석",
  accidentAgent: "사고사례 조사",
  legalAgent: "관련 법령 확인",
  legalBasisAgent: "위험 항목별 법령 근거 확인",
  browserAgent: "웹 검색",
  formatterAgent: "결과 문서 작성",
  finalEvalAgent: "결과 검토",
  tbmSupervisorAgent: "TBM 교육 자료 구성",
  tbmScenarioAgent: "TBM 시나리오 구성",
  tbmRecordAgent: "TBM 기록지 작성",
};

const TBM_AGENT_IDS = new Set([
  "tbmSupervisorAgent",
  "tbmScenarioAgent",
  "tbmRecordAgent",
]);
const SUPERVISOR_AGENT_IDS = new Set([
  "supervisorAgent",
  "quick-fast-v1.0.0",
  "deep-pro-v1.0.0",
]);

const getAgentWorkSummary = (step: PipelineStepType) => {
  const text = step.accumulatedText ?? "";
  const riskBlocks = [
    ...text.matchAll(/<risk\b[^>]*>([\s\S]*?)<\/risk>/gi),
  ].map((match) => match[1]);
  const itemCount = (text.match(/<item\b[^>]*>/gi) ?? []).length;
  const hazards = riskBlocks
    .map(
      (block) =>
        extractXmlTagForDisplay("hazard", block) ||
        extractXmlTagForDisplay("risk_category", block),
    )
    .filter(Boolean)
    .filter((value, index, values) => values.indexOf(value) === index);
  const taggedSummary =
    extractXmlTagForDisplay("summary", text) ||
    extractXmlTagForDisplay("photo_analysis_summary", text);
  const friendlyName =
    getQuickFastReasoningPresentation(step.agentId)?.title ??
    AGENT_FRIENDLY_NAMES[step.agentId] ??
    (step.agentName.replace(/\s*에이전트\s*$/, "") || "에이전트 작업");

  if (step.agentId === "riskAgent" || riskBlocks.length > 0) {
    return {
      title: "위험성평가 진행 내역",
      description:
        step.status === "running"
          ? "작업 조건과 사고 데이터를 비교해 위험요인을 찾고 있습니다."
          : `${riskBlocks.length || itemCount}개의 위험요인을 확인해 평가표 작성 단계로 전달했습니다.`,
      chips: hazards,
      tasks: [
        {
          title: "위험요인 검색",
          detail: "작업 조건과 유사한 위험요인 데이터를 확인했습니다.",
        },
        {
          title: "위험요인 분석",
          detail: `${riskBlocks.length || itemCount}개의 위험요인을 평가하고 우선순위를 정리했습니다.`,
        },
      ],
    };
  }
  if (step.agentId === "formatterAgent") {
    return {
      title: friendlyName,
      description:
        step.status === "running"
          ? "분석 결과를 사용자가 확인하기 쉬운 위험성평가표로 정리하고 있습니다."
          : `${itemCount ? `${itemCount}개 항목을 ` : ""}위험성평가표 형식으로 정리했습니다.`,
      chips: [] as string[],
      tasks: [] as Array<{ title: string; detail: string }>,
    };
  }
  if (taggedSummary) {
    return {
      title: friendlyName,
      description: truncatePreview(taggedSummary, 180),
      chips: [] as string[],
      tasks: [] as Array<{ title: string; detail: string }>,
    };
  }
  return {
    title: friendlyName,
    description:
      step.status === "running"
        ? "요청 내용을 분석하고 있습니다."
        : "분석을 마치고 다음 작업에 필요한 결과를 전달했습니다.",
    chips: [] as string[],
    tasks: [] as Array<{ title: string; detail: string }>,
  };
};

const TECHNICAL_FIELD_LABELS: Record<string, string> = {
  hazard: "위험요인",
  risk_category: "단위 작업",
  risk_cause: "발생 원인",
  current_safety_measure: "현재 안전조치",
  reduction_measure: "개선 대책",
  prob_freq: "빈도",
  severity: "강도",
  risk_level: "위험도",
  risk_level_verbal: "위험 수준",
  score_source: "평가 근거",
};

const getTechnicalRows = (text: string) => {
  const riskBlocks = [
    ...text.matchAll(/<risk\b[^>]*>([\s\S]*?)<\/risk>/gi),
  ].map((match) => match[1]);
  const itemBlocks = [
    ...text.matchAll(/<item\b[^>]*>([\s\S]*?)<\/item>/gi),
  ].map((match) => match[1]);
  const blocks = riskBlocks.length > 0 ? riskBlocks : itemBlocks;
  const fields = Object.keys(TECHNICAL_FIELD_LABELS);

  if (blocks.length > 0) {
    return blocks.flatMap((block, index) =>
      fields
        .map((field) => ({
          group: index + 1,
          field,
          value: extractXmlTagForDisplay(field, block),
        }))
        .filter((row) => row.value),
    );
  }

  return fields
    .map((field) => ({
      group: 1,
      field,
      value: extractXmlTagForDisplay(field, text),
    }))
    .filter((row) => row.value);
};

const TechnicalInfoTable = ({ text }: { text: string }) => {
  const rows = getTechnicalRows(text);
  if (rows.length === 0) {
    return (
      <p className="whitespace-pre-wrap font-mono text-[10px] leading-relaxed text-gray-400">
        {stripXmlForDisplay(text)}
      </p>
    );
  }

  return (
    <div className="max-h-72 overflow-auto rounded-lg border border-gray-200 dark:border-gray-800 custom-scrollbar">
      <table className="w-full min-w-[520px] border-collapse text-left text-[11px]">
        <thead className="sticky top-0 bg-gray-100 text-gray-500 dark:bg-[#242428] dark:text-gray-400">
          <tr>
            <th className="w-16 px-2.5 py-2 font-bold">항목</th>
            <th className="w-28 px-2.5 py-2 font-bold">구분</th>
            <th className="px-2.5 py-2 font-bold">내용</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white/70 dark:divide-gray-800 dark:bg-black/10">
          {rows.map((row, index) => (
            <tr key={`${row.group}-${row.field}-${index}`}>
              <td className="px-2.5 py-2 align-top font-semibold text-gray-400">
                #{row.group}
              </td>
              <td className="px-2.5 py-2 align-top font-bold text-gray-600 dark:text-gray-300">
                {TECHNICAL_FIELD_LABELS[row.field]}
              </td>
              <td className="px-2.5 py-2 leading-relaxed text-gray-600 dark:text-gray-400">
                {row.value}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const AgentTextToggle = ({ step }: { step: PipelineStepType }) => {
  const [isOpen, setIsOpen] = useState(false);
  const cleanText = stripXmlForDisplay(step.accumulatedText ?? "");
  const summary = getAgentWorkSummary(step);
  if (!cleanText) return null;

  return (
    <div className="my-0.5">
      <ActionToggleRow
        toggleable
        open={isOpen}
        onToggle={() => setIsOpen((open) => !open)}
        className="text-gray-500 dark:text-gray-400"
        icon={
          <FileText
            size={18}
            strokeWidth={1.8}
            className="shrink-0 text-gray-400 transition-colors group-hover:text-gray-600 dark:group-hover:text-gray-200"
          />
        }
        label={
          step.agentId === "riskAgent"
            ? "유해·위험요인 분석함"
            : `${summary.title} 결과`
        }
      />
      <ActionTogglePanel open={isOpen}>
        <ActionToggleContent>
          {summary.chips.length > 0 && (
            <ul className="mb-2 space-y-1 pl-4 text-[11px] leading-relaxed text-gray-500 marker:text-gray-400 dark:text-gray-400">
              {summary.chips.map((hazard) => (
                <li key={hazard} className="list-disc pl-0.5">
                  {hazard}
                </li>
              ))}
            </ul>
          )}
          <TechnicalInfoTable text={step.accumulatedText ?? cleanText} />
        </ActionToggleContent>
      </ActionTogglePanel>
    </div>
  );
};

const AgentSubtaskList = ({ step }: { step: PipelineStepType }) => {
  const [openResults, setOpenResults] = useState<Record<string, boolean>>({});
  const fallback: AgentSubtaskType[] =
    step.agentId === "riskAgent" &&
    step.status === "complete" &&
    !step.subtasks?.length
      ? [
          { id: "risk-search", title: "위험요인 검색" },
          { id: "risk-analysis", title: "위험요인 분석" },
        ]
      : [];
  const actionTitles: Record<string, string> = {
    "input-normalize": "입력 조건 정규화",
    "photo-analysis": "현장 사진 분석",
    "risk-search": "위험요인 검색",
    "risk-analysis": "위험요인 분석",
    "risk-dedupe": "중복 위험요인 정리",
    "risk-evidence": "평가 근거 반영",
    "risk-legal-basis": "법령 근거 확인",
    "accident-cases": "유사 사고사례 확인",
    "risk-format": "최종 보고서 구성",
  };
  const streamedTasks: AgentSubtaskType[] = (step.actions ?? []).map(
    (action) => ({
      id: action.actionId ?? "default",
      title: actionTitles[action.actionId ?? ""] ?? "작업 결과",
    }),
  );
  const baseTasks = step.subtasks?.length ? step.subtasks : fallback;
  const tasks: AgentSubtaskType[] = [...baseTasks];
  for (const streamed of streamedTasks) {
    if (!tasks.some((task) => task.id === streamed.id)) tasks.push(streamed);
  }
  if (tasks.length === 0) return null;

  return (
    <div className="mb-1">
      {tasks.map((task) => {
        const duration = formatWorkDuration(task.durationMs);
        const Icon = task.id.includes("search")
          ? Search
          : task.id.includes("analysis")
            ? SquareTerminal
            : task.id.includes("evidence")
              ? PenLine
              : FileText;
        const hideDetails = shouldHideRiskSearchDetails({ actionId: task.id });
        const results = hideDetails ? [] : (task.results ?? []);
        const streamedContent = hideDetails
          ? ""
          : (step.actions?.find(
              (action) => (action.actionId ?? "default") === task.id,
            )?.content ?? "");
        const taskRunning =
          !hideDetails &&
          step.status === "running" &&
          Boolean(streamedContent) &&
          results.length === 0;
        const open = taskRunning ? true : (openResults[task.id] ?? false);
        const hasResults =
          !hideDetails &&
          (results.length > 0 ||
            Boolean(task.resultSummary) ||
            Boolean(streamedContent));
        return (
          <div key={task.id} className="py-0.5">
            <ActionToggleRow
              toggleable={hasResults}
              open={open}
              onToggle={() =>
                setOpenResults((previous) => ({
                  ...previous,
                  [task.id]: !open,
                }))
              }
              loading={taskRunning}
              className="text-gray-500 dark:text-gray-400"
              icon={
                <Icon
                  size={18}
                  strokeWidth={1.8}
                  className="shrink-0 text-gray-400 transition-colors group-hover:text-gray-600 dark:group-hover:text-gray-200"
                />
              }
              label={
                taskRunning
                  ? `${task.title} 중`
                  : duration
                    ? `${duration} 동안 ${task.title}함`
                    : `${task.title} 완료`
              }
            />
            {hasResults && (
              <ActionTogglePanel open={open}>
                {results.length > 0 ? (
                  <div className="mb-2 ml-2.5 max-h-80 overflow-y-auto rounded-lg border border-gray-200/80 bg-white/50 dark:border-gray-800 dark:bg-white/[0.015] custom-scrollbar">
                    <div className="divide-y divide-gray-100 dark:divide-gray-800/80">
                      {results.map((result, index) => (
                        <div
                          key={`${task.id}-result-${index}`}
                          className="px-3 py-2.5 text-[13px] leading-relaxed"
                        >
                          <div className="mb-1 font-semibold text-gray-700 dark:text-gray-200">
                            {result.hazard ||
                              result.task ||
                              `결과 ${index + 1}`}
                          </div>
                          {result.task && result.task !== result.hazard && (
                            <div className="text-gray-500 dark:text-gray-400">
                              <span className="font-medium">작업</span> ·{" "}
                              {result.task}
                            </div>
                          )}
                          {result.cause && (
                            <div className="text-gray-500 dark:text-gray-400">
                              <span className="font-medium">발생 원인</span> ·{" "}
                              {result.cause}
                            </div>
                          )}
                          {result.currentMeasure && (
                            <div className="text-gray-500 dark:text-gray-400">
                              <span className="font-medium">현재 조치</span> ·{" "}
                              {result.currentMeasure}
                            </div>
                          )}
                          {result.reductionMeasure && (
                            <div className="text-gray-500 dark:text-gray-400">
                              <span className="font-medium">개선 대책</span> ·{" "}
                              {result.reductionMeasure}
                            </div>
                          )}
                          {(result.frequency ||
                            result.severity ||
                            result.riskLevel) && (
                            <div className="mt-1 text-gray-500 dark:text-gray-400">
                              {[
                                result.frequency && `빈도 ${result.frequency}`,
                                result.severity && `강도 ${result.severity}`,
                                result.riskLevel &&
                                  `위험도 ${result.riskLevel}`,
                              ]
                                .filter(Boolean)
                                .join(" · ")}
                            </div>
                          )}
                          {result.basis && (
                            <div className="mt-1 text-gray-500 dark:text-gray-400">
                              <span className="font-medium">평가 근거</span> ·{" "}
                              {result.basis}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : streamedContent ? (
                  <div className="mb-2 ml-2.5 max-h-72 overflow-y-auto whitespace-pre-wrap rounded-lg border border-gray-200/80 bg-white/50 px-3 py-2.5 text-[13px] leading-relaxed text-gray-600 dark:border-gray-800 dark:bg-white/[0.015] dark:text-gray-300 custom-scrollbar">
                    {stripXmlForDisplay(streamedContent)}
                  </div>
                ) : task.resultSummary ? (
                  <p className="ml-2.5 px-2.5 pb-2 pt-1 text-[12px] leading-relaxed text-gray-400">
                    {task.resultSummary}
                  </p>
                ) : null}
              </ActionTogglePanel>
            )}
          </div>
        );
      })}
    </div>
  );
};

const StepResultToggle = ({ summary }: { summary: string }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="my-0.5">
      <ActionToggleRow
        toggleable
        open={open}
        onToggle={() => setOpen((value) => !value)}
        className="text-gray-500 dark:text-gray-400"
        icon={
          <Brain
            size={18}
            strokeWidth={1.8}
            className="shrink-0 text-gray-400 transition-colors group-hover:text-gray-600 dark:group-hover:text-gray-200"
          />
        }
        label="생각한 내용"
      />
      <ActionTogglePanel open={open}>
        <ActionToggleContent className="px-2.5 py-2 text-[12px] leading-relaxed text-gray-400">
          {summary}
        </ActionToggleContent>
      </ActionTogglePanel>
    </div>
  );
};

const WebSearchResultsPanel = ({ step }: { step: PipelineStepType }) => {
  const searches = step.webSearches ?? [];
  if (searches.length === 0) return null;

  return (
    <ActionToggleContent className="space-y-3 px-2.5 pb-2">
      {searches.map((search, searchIndex) => (
        <div key={`${search.query}-${searchIndex}`}>
          <p className="mb-1.5 text-[11px] font-bold text-gray-500 dark:text-gray-400">
            검색어 · {search.query}
          </p>
          <div className="overflow-hidden rounded-xl border border-gray-200/80 bg-white/70 dark:border-gray-800 dark:bg-white/[0.015]">
            {search.results.length > 0 ? (
              <div className="divide-y divide-gray-100 dark:divide-gray-800/80">
                {search.results.map((result, resultIndex) => {
                  const siteName = getReferenceSiteName(result.url);
                  const faviconUrl = `https://www.google.com/s2/favicons?domain=${encodeURIComponent(getReferenceHostname(result.url))}&sz=32`;
                  return (
                    <a
                      key={`${result.url}-${resultIndex}`}
                      href={result.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group/result flex min-w-0 items-start gap-2.5 px-3 py-2.5 transition-colors hover:bg-gray-50 dark:hover:bg-white/[0.025]"
                    >
                      <span
                        aria-hidden="true"
                        className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-[5px] bg-white bg-contain bg-center bg-no-repeat ring-1 ring-black/5 dark:bg-gray-800"
                        style={{ backgroundImage: `url(${faviconUrl})` }}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[12px] font-bold text-gray-700 transition-colors group-hover/result:text-gray-950 dark:text-gray-200 dark:group-hover/result:text-white">
                          {result.title}
                        </span>
                        {result.snippet && (
                          <span className="mt-0.5 line-clamp-2 block text-[11px] leading-relaxed text-gray-500 dark:text-gray-400">
                            {result.snippet}
                          </span>
                        )}
                        <span className="mt-1 block truncate text-[10px] font-semibold text-gray-400">
                          {siteName} · {result.url}
                        </span>
                      </span>
                    </a>
                  );
                })}
              </div>
            ) : (
              <p className="px-3 py-2.5 text-[11px] text-gray-400">
                표시할 검색 결과가 없습니다.
              </p>
            )}
          </div>
        </div>
      ))}
    </ActionToggleContent>
  );
};

const WebReferenceBadges = ({
  references,
}: {
  references: WebReferenceType[];
}) => {
  if (references.length === 0) return null;

  return (
    <div
      className="mt-3 flex flex-wrap items-center gap-1.5"
      aria-label="웹 검색 참고 출처"
    >
      {references.map((reference, index) => {
        const hostname = getReferenceHostname(reference.url);
        const siteName = getReferenceSiteName(reference.url);
        const faviconUrl = `https://www.google.com/s2/favicons?domain=${encodeURIComponent(hostname)}&sz=32`;
        return (
          <a
            key={`${reference.url}-${index}`}
            href={reference.url}
            target="_blank"
            rel="noopener noreferrer"
            title={`${reference.title}\n${reference.url}`}
            className="inline-flex max-w-full items-center gap-1 rounded-full border border-gray-200 bg-gray-100 px-1.5 py-1 text-[11px] font-semibold leading-none text-gray-700 no-underline transition-colors hover:border-gray-300 hover:bg-gray-200 hover:text-gray-950 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700 dark:hover:text-white"
          >
            <span
              aria-hidden="true"
              className="flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-[3px] bg-white bg-contain bg-center bg-no-repeat ring-1 ring-black/5"
              style={{ backgroundImage: `url(${faviconUrl})` }}
            />
            <span className="max-w-[104px] truncate">{siteName}</span>
          </a>
        );
      })}
    </div>
  );
};

const MarkdownTable = ({ children }: { children: ReactNode }) => {
  const tableRef = useRef<HTMLTableElement>(null);
  const [hovered, setHovered] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const tableText = tableRef.current?.innerText.trim();
    if (!tableText || !(await copyText(tableText))) return;
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div
      className="relative my-6 w-full"
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
    >
      <div className="w-full overflow-x-auto">
        <table
          ref={tableRef}
          className="!my-0 w-full min-w-[620px] border-collapse text-left text-[13px] leading-5"
        >
          {children}
        </table>
      </div>
      <button
        type="button"
        onClick={handleCopy}
        aria-label={copied ? "표 복사됨" : "표 복사"}
        title={copied ? "복사됨" : "표 복사"}
        className={`absolute right-1 top-1 z-10 flex h-7 w-7 cursor-pointer items-center justify-center text-gray-400 transition-[opacity,color] hover:text-gray-800 focus-visible:opacity-100 focus-visible:outline-none focus-visible:text-seexr-primary dark:text-gray-500 dark:hover:text-gray-200 ${hovered || copied ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"}`}
      >
        {copied ? (
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        ) : (
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
          </svg>
        )}
      </button>
    </div>
  );
};

const CHAT_MARKDOWN_COMPONENTS: MarkdownComponents = {
  table: ({ children }) => <MarkdownTable>{children}</MarkdownTable>,
  thead: ({ children }) => (
    <thead className="text-gray-700 dark:text-gray-200">{children}</thead>
  ),
  tbody: ({ children }) => (
    <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
      {children}
    </tbody>
  ),
  th: ({ children }) => (
    <th className="px-3 py-2 align-top text-[12px] font-bold">{children}</th>
  ),
  td: ({ children }) => (
    <td className="px-3 py-2 align-top text-gray-600 dark:text-gray-300">
      {children}
    </td>
  ),
};

const formatWorkDuration = (durationMs?: number) => {
  if (durationMs == null) return "";
  const totalSeconds = Math.max(1, Math.round(durationMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes > 0 ? `${minutes}분 ${seconds}초` : `${seconds}초`;
};

const getStepStatusText = (step: PipelineStepType) => {
  const duration = formatWorkDuration(step.durationMs);
  const quickFastStatus = getQuickFastReasoningStatusText(
    step.agentId,
    step.status,
    duration,
  );
  if (quickFastStatus) return quickFastStatus;
  if (step.status === "error") {
    if (step.agentId === "riskAgent") return "위험성평가를 완료하지 못함";
    if (step.agentId === "formatterAgent")
      return "위험성평가표를 작성하지 못함";
    if (step.agentId === "tbmSupervisorAgent")
      return "TBM 교육 자료 구성을 완료하지 못함";
    if (step.agentId === "tbmScenarioAgent")
      return "TBM 시나리오 구성을 완료하지 못함";
    if (step.agentId === "tbmRecordAgent")
      return "TBM 기록지 작성을 완료하지 못함";
    return "작업을 완료하지 못함";
  }
  if (SUPERVISOR_AGENT_IDS.has(step.agentId)) {
    return step.status === "running"
      ? "생각 중"
      : duration
        ? `${duration} 동안 생각함`
        : "생각을 마침";
  }
  if (step.agentId === "riskAgent") {
    return step.status === "running"
      ? "위험성평가 작업 중"
      : duration
        ? `${duration} 동안 위험성평가를 실시함`
        : "위험성평가를 실시함";
  }
  if (step.agentId === "formatterAgent") {
    return step.status === "running"
      ? "마무리 작업 중"
      : duration
        ? `${duration} 동안 위험성평가표를 작성함`
        : "위험성평가표를 작성함";
  }
  if (step.agentId === "tbmSupervisorAgent") {
    return step.status === "running"
      ? "TBM 교육 자료 구성 중"
      : duration
        ? `${duration} 동안 TBM 교육 자료 구성을 조율함`
        : "TBM 교육 자료 구성을 마침";
  }
  if (step.agentId === "tbmScenarioAgent") {
    return step.status === "running"
      ? "TBM 시나리오 구성 중"
      : duration
        ? `${duration} 동안 TBM 시나리오를 구성함`
        : "TBM 시나리오를 구성함";
  }
  if (step.agentId === "tbmRecordAgent") {
    return step.status === "running"
      ? "TBM 기록지 작성 중"
      : duration
        ? `${duration} 동안 TBM 기록지를 작성함`
        : "TBM 기록지를 작성함";
  }
  if (step.agentId === "browserAgent") {
    return step.status === "running"
      ? "웹 검색 중"
      : duration
        ? `${duration} 동안 웹을 검색함`
        : "웹 검색을 완료함";
  }
  const taskName =
    AGENT_FRIENDLY_NAMES[step.agentId] ??
    step.agentName.replace(/\s*에이전트\s*$/, "");
  return step.status === "running"
    ? `${taskName} 중`
    : duration
      ? `${duration} 동안 ${taskName}을 완료함`
      : `${taskName} 완료`;
};

const StepIcon = ({ step }: { step: PipelineStepType }) => {
  if (SUPERVISOR_AGENT_IDS.has(step.agentId)) return null;
  if (step.status === "error")
    return (
      <TriangleAlert
        size={16}
        strokeWidth={1.8}
        className="shrink-0 text-gray-400 transition-colors group-hover:text-gray-600 dark:group-hover:text-gray-200"
      />
    );
  const quickFastIcon = getQuickFastReasoningPresentation(step.agentId)?.icon;
  if (quickFastIcon === "search" || quickFastIcon === "accident")
    return (
      <Search
        size={16}
        strokeWidth={1.8}
        className="shrink-0 text-gray-400 transition-colors group-hover:text-gray-600 dark:group-hover:text-gray-200"
      />
    );
  if (quickFastIcon === "analysis")
    return (
      <SquareTerminal
        size={17}
        strokeWidth={1.8}
        className="shrink-0 text-gray-400 transition-colors group-hover:text-gray-600 dark:group-hover:text-gray-200"
      />
    );
  if (quickFastIcon === "evidence" || quickFastIcon === "legal")
    return (
      <FileText
        size={16}
        strokeWidth={1.8}
        className="shrink-0 text-gray-400 transition-colors group-hover:text-gray-600 dark:group-hover:text-gray-200"
      />
    );
  if (quickFastIcon === "format")
    return (
      <PenLine
        size={16}
        strokeWidth={1.8}
        className="shrink-0 text-gray-400 transition-colors group-hover:text-gray-600 dark:group-hover:text-gray-200"
      />
    );
  if (quickFastIcon === "photo")
    return (
      <ImageIcon
        size={16}
        strokeWidth={1.8}
        className="shrink-0 text-gray-400 transition-colors group-hover:text-gray-600 dark:group-hover:text-gray-200"
      />
    );
  if (quickFastIcon === "review") return null;
  if (quickFastIcon === "verify")
    return (
      <CheckCircle2
        size={16}
        strokeWidth={1.8}
        className="shrink-0 text-gray-400 transition-colors group-hover:text-gray-600 dark:group-hover:text-gray-200"
      />
    );
  if (step.agentId === "riskAgent")
    return (
      <SquareTerminal
        size={17}
        strokeWidth={1.8}
        className="shrink-0 text-gray-400 transition-colors group-hover:text-gray-600 dark:group-hover:text-gray-200"
      />
    );
  if (TBM_AGENT_IDS.has(step.agentId))
    return (
      <SquareTerminal
        size={17}
        strokeWidth={1.8}
        className="shrink-0 text-gray-400 transition-colors group-hover:text-gray-600 dark:group-hover:text-gray-200"
      />
    );
  if (step.agentId === "formatterAgent")
    return (
      <PenLine
        size={16}
        strokeWidth={1.8}
        className="shrink-0 text-gray-400 transition-colors group-hover:text-gray-600 dark:group-hover:text-gray-200"
      />
    );
  if (step.agentId === "browserAgent")
    return (
      <Globe
        size={17}
        strokeWidth={1.8}
        className="shrink-0 text-gray-400 transition-colors group-hover:text-gray-600 dark:group-hover:text-gray-200"
      />
    );
  if (step.agentId === "accidentAgent" || step.agentId === "legalAgent")
    return (
      <Search
        size={16}
        strokeWidth={1.8}
        className="shrink-0 text-gray-400 transition-colors group-hover:text-gray-600 dark:group-hover:text-gray-200"
      />
    );
  return (
    <CheckCircle2
      size={16}
      strokeWidth={1.8}
      className="shrink-0 text-gray-400 transition-colors group-hover:text-gray-600 dark:group-hover:text-gray-200"
    />
  );
};

const PipelineTimeline = ({
  steps,
  isLive = false,
}: {
  steps: PipelineStepType[];
  isLive?: boolean;
}) => {
  const [expandedSteps, setExpandedSteps] = useState<Record<string, boolean>>(
    {},
  );
  const stepHasRunningWork = (step: PipelineStepType) =>
    step.status === "running" ||
    Boolean(step.toolCalls?.some((tool) => tool.status === "running"));
  const anyRunning = steps.some(stepHasRunningWork);
  const [rootOpen, setRootOpen] = useState(anyRunning || isLive);
  const startedAtRef = useRef(Date.now());
  const [liveElapsedMs, setLiveElapsedMs] = useState(0);
  const statusSignature = steps
    .map(
      (step) =>
        `${step.agentId}:${step.status}:${step.toolCalls?.some((tool) => tool.status === "running") ? "tool-running" : ""}`,
    )
    .join("|");

  useEffect(() => {
    if (!anyRunning) return;
    const update = () => setLiveElapsedMs(Date.now() - startedAtRef.current);
    update();
    const timer = window.setInterval(update, 1000);
    return () => window.clearInterval(timer);
  }, [anyRunning]);

  useEffect(() => {
    setRootOpen(anyRunning);
    setExpandedSteps((previous) => {
      const next = { ...previous };
      steps.forEach((step, index) => {
        const key = `${step.agentId}-${index}`;
        if (stepHasRunningWork(step)) next[key] = true;
        if (
          !stepHasRunningWork(step) &&
          (step.status === "complete" || step.status === "error")
        )
          next[key] = false;
      });
      return next;
    });
  }, [anyRunning, statusSignature]);

  const hasQuickFastWorkflowStages = steps.some((step) =>
    isQuickFastReasoningStage(step.agentId),
  );
  const visibleSteps = (
    isLive ? steps.filter((step) => step.status !== "pending") : steps
  ).filter(
    (step) =>
      !(hasQuickFastWorkflowStages && step.agentId === "quick-fast-v1.0.0"),
  );
  if (visibleSteps.length === 0) return null;
  const completedDurationMs = steps.reduce(
    (total, step) => total + (step.durationMs ?? 0),
    0,
  );
  const rootDuration = formatWorkDuration(
    anyRunning ? liveElapsedMs : completedDurationMs,
  );
  const rootLabel = anyRunning
    ? `${rootDuration || "0초"} 동안 작업 중`
    : `총 ${rootDuration || "1초"} 동안 작업함`;
  const effectiveRootOpen = anyRunning ? true : rootOpen;

  return (
    <div className="flex flex-col mb-2 min-w-0 w-full">
      <div className="min-w-0">
        <ActionToggleRow
          toggleable
          open={effectiveRootOpen}
          onToggle={() => setRootOpen((open) => !open)}
          loading={anyRunning}
          className="agent-action-row font-medium text-gray-600 dark:text-gray-300"
          label={rootLabel}
        />
        <hr className="my-1.5 border-gray-200/80 dark:border-gray-800" />
        <ActionTogglePanel open={effectiveRootOpen}>
          <div className="flex flex-col gap-1">
            {visibleSteps.map((step, idx) => {
              const stepKey = `${step.agentId}-${idx}`;
              const quickFastPresentation = getQuickFastReasoningPresentation(
                step.agentId,
              );
              if (quickFastPresentation?.display === "body") {
                const bodyOutput =
                  step.actions
                    ?.find((action) => action.actionId === "input-normalize")
                    ?.content.trim() ||
                  step.subtasks
                    ?.find((subtask) => subtask.id === "input-normalize")
                    ?.resultSummary?.trim() ||
                  step.resultSummary?.trim() ||
                  "";
                const waitingForOutput =
                  !bodyOutput && step.status === "running";
                const bodyText =
                  bodyOutput ||
                  (waitingForOutput
                    ? quickFastPresentation.running
                    : step.status === "error"
                      ? quickFastPresentation.failed
                      : "");
                if (!bodyText) return null;
                return (
                  <div
                    key={stepKey}
                    className={`whitespace-pre-wrap px-2.5 py-1.5 text-[15px] font-medium leading-relaxed ${
                      waitingForOutput
                        ? "agent-action-loading-text text-gray-800 dark:text-gray-200"
                        : step.status === "error" && !bodyOutput
                          ? "text-red-400"
                          : "text-gray-700 dark:text-gray-300"
                    }`}
                  >
                    {bodyText}
                  </div>
                );
              }
              const hideStepDetails = shouldHideRiskSearchDetails({
                agentId: step.agentId,
              });
              const visibleToolCalls = (step.toolCalls ?? []).filter(
                (toolCall) =>
                  !shouldHideRiskSearchDetails({ toolName: toolCall.toolName }),
              );
              const hasVisibleActions = Boolean(
                step.actions?.some(
                  (action) =>
                    !shouldHideRiskSearchDetails({ actionId: action.actionId }),
                ),
              );
              const hasVisibleSubtasks = Boolean(
                step.subtasks?.some(
                  (subtask) =>
                    !shouldHideRiskSearchDetails({ actionId: subtask.id }),
                ),
              );
              const hasStepDetails =
                !hideStepDetails &&
                Boolean(
                  visibleToolCalls.length > 0 ||
                  hasVisibleActions ||
                  step.accumulatedText ||
                  hasVisibleSubtasks ||
                  step.resultSummary ||
                  step.webSearches?.length ||
                  step.references?.length,
                );
              const stepRunning = stepHasRunningWork(step);
              const isStepOpen = stepRunning
                ? true
                : (expandedSteps[stepKey] ?? false);
              const showStepIcon = !SUPERVISOR_AGENT_IDS.has(step.agentId);

              return (
                <div key={stepKey} className="flex flex-col shrink-0">
                  {/* Step row */}
                  <ActionToggleRow
                    toggleable={hasStepDetails}
                    open={isStepOpen}
                    onToggle={() =>
                      setExpandedSteps((previous) => ({
                        ...previous,
                        [stepKey]: !isStepOpen,
                      }))
                    }
                    loading={stepRunning}
                    icon={showStepIcon ? <StepIcon step={step} /> : undefined}
                    label={getStepStatusText(step)}
                    labelClassName={
                      step.status === "running"
                        ? "text-gray-800 dark:text-gray-200 font-semibold"
                        : step.status === "complete"
                          ? "text-gray-500 dark:text-gray-400"
                          : step.status === "error"
                            ? "text-red-400"
                            : "text-gray-400 dark:text-gray-500"
                    }
                  />

                  {/* Tool calls + agent text under this step */}
                  {hasStepDetails && (
                    <ActionTogglePanel open={isStepOpen}>
                      <div className="min-w-0 mb-1">
                        {/* Tool call badges */}
                        {visibleToolCalls.map((tc) => (
                          <ToolCallCard key={tc.toolCallId} toolCall={tc} />
                        ))}

                        {/* Public web search results */}
                        <WebSearchResultsPanel step={step} />

                        {/* Nested work items + agent result */}
                        <AgentSubtaskList step={step} />
                        {step.resultSummary && (
                          <StepResultToggle summary={step.resultSummary} />
                        )}
                        {step.accumulatedText && (
                          <AgentResultPreview step={step} />
                        )}
                        {step.accumulatedText && (
                          <AgentTextToggle step={step} />
                        )}
                      </div>
                    </ActionTogglePanel>
                  )}
                </div>
              );
            })}
          </div>
        </ActionTogglePanel>
      </div>
    </div>
  );
};

/*
 * AgentPlanPanel (현재 비활성화)
 * 새 채팅에서 에이전트가 전달하는 계획 단계의 제목, 진행 상태, 완료 개수를
 * 입력창 위에 실시간으로 보여주던 작업 계획 패널이다. 추후 다시 사용할 수 있도록
 * 구현은 삭제하지 않고 주석으로 보존한다.
 *
const AgentPlanPanel = ({ steps }: { steps: ComposerPlanStepType[] }) => {
  if (steps.length === 0) return null;

  const completedCount = steps.filter(step => step.status === 'complete').length;
  const title = completedCount === steps.length ? '계획 완료' : '작업 계획';

  return (
    <div className="w-full max-w-[720px] rounded-[28px] border border-gray-200 bg-white/70 px-4 py-3.5 shadow-[0_4px_24px_rgba(0,0,0,0.04)] backdrop-blur-3xl dark:border-[#2a2a2e] dark:bg-[#161618]/70">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate text-[13px] font-black text-gray-800 dark:text-gray-100">{title}</span>
        </div>
        <span className="shrink-0 text-[11px] font-bold text-gray-400 dark:text-gray-500">
          {completedCount}/{steps.length}
        </span>
      </div>

      <div className="flex flex-col gap-2">
        {steps.map((step, idx) => {
          const isComplete = step.status === 'complete';
          const isRunning = step.status === 'running';
          const isError = step.status === 'error';
          return (
            <div key={`${step.id}-${idx}`} className="flex items-center gap-2.5">
              <span
                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-all ${
                  isComplete
                    ? 'border-gray-300 bg-white text-seexr-primary dark:border-gray-700 dark:bg-[#202022]'
                    : isRunning
                      ? 'border-gray-400 bg-white text-transparent dark:border-gray-500 dark:bg-[#202022]'
                      : isError
                        ? 'border-red-300 bg-red-50 text-red-500 dark:border-red-800 dark:bg-red-950/30'
                        : 'border-gray-300 bg-white text-transparent dark:border-gray-700 dark:bg-[#202022]'
                }`}
              >
                {isComplete ? (
                  <svg width="13" height="13" viewBox="0 0 20 20" fill="none">
                    <path d="M4.5 10.5L8 14l7.5-8" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : isError ? (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                ) : null}
              </span>
              <div className="min-w-0 flex-1">
                <div className={`truncate text-[13px] font-bold ${
                  isRunning
                  ? 'text-gray-900 dark:text-gray-100'
                  : isComplete
                      ? 'text-gray-500 dark:text-gray-400'
                      : isError
                        ? 'text-red-500'
                        : 'text-gray-400 dark:text-gray-500'
                }`}>
                  {step.title}
                </div>
                {step.detail && (
                  <div className="mt-0.5 truncate text-[11px] font-semibold text-gray-400 dark:text-gray-500">
                    {step.detail}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
*/

type ChatArtifactType =
  | "risk_assessment"
  | "tbm_scenario"
  | "tbm_record"
  | "cases"
  | "laws"
  | "cost_estimate"
  | "cost_estimate_material_change";
type AskUserMode =
  | "single_select"
  | "multi_select"
  | "confirm"
  | "form"
  | "cost_material_replacement";
type AskUserOption = {
  id: string;
  label: string;
  value: string;
  description?: string;
};
type AskUserField = {
  id: string;
  label: string;
  type: "text" | "textarea" | "number" | "select" | "multi_select" | "boolean";
  required: boolean;
  placeholder?: string;
  options: AskUserOption[];
};
type AskUserPrompt = {
  id: string;
  mode: AskUserMode;
  question: string;
  reason?: string;
  submitLabel: string;
  options: AskUserOption[];
  fields: AskUserField[];
};
type CostWizardStep =
  "location" | "original" | "new_material" | "scope" | "summary";
type CostMaterialChangeDraft = {
  id: string;
  location: string;
  locationLabel: string;
  originalMaterial?: string;
  originalLabel?: string;
  newMaterial: string;
  scopeNote?: string;
};
type CostEstimateMaterialItem = {
  id: string;
  location: string;
  materialName: string;
  quantity?: string;
  unit?: string;
};

const decodeXmlEntities = (source: string) =>
  source
    .replace(/&apos;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&gt;/g, ">")
    .replace(/&lt;/g, "<")
    .replace(/&amp;/g, "&");

const encodeXmlEntities = (source: string) =>
  source
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");

const getXmlTagBody = (tag: string, source: string): string => {
  const match = source.match(
    new RegExp(`<${tag}(?:\\s+[^>]*)?>([\\s\\S]*?)(?:<\\/${tag}>|$)`, "i"),
  );
  return match ? match[1].trim() : "";
};

const getXmlTagText = (tag: string, source: string): string =>
  decodeXmlEntities(getXmlTagBody(tag, source));

const getXmlTagBodies = (tag: string, source: string): string[] => {
  return [
    ...source.matchAll(
      new RegExp(`<${tag}(?:\\s+[^>]*)?>([\\s\\S]*?)<\\/${tag}>`, "gi"),
    ),
  ].map((match) => match[1].trim());
};

const getLastXmlTagBody = (tag: string, source: string): string => {
  const bodies = getXmlTagBodies(tag, source);
  return bodies[bodies.length - 1] || "";
};

const normalizeCostOptionId = (value: string, fallback: string) => {
  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9가-힣]+/gi, "_")
    .replace(/^_+|_+$/g, "");
  return normalized || fallback;
};

const parseCostEstimateMaterials = (
  content: string,
): CostEstimateMaterialItem[] => {
  const costEstimateBlock = getLastXmlTagBody("cost_estimate", content);
  if (!costEstimateBlock) return [];

  const materialsBlock = getXmlTagBody("materials", costEstimateBlock);
  const rawItems = [
    ...getXmlTagBodies("item", materialsBlock),
    ...getXmlTagBodies("material", materialsBlock),
  ];
  const seen = new Set<string>();

  return rawItems
    .map((block, index) => {
      const location = getXmlTagText("location", block).trim();
      const materialName = (
        getXmlTagText("material_name", block) || getXmlTagText("name", block)
      ).trim();
      if (!location || !materialName) return null;

      const key = `${location}::${materialName}`;
      if (seen.has(key)) return null;
      seen.add(key);

      return {
        id: normalizeCostOptionId(key, `material_${index + 1}`),
        location,
        materialName,
        quantity: getXmlTagText("quantity", block).trim() || undefined,
        unit: getXmlTagText("unit", block).trim() || undefined,
      };
    })
    .filter(Boolean) as CostEstimateMaterialItem[];
};

const parseAskUserOption = (block: string, index: number): AskUserOption => {
  const label = getXmlTagText("label", block) || `선택 ${index + 1}`;
  const id = getXmlTagText("id", block) || `option_${index + 1}`;
  return {
    id,
    label,
    value: getXmlTagText("value", block) || label,
    description: getXmlTagText("description", block) || undefined,
  };
};

const parseAskUserPrompt = (content: string): AskUserPrompt | null => {
  const block = getXmlTagBody("ask_user", content);
  if (!block) return null;

  const fieldsBlock = getXmlTagBody("fields", block);
  const blockWithoutFields = block.replace(
    /<fields(?:\s+[^>]*)?>[\s\S]*?(?:<\/fields>|$)/gi,
    "",
  );
  const optionsBlock = getXmlTagBody("options", blockWithoutFields);
  const options = getXmlTagBodies("option", optionsBlock).map(
    parseAskUserOption,
  );
  const fields: AskUserField[] = getXmlTagBodies("field", fieldsBlock).map(
    (fieldBlock, index) => {
      const fieldOptionsBlock = getXmlTagBody("options", fieldBlock);
      const rawType = getXmlTagText("type", fieldBlock);
      const type = (
        [
          "text",
          "textarea",
          "number",
          "select",
          "multi_select",
          "boolean",
        ].includes(rawType)
          ? rawType
          : "text"
      ) as AskUserField["type"];
      const label = getXmlTagText("label", fieldBlock) || `입력 ${index + 1}`;
      return {
        id: getXmlTagText("id", fieldBlock) || `field_${index + 1}`,
        label,
        type,
        required:
          getXmlTagText("required", fieldBlock).toLowerCase() === "true",
        placeholder: getXmlTagText("placeholder", fieldBlock) || undefined,
        options: getXmlTagBodies("option", fieldOptionsBlock).map(
          parseAskUserOption,
        ),
      };
    },
  );

  const rawMode = getXmlTagText("mode", block);
  const mode = (
    [
      "single_select",
      "multi_select",
      "confirm",
      "form",
      "cost_material_replacement",
    ].includes(rawMode)
      ? rawMode
      : fields.length > 0
        ? "form"
        : "single_select"
  ) as AskUserMode;

  return {
    id: getXmlTagText("id", block) || `ask_${Date.now()}`,
    mode,
    question: getXmlTagText("question", block) || "추가 정보가 필요합니다.",
    reason: getXmlTagText("reason", block) || undefined,
    submitLabel:
      getXmlTagText("submit_label", block) ||
      (mode === "multi_select" ? "선택 완료" : "답변하기"),
    options,
    fields,
  };
};

const getCostMaterialChangeApplyPrompt = (): AskUserPrompt => ({
  id: "cost_material_change_apply",
  mode: "confirm",
  question: "위 변경안을 적용할까요?",
  reason: "적용하면 현재 개산견적 세션의 누적 자재 변경안에 반영됩니다.",
  submitLabel: "결정하기",
  options: [
    { id: "apply", label: "예, 적용합니다", value: "yes" },
    { id: "cancel", label: "아니오, 취소합니다", value: "no" },
  ],
  fields: [],
});

const parseLegacyCostMaterialApplyPrompt = (
  content: string,
): AskUserPrompt | null => {
  if (!/<cost_estimate_material_change\b/i.test(content)) return null;
  if (!/위\s*변경안을\s*적용할까요/i.test(content)) return null;
  return getCostMaterialChangeApplyPrompt();
};

const getLatestPendingAskUserPrompt = (
  messages: any[],
): AskUserPrompt | null => {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (msg?.role === "user") return null;
    if (msg?.role === "assistant" && typeof msg.content === "string") {
      const prompt =
        parseAskUserPrompt(msg.content) ??
        parseLegacyCostMaterialApplyPrompt(msg.content);
      if (prompt) return prompt;
    }
  }
  return null;
};

const buildAskUserResponse = (
  prompt: AskUserPrompt,
  params: {
    answerText: string;
    selections?: AskUserOption[];
    fieldValues?: Record<string, string | string[] | boolean>;
  },
) => {
  const selections = params.selections ?? [];
  const fieldValues = params.fieldValues ?? {};
  const fieldEntries = Object.entries(fieldValues);
  const selectionXml = selections
    .map((option) =>
      [
        "    <selection>",
        `      <id>${encodeXmlEntities(option.id)}</id>`,
        `      <label>${encodeXmlEntities(option.label)}</label>`,
        `      <value>${encodeXmlEntities(option.value || option.label)}</value>`,
        "    </selection>",
      ].join("\n"),
    )
    .join("\n");
  const fieldsXml = fieldEntries
    .map(([id, value]) => {
      const field = prompt.fields.find((candidate) => candidate.id === id);
      const label = field?.label || id;
      const normalizedValue = Array.isArray(value)
        ? value.join(", ")
        : String(value ?? "");
      return [
        "    <field>",
        `      <id>${encodeXmlEntities(id)}</id>`,
        `      <label>${encodeXmlEntities(label)}</label>`,
        `      <value>${encodeXmlEntities(normalizedValue)}</value>`,
        "    </field>",
      ].join("\n");
    })
    .join("\n");

  const answerText = params.answerText.trim();
  return [
    `추가 정보 답변: ${answerText}`,
    "",
    "<ask_user_response>",
    `  <id>${encodeXmlEntities(prompt.id)}</id>`,
    `  <mode>${encodeXmlEntities(prompt.mode)}</mode>`,
    `  <answer_text>${encodeXmlEntities(answerText)}</answer_text>`,
    selections.length
      ? ["  <selections>", selectionXml, "  </selections>"].join("\n")
      : "",
    fieldEntries.length
      ? ["  <fields>", fieldsXml, "  </fields>"].join("\n")
      : "",
    "</ask_user_response>",
  ]
    .filter((line) => line !== "")
    .join("\n");
};

const getUserMessageDisplayText = (content: string): string => {
  if (!content) return "";
  const answerText = getXmlTagText("answer_text", content);
  if (answerText) return answerText;
  return content
    .replace(/<ask_user_response>[\s\S]*?(?:<\/ask_user_response>|$)/gi, "")
    .replace(/^추가 정보 답변:\s*/i, "")
    .trim();
};

function extractArtifactTitle(
  type: ChatArtifactType,
  xmlContent: string,
): string {
  const unescape = (s: string) =>
    s.replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">");

  if (type === "risk_assessment") {
    const industry = xmlContent
      .match(/<industry_name>([^<]+)<\/industry_name>/)?.[1]
      ?.trim();
    const process = xmlContent
      .match(/<process_name>([^<]+)<\/process_name>/)?.[1]
      ?.trim();
    const validIndustry =
      industry && industry !== "-" ? unescape(industry) : null;
    const validProcess = process && process !== "-" ? unescape(process) : null;

    if (validIndustry && validProcess)
      return `${validIndustry} ${validProcess} 위험성평가`;
    if (validIndustry) return `${validIndustry} 위험성평가`;
    return "위험성평가 보고서";
  }

  if (type === "tbm_scenario" || type === "tbm_record") {
    return deriveTbmArtifactTitle(type, xmlContent);
  }

  if (type === "cases") {
    const caseCount = xmlContent.match(/<case\b/g)?.length ?? 0;
    const firstTitle = xmlContent
      .match(/<case[^>]*>[\s\S]*?<title>([^<]+)<\/title>/)?.[1]
      ?.trim();
    if (firstTitle) {
      const t = unescape(
        firstTitle.length > 25
          ? firstTitle.substring(0, 22) + "..."
          : firstTitle,
      );
      return caseCount > 1 ? `${t} 외 ${caseCount - 1}건` : t;
    }
    const cause = xmlContent
      .match(/<accidentCause>([^<]+)<\/accidentCause>/)?.[1]
      ?.trim();
    if (cause)
      return caseCount > 1
        ? `${unescape(cause)} 사고사례 ${caseCount}건`
        : `${unescape(cause)} 사고사례`;
    return "사고사례 데이터";
  }

  if (type === "laws") {
    const lawCount = xmlContent.match(/<law\b/g)?.length ?? 0;
    const firstAct = xmlContent.match(/<act>([^<]+)<\/act>/)?.[1]?.trim();
    const firstArticle = xmlContent
      .match(/<article>([^<]+)<\/article>/)?.[1]
      ?.trim();
    if (firstAct && firstArticle) {
      const t = unescape(`${firstAct} ${firstArticle}`);
      return lawCount > 1 ? `${t} 외 ${lawCount - 1}건` : t;
    }
    if (firstAct)
      return lawCount > 1
        ? `${unescape(firstAct)} 관련 법령 ${lawCount}건`
        : unescape(firstAct);
    return `관련 법령 ${lawCount || ""}조문`;
  }

  if (type === "cost_estimate") {
    const projectName = xmlContent
      .match(/<matched_project>[\s\S]*?<name>([^<]+)<\/name>/)?.[1]
      ?.trim();
    const buildingType = xmlContent
      .match(/<building_type>([^<]+)<\/building_type>/)?.[1]
      ?.trim();
    const adjustedCost = xmlContent
      .match(/<adjusted_cost>([^<]+)<\/adjusted_cost>/)?.[1]
      ?.trim();
    const baseTitle = projectName || buildingType;
    const costNumber = adjustedCost
      ? Number(adjustedCost.replace(/,/g, ""))
      : NaN;
    if (baseTitle && Number.isFinite(costNumber))
      return `${unescape(baseTitle)} 개산견적`;
    if (baseTitle) return `${unescape(baseTitle)} 개산견적`;
    return "개산견적 보고서";
  }

  if (type === "cost_estimate_material_change") {
    const location = xmlContent
      .match(/<change>[\s\S]*?<location>([^<]+)<\/location>/)?.[1]
      ?.trim();
    const newMaterial = xmlContent
      .match(/<change>[\s\S]*?<new_material>([^<]+)<\/new_material>/)?.[1]
      ?.trim();
    const totalDifference = xmlContent
      .match(/<total_difference>([^<]+)<\/total_difference>/)?.[1]
      ?.trim();
    const diffNumber = totalDifference
      ? Number(totalDifference.replace(/,/g, ""))
      : NaN;
    const diffLabel =
      Number.isFinite(diffNumber) && diffNumber !== 0
        ? ` (${diffNumber > 0 ? "+" : ""}${new Intl.NumberFormat("ko-KR").format(Math.round(diffNumber))}원)`
        : "";
    if (location && newMaterial)
      return `${unescape(location)} ${unescape(newMaterial)} 변경안${diffLabel}`;
    return `개산견적 자재 변경안${diffLabel}`;
  }

  return "";
}

const TBM_EDUCATION_CTA =
  "이어서 이 위험성평가를 바탕으로 TBM 교육 자료를 만들어 드릴까요?";
const TBM_RECORD_GENERATION_CTA =
  "이어서 이 TBM 시나리오를 바탕으로 TBM 기록지를 만들어 드릴까요?";

const isAffirmativeTbmReply = (text: string) =>
  /^(?:네|예|응|좋아|그래|진행해\s*줘|만들어\s*줘|해주세요|부탁해)(?:[,，.!\s]|$)/i.test(
    text.trim(),
  );

export const getTbmGenerationStage = (
  text: string,
  latestAssistantText = "",
): "scenario" | "record" => {
  const normalized = text.trim();
  const acceptsRecordOffer =
    latestAssistantText.includes(TBM_RECORD_GENERATION_CTA) &&
    (isAffirmativeTbmReply(normalized) ||
      (/기록지/i.test(normalized) &&
        /(?:만들|생성|작성|제작|진행|준비|해\s*줘|해주세요|부탁)/i.test(
          normalized,
        )));
  const explicitlyRequestsRecord =
    /(?:\bTBM\b|작업\s*전\s*안전(?:점검회의|브리핑))/i.test(normalized) &&
    /기록지/i.test(normalized) &&
    /(?:만들|생성|작성|제작|진행|준비|해\s*줘|해주세요|부탁)/i.test(normalized);
  return acceptsRecordOffer || explicitlyRequestsRecord ? "record" : "scenario";
};

export const isTbmGenerationRequest = (
  text: string,
  latestAssistantText = "",
) => {
  const normalized = text.trim();
  const namesTbm = /(?:\bTBM\b|작업\s*전\s*안전(?:점검회의|브리핑))/i.test(
    normalized,
  );
  const asksForExecution =
    /(?:만들|생성|작성|제작|진행|준비|구성|해\s*줘|해주세요|부탁|create|generate|prepare)/i.test(
      normalized,
    );
  const asksForExplanationOnly =
    /(?:무엇|뭐|개념|정의|의미|방법).{0,12}(?:설명|알려)|(?:설명|알려).{0,12}(?:무엇|뭐|개념|정의|의미)|what\s+is|explain/i.test(
      normalized,
    );
  const acceptsLatestOffer =
    latestAssistantText.includes(TBM_EDUCATION_CTA) &&
    isAffirmativeTbmReply(normalized);
  const acceptsRecordOffer =
    latestAssistantText.includes(TBM_RECORD_GENERATION_CTA) &&
    (isAffirmativeTbmReply(normalized) ||
      (/기록지/i.test(normalized) && asksForExecution));
  return (
    acceptsLatestOffer ||
    acceptsRecordOffer ||
    (namesTbm && asksForExecution && !asksForExplanationOnly)
  );
};

const ArtifactBlock = ({
  title,
  type,
  onClick,
}: {
  title: string;
  type: string;
  onClick?: () => void;
}) => {
  const typeLabel =
    type === "cost_estimate_material_change" ? "COST_CHANGE" : type;
  return (
    <div
      className="flex flex-col gap-2 py-3 w-full max-w-[340px]"
      onClick={onClick}
    >
      <div className="flex text-left rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/80 transition duration-300 w-full hover:bg-gray-50 hover:-translate-y-0.5 hover:shadow-md dark:hover:bg-gray-700 cursor-pointer shadow-sm group">
        <div className="flex flex-1 items-center justify-between p-4 min-w-0">
          <div className="flex flex-col gap-1 min-w-0 flex-1">
            <div className="leading-tight text-[14px] font-black text-gray-800 dark:text-gray-200 truncate">
              {title}
            </div>
            <div className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">
              {typeLabel}
            </div>
          </div>
          <div className="flex items-center justify-center shrink-0 ml-4">
            <div className="w-10 h-10 rounded-xl bg-seexr-solid/10 text-seexr-primary flex items-center justify-center transition-transform group-hover:scale-110">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export function ChatInterface({
  agentId = "supervisorAgent",
  initialHeadline,
  classicAdapter,
  riskAssessmentReportComponent,
}: {
  agentId?: string;
  initialHeadline?: string;
  classicAdapter?: ChatClassicAdapter;
  riskAssessmentReportComponent?: RiskAssessmentReportComponent;
}) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { languageLabel, t } = useLanguage();
  const { reasoningStyle } = useAppPreferences();
  const isSpecializedAgent =
    agentId !== "intentNetworkAgent" && agentId !== "supervisorAgent";
  const isSupervisorConversation = agentId === "supervisorAgent";
  const currentAgentHubItem = AGENT_HUB_ITEMS.find(
    (item) => item.id === agentId,
  );
  const [preferredAgentId, setPreferredAgentId] = useState<string | null>(null);
  const [isPreferredAgentSaving, setIsPreferredAgentSaving] = useState(false);
  const preferredAgentRevisionRef = useRef(0);
  const preferredAgentHubItem = AGENT_HUB_ITEMS.find(
    (item) => item.id === preferredAgentId,
  );
  const composerAgentId = isSpecializedAgent ? agentId : preferredAgentId;
  const enforceRiskSitePhotoLimit = RISK_SITE_PHOTO_LIMIT_AGENT_IDS.has(
    composerAgentId ?? agentId,
  );
  const composerAgentHubItem = isSpecializedAgent
    ? currentAgentHubItem
    : preferredAgentHubItem;
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<any[]>([]);
  const [askUserDraft, setAskUserDraft] = useState<
    Record<string, string | string[] | boolean>
  >({});
  const queuedAskUserResponseRef = useRef<{
    promptId: string;
    payload: string;
  } | null>(null);
  const costWizardPanelRef = useRef<HTMLDivElement | null>(null);
  const latestAskUserPrompt = getLatestPendingAskUserPrompt(messages);
  const latestCostEstimateMaterials = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      if (msg?.role !== "assistant" || typeof msg.content !== "string")
        continue;
      const materials = parseCostEstimateMaterials(msg.content);
      if (materials.length > 0) return materials;
    }
    return [];
  }, [messages]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [revealedAskUserPromptId, setRevealedAskUserPromptId] = useState<
    string | null
  >(null);
  const shouldShowAskUserPanel =
    !!latestAskUserPrompt &&
    !isGenerating &&
    revealedAskUserPromptId === latestAskUserPrompt.id;
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [activeArtifact, setActiveArtifact] = useState<{
    msgIdx: number;
    type: ChatArtifactType;
    title: string;
  } | null>(null);
  const [isArtifactVisible, setIsArtifactVisible] = useState(false);
  const [editingMsgIdx, setEditingMsgIdx] = useState<number | null>(null);
  const [hoveredUserMsgIdx, setHoveredUserMsgIdx] = useState<number | null>(
    null,
  );
  const [hoveredAssistantMsgIdx, setHoveredAssistantMsgIdx] = useState<
    number | null
  >(null);
  const [pendingEditSubmit, setPendingEditSubmit] = useState(false);
  const [isDesktopArtifact, setIsDesktopArtifact] = useState(false);
  const routeSessionId = searchParams.get("session");
  const displayedArtifact =
    activeSessionId === routeSessionId ? activeArtifact : null;
  const isDiscardingStaleArtifact = !!activeArtifact && !displayedArtifact;
  const sessionArtifacts = useMemo(() => {
    const artifactTypes: ChatArtifactType[] = [
      "risk_assessment",
      "tbm_scenario",
      "tbm_record",
      "cost_estimate",
      "cost_estimate_material_change",
      "cases",
      "laws",
    ];
    const items: Array<{
      key: string;
      msgIdx: number;
      type: ChatArtifactType;
      title: string;
    }> = [];

    messages.forEach((message, msgIdx) => {
      if (message?.role !== "assistant" || typeof message.content !== "string")
        return;
      artifactTypes.forEach((artifactType) => {
        const match = message.content.match(
          new RegExp(
            `<${artifactType}(?:\\s+[^>]*)?>([\\s\\S]*?)(?:<\\/${artifactType}>|$)`,
            "i",
          ),
        );
        if (!match) return;
        items.push({
          key: `${msgIdx}:${artifactType}`,
          msgIdx,
          type: artifactType,
          title: extractArtifactTitle(artifactType, match[1]),
        });
      });
    });

    return items;
  }, [messages]);

  useEffect(() => {
    setAskUserDraft({});
    queuedAskUserResponseRef.current = null;
  }, [latestAskUserPrompt?.id]);

  useEffect(() => {
    if (!latestAskUserPrompt || isGenerating) {
      setRevealedAskUserPromptId(null);
      return;
    }

    if (latestAskUserPrompt.mode !== "cost_material_replacement") {
      setRevealedAskUserPromptId(latestAskUserPrompt.id);
      return;
    }

    setRevealedAskUserPromptId(null);
    const timer = window.setTimeout(() => {
      setRevealedAskUserPromptId(latestAskUserPrompt.id);
    }, 520);
    return () => window.clearTimeout(timer);
  }, [latestAskUserPrompt?.id, latestAskUserPrompt?.mode, isGenerating]);

  useEffect(() => {
    if (
      !shouldShowAskUserPanel ||
      latestAskUserPrompt?.mode !== "cost_material_replacement"
    )
      return;
    const timer = window.setTimeout(
      () => costWizardPanelRef.current?.focus(),
      220,
    );
    return () => window.clearTimeout(timer);
  }, [
    latestAskUserPrompt?.id,
    latestAskUserPrompt?.mode,
    shouldShowAskUserPanel,
  ]);

  // md breakpoint 감지 — artifact 패널의 top 오프셋을 viewport y=0부터 시작하게 inline style로 제어
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const update = () => setIsDesktopArtifact(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    // 헤더 폭 조정을 위해 Sidebar에 상태 브로드캐스트
    window.dispatchEvent(
      new CustomEvent("artifact-state-change", {
        detail: { open: !!displayedArtifact },
      }),
    );
    if (displayedArtifact) {
      const timer = setTimeout(() => setIsArtifactVisible(true), 10);
      return () => clearTimeout(timer);
    } else {
      setIsArtifactVisible(false);
    }
  }, [displayedArtifact]);

  // 언마운트 시 헤더 상태 복원
  useEffect(() => {
    return () => {
      window.dispatchEvent(
        new CustomEvent("artifact-state-change", { detail: { open: false } }),
      );
    };
  }, []);

  const closeArtifact = () => {
    setIsArtifactVisible(false);
    setTimeout(() => {
      setActiveArtifact(null);
      setIsCanvasExpanded(false);
    }, 400); // 400ms transition
  };
  const [modelMode, setModelMode] = useState<"fast" | "pro">("fast");
  const [isModeDropdownOpen, setIsModeDropdownOpen] = useState(false);
  const [pending, setPending] = useState<PendingAttachment[]>([]);
  const [isUploadingAttachments, setIsUploadingAttachments] = useState(false);

  // 드래그 앤 드롭 업로드 — 자식 요소 사이 드래그 이동 시 flicker 방지를 위해 enter/leave 카운터 사용
  const [isDragging, setIsDragging] = useState(false);
  const dragCounterRef = useRef(0);
  const [imageViewer, setImageViewer] = useState<{
    images: ImgSrc[];
    index: number;
  } | null>(null);
  const [isListening, setIsListening] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isCanvasExpanded, setIsCanvasExpanded] = useState(false);
  // Randomness lives server-side per request (app/(sidebar)/page.tsx); client must render the
  // same text as the server for hydration, so no Math.random() here.
  const safetyHeadline = useMemo(
    () => initialHeadline ?? pickSafetyHeadline(agentId),
    [initialHeadline, agentId],
  );
  const [isClassicMode, setIsClassicMode] = useState(false);
  const [classicFields, setClassicFields] = useState<Record<string, string>>(
    {},
  );
  const pendingSiteEvidenceRef = useRef<RiskSiteEvidence | null>(null);
  const [classicDrafts, setClassicDrafts] = useState<Record<string, string>>(
    {},
  );
  const [classicSuggestionKey, setClassicSuggestionKey] = useState<
    string | null
  >(null);
  const [isAttachMenuOpen, setIsAttachMenuOpen] = useState(false);
  const [isArtifactSubmenuOpen, setIsArtifactSubmenuOpen] = useState(false);
  const [recentArtifacts, setRecentArtifacts] = useState<
    ArtifactSelectionItem[] | null
  >(null);
  const [isArtifactSelectorOpen, setIsArtifactSelectorOpen] = useState(false);
  const [selectedArtifacts, setSelectedArtifacts] = useState<
    ArtifactSelectionItem[]
  >([]);
  const [isAgentSubmenuOpen, setIsAgentSubmenuOpen] = useState(false);

  // Project selector (new chat only) — pick a project to scope the session to.
  type ProjectLite = {
    id: string;
    name: string;
    description?: string | null;
    avatar_file_id?: string | null;
  };
  const [availableProjects, setAvailableProjects] = useState<ProjectLite[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    null,
  );
  const [isProjectSubmenuOpen, setIsProjectSubmenuOpen] = useState(false);
  const [projectSubmenuPos, setProjectSubmenuPos] = useState({
    top: 0,
    left: 0,
  });
  const projectTriggerRef = useRef<HTMLDivElement>(null);
  const projectSubmenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Preselect project from ?project=<id> (set when navigating from project detail page).
    const qp = searchParams.get("project");
    if (qp) setSelectedProjectId(qp);
  }, [searchParams]);

  useEffect(() => {
    // Load projects once for the selector. Only meaningful before first message (pre-session).
    if (activeSessionId) return;
    let cancelled = false;
    (async () => {
      try {
        const projects = await fetchAllProjects({ skipRedirectOn401: true });
        if (!cancelled) setAvailableProjects(projects);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeSessionId]);

  const selectedProject = selectedProjectId
    ? availableProjects.find((p) => p.id === selectedProjectId) || null
    : null;

  // V2 structured event state
  type PipelineStep = PipelineStepType;
  const [pipelineSteps, _setPipelineSteps] = useState<PipelineStep[]>([]);
  const pipelineStepsRef = useRef<PipelineStep[]>([]);
  const setPipelineSteps = (
    updater: PipelineStep[] | ((prev: PipelineStep[]) => PipelineStep[]),
  ) => {
    _setPipelineSteps((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      pipelineStepsRef.current = next;
      return next;
    });
  };
  const [completedPipelines, setCompletedPipelines] = useState<
    Record<number, PipelineStep[]>
  >({});
  // AgentPlanPanel을 다시 활성화할 때 즉시 연결할 수 있도록 계획 상태 수집은 유지한다.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [composerPlanSteps, setComposerPlanSteps] = useState<
    ComposerPlanStepType[]
  >([]);
  const [showComposerPlan, setShowComposerPlan] = useState(false);

  useEffect(() => {
    if (!isGenerating && showComposerPlan && input.trim().length > 0) {
      setShowComposerPlan(false);
      setComposerPlanSteps([]);
    }
  }, [input, isGenerating, showComposerPlan]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const classicImageInputRef = useRef<HTMLInputElement>(null);
  const classicDocumentInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const attachMenuRef = useRef<HTMLDivElement>(null);
  const artifactTriggerRef = useRef<HTMLDivElement>(null);
  const agentTriggerRef = useRef<HTMLDivElement>(null);
  const artifactSubmenuRef = useRef<HTMLDivElement>(null);
  const agentSubmenuRef = useRef<HTMLDivElement>(null);
  const submenuCloseTimeoutRef = useRef<
    ReturnType<typeof setTimeout> | undefined
  >(undefined);
  const classicSuggestionCloseTimeoutRef = useRef<
    ReturnType<typeof setTimeout> | undefined
  >(undefined);
  const fetchedSessionRef = useRef<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const durableStreamAbortControllerRef = useRef<AbortController | null>(null);
  const activeRunIdRef = useRef<string | null>(null);
  const lastAutoOpenedTbmArtifactRef = useRef<string | null>(null);
  const lastRunEventSequenceRef = useRef(0);
  const assistantTextAttemptRef = useRef(new Map<string, number>());
  const assistantTextOffsetRef = useRef(new Map<string, number>());
  const actionTextAttemptRef = useRef(new Map<string, number>());
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const uploadAbortControllersRef = useRef(new Map<string, AbortController>());
  const classicSubmissionRef = useRef(false);
  const reusedEditAttachmentsRef = useRef<RiskAttachment[]>([]);
  const isStreamingRef = useRef(false);
  const activeStreamOwnerRef = useRef<symbol | null>(null);
  const lifecycleSuspendedRef = useRef(false);
  const streamSuspendedRef = useRef(false);
  const streamReconnectTimeoutRef = useRef<number | null>(null);
  const [streamReconnectRevision, setStreamReconnectRevision] = useState(0);
  const recognitionRef = useRef<any>(null);
  const baseInputRef = useRef<string>("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const setTextareaRef = useCallback((el: HTMLTextAreaElement | null) => {
    if (el && el.offsetParent !== null) textareaRef.current = el;
  }, []);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const userScrolledUpRef = useRef(false);

  useEffect(() => {
    if (isGenerating) return;
    const latestTbmArtifact = [...sessionArtifacts]
      .reverse()
      .find(
        (artifact) =>
          artifact.type === "tbm_scenario" || artifact.type === "tbm_record",
      );
    if (!latestTbmArtifact) return;
    const autoOpenKey = `${activeSessionId ?? "session"}:${latestTbmArtifact.key}`;
    if (lastAutoOpenedTbmArtifactRef.current === autoOpenKey) return;
    lastAutoOpenedTbmArtifactRef.current = autoOpenKey;
    setActiveArtifact({
      msgIdx: latestTbmArtifact.msgIdx,
      type: latestTbmArtifact.type,
      title: latestTbmArtifact.title,
    });
  }, [activeSessionId, isGenerating, sessionArtifacts]);

  const applyLiveAssistantDelta = (
    event: LiveTextDeltaEvent,
    runId: string,
    currentText: string,
  ) => {
    const eventAttempt = Number.isSafeInteger(Number(event?.attempt))
      ? Number(event.attempt)
      : 1;
    const cursor: LiveTextCursor = {
      attempt: assistantTextAttemptRef.current.get(runId) ?? eventAttempt,
      offset: assistantTextOffsetRef.current.get(runId) ?? currentText.length,
    };
    const result = mergeLiveTextDelta(currentText, cursor, event);
    if (result.handled) {
      assistantTextAttemptRef.current.set(runId, result.cursor.attempt);
      assistantTextOffsetRef.current.set(runId, result.cursor.offset);
    }
    return result;
  };

  const applyDurableTextEvent = (
    event: DurableRunTextEvent,
    runId: string,
  ): { handled: boolean; terminal: boolean; assistantText?: string } => {
    const sequence = Number(event?.sequence);
    if (Number.isSafeInteger(sequence)) {
      if (sequence <= lastRunEventSequenceRef.current)
        return { handled: true, terminal: false };
      lastRunEventSequenceRef.current = sequence;
    }
    const attempt = Number.isSafeInteger(Number(event?.attempt))
      ? Number(event.attempt)
      : 1;
    if (event.type === "text-snapshot") {
      const currentAttempt = assistantTextAttemptRef.current.get(runId) ?? 1;
      const currentOffset = assistantTextOffsetRef.current.get(runId) ?? 0;
      const snapshotText = typeof event.text === "string" ? event.text : null;
      const declaredOffset = Number(event.textOffset);
      const snapshotOffset =
        Number.isSafeInteger(declaredOffset) &&
        declaredOffset >= 0 &&
        (snapshotText === null || declaredOffset === snapshotText.length)
          ? declaredOffset
          : (snapshotText?.length ?? 0);
      if (
        attempt < currentAttempt ||
        (attempt === currentAttempt && snapshotOffset < currentOffset)
      ) {
        return { handled: true, terminal: false };
      }
      assistantTextAttemptRef.current.set(runId, attempt);
      assistantTextOffsetRef.current.set(runId, snapshotOffset);
      if (typeof event.text === "string") {
        setMessages((previous) =>
          previous.map((message) =>
            message.runId === runId
              ? { ...message, content: event.text }
              : message,
          ),
        );
        return { handled: true, terminal: false, assistantText: event.text };
      }
      return { handled: true, terminal: false };
    }
    if (event.type === "action-text-snapshot") {
      const actionKey = `${runId}:${event.agentId}:${event.actionId ?? "default"}`;
      const currentAttempt = actionTextAttemptRef.current.get(actionKey) ?? 1;
      if (attempt < currentAttempt || typeof event.text !== "string")
        return { handled: true, terminal: false };
      const actionText = event.text;
      actionTextAttemptRef.current.set(actionKey, attempt);
      setPipelineSteps((previous) =>
        previous.map((step) => {
          if (step.agentId !== event.agentId) return step;
          const actions = step.actions ?? [];
          const key = event.actionId ?? "default";
          const exists = actions.some(
            (action) => (action.actionId ?? "default") === key,
          );
          return {
            ...step,
            actions: exists
              ? actions.map((action) =>
                  (action.actionId ?? "default") === key
                    ? {
                        ...action,
                        actionId: event.actionId,
                        content: actionText,
                        attempt,
                      }
                    : action,
                )
              : [
                  ...actions,
                  { actionId: event.actionId, content: actionText, attempt },
                ],
          };
        }),
      );
      return { handled: true, terminal: false };
    }
    if (event.type === "agent-text-reset") {
      if (event.scope === "assistant") {
        const currentAttempt = assistantTextAttemptRef.current.get(runId) ?? 1;
        if (attempt >= currentAttempt) {
          assistantTextAttemptRef.current.set(runId, attempt);
          assistantTextOffsetRef.current.set(runId, 0);
          setMessages((previous) =>
            previous.map((message) =>
              message.runId === runId ? { ...message, content: "" } : message,
            ),
          );
          return { handled: true, terminal: false, assistantText: "" };
        }
      } else if (event.scope === "action" && event.agentId) {
        const actionKey = `${runId}:${event.agentId}:${event.actionId ?? "default"}`;
        const currentAttempt = actionTextAttemptRef.current.get(actionKey) ?? 1;
        if (attempt >= currentAttempt) {
          actionTextAttemptRef.current.set(actionKey, attempt);
          setPipelineSteps((previous) =>
            previous.map((step) =>
              step.agentId !== event.agentId
                ? step
                : {
                    ...step,
                    actions: (step.actions ?? []).map((action) =>
                      (action.actionId ?? "default") ===
                      (event.actionId ?? "default")
                        ? { ...action, content: "", attempt }
                        : action,
                    ),
                  },
            ),
          );
        }
      }
      return { handled: true, terminal: false };
    }
    if (event.type === "run-retry-scheduled") {
      return { handled: true, terminal: false };
    }
    if (event.type === "run-restarted") {
      setPipelineSteps([]);
      setComposerPlanSteps([]);
      assistantTextAttemptRef.current.set(runId, attempt);
      assistantTextOffsetRef.current.set(runId, 0);
      for (const key of actionTextAttemptRef.current.keys()) {
        if (key.startsWith(`${runId}:`))
          actionTextAttemptRef.current.delete(key);
      }
      return { handled: true, terminal: false };
    }
    if (event.type === "run-completed") {
      if (typeof event.content === "string") {
        assistantTextAttemptRef.current.set(runId, attempt);
        assistantTextOffsetRef.current.set(runId, event.content.length);
        setMessages((previous) =>
          previous.map((message) =>
            message.runId === runId
              ? { ...message, content: event.content }
              : message,
          ),
        );
        return { handled: true, terminal: true, assistantText: event.content };
      }
      return { handled: true, terminal: true };
    }
    if (event.type === "run-failed") {
      if (typeof event.content === "string" && event.content.trim()) {
        assistantTextAttemptRef.current.set(runId, attempt);
        assistantTextOffsetRef.current.set(runId, event.content.length);
        setMessages((previous) =>
          previous.map((message) =>
            message.runId === runId
              ? { ...message, content: event.content }
              : message,
          ),
        );
      }
      setPipelineSteps((previous) =>
        previous.map((step) =>
          step.status === "running"
            ? { ...step, status: "error" as const }
            : step,
        ),
      );
      return {
        handled: true,
        terminal: true,
        ...(typeof event.content === "string" && event.content.trim()
          ? { assistantText: event.content }
          : {}),
      };
    }
    if (event.type === "run-cancelled")
      return { handled: true, terminal: true };
    return { handled: false, terminal: false };
  };

  const applyStructuredRunEvent = (event: any): boolean => {
    switch (event.type) {
      case "routing-decision":
        setPipelineSteps(
          (event.agents ?? []).map((agentId: string, index: number) => ({
            agentId,
            agentName: agentId,
            step: index + 1,
            totalSteps: event.agents.length,
            status: "pending" as const,
            resultSummary:
              index === 0 && typeof event.reason === "string"
                ? event.reason
                : undefined,
          })),
        );
        return true;
      case "agent-start":
        setPipelineSteps((previous) => {
          const existing = previous.some(
            (step) => step.agentId === event.agentId,
          );
          return existing
            ? previous.map((step) =>
                step.agentId === event.agentId
                  ? {
                      ...step,
                      agentName: event.agentName ?? step.agentName,
                      status: "running" as const,
                    }
                  : step,
              )
            : [
                ...previous,
                {
                  agentId: event.agentId,
                  agentName: event.agentName ?? event.agentId,
                  step: event.step ?? 1,
                  totalSteps: event.totalSteps ?? 1,
                  status: "running" as const,
                },
              ];
        });
        return true;
      case "agent-complete":
        setPipelineSteps((previous) =>
          previous.map((step) =>
            step.agentId === event.agentId
              ? {
                  ...step,
                  status: "complete" as const,
                  durationMs: event.durationMs,
                }
              : step,
          ),
        );
        return true;
      case "agent-subtasks":
        setPipelineSteps((previous) =>
          previous.map((step) =>
            step.agentId !== event.agentId
              ? step
              : {
                  ...step,
                  subtasks: Array.isArray(event.tasks)
                    ? event.tasks.map((task: any) => ({
                        id: String(task.id),
                        title: String(task.title),
                        durationMs: Number.isFinite(Number(task.durationMs))
                          ? Number(task.durationMs)
                          : undefined,
                        results: Array.isArray(task.results)
                          ? task.results
                          : undefined,
                        resultSummary:
                          typeof task.resultSummary === "string"
                            ? task.resultSummary
                            : undefined,
                      }))
                    : [],
                },
          ),
        );
        return true;
      case "tool-call-start":
        setPipelineSteps((previous) =>
          previous.map((step) =>
            step.status === "running"
              ? {
                  ...step,
                  toolCalls: [
                    ...(step.toolCalls ?? []),
                    {
                      toolCallId: event.toolCallId,
                      toolName: event.toolName,
                      status: "running" as const,
                    },
                  ],
                }
              : step,
          ),
        );
        return true;
      case "tool-result":
        setPipelineSteps((previous) =>
          previous.map((step) => ({
            ...step,
            toolCalls: step.toolCalls?.map((tool) =>
              tool.toolCallId === event.toolCallId
                ? {
                    ...tool,
                    status: (event.status === "error" ? "error" : "success") as
                      "error" | "success",
                  }
                : tool,
            ),
          })),
        );
        return true;
      case "web-search-results":
        setPipelineSteps((previous) =>
          appendWebSearchToSteps(
            previous,
            String(event.agentId ?? "browserAgent"),
            event.search,
          ),
        );
        return true;
      case "web-page-reference":
        setPipelineSteps((previous) =>
          appendWebReferenceToSteps(
            previous,
            String(event.agentId ?? "browserAgent"),
            event.reference,
          ),
        );
        return true;
      case "plan-start":
        setComposerPlanSteps(Array.isArray(event.steps) ? event.steps : []);
        setShowComposerPlan(true);
        return true;
      case "plan-step":
        if (!event.step?.id) return true;
        setComposerPlanSteps((previous) =>
          previous.some((step) => step.id === event.step.id)
            ? previous.map((step) =>
                step.id === event.step.id ? { ...step, ...event.step } : step,
              )
            : [...previous, event.step],
        );
        setShowComposerPlan(true);
        return true;
      case "plan-end":
        setComposerPlanSteps((previous) =>
          previous.map((step) =>
            step.status === "running"
              ? { ...step, status: "complete" as const }
              : step,
          ),
        );
        return true;
      case "error":
        setPipelineSteps((previous) =>
          previous.map((step) =>
            step.status === "running"
              ? { ...step, status: "error" as const }
              : step,
          ),
        );
        return true;
      default:
        return false;
    }
  };

  useEffect(() => {
    const suspendActiveStream = () => {
      lifecycleSuspendedRef.current = true;
      if (!activeRunIdRef.current) return;

      // Core의 durable run은 계속 실행된다. 여기서는 브라우저가 더 이상
      // 소비할 수 없는 수신 연결만 닫고, resume 때 마지막 sequence부터 잇는다.
      streamSuspendedRef.current = true;
      if (streamReconnectTimeoutRef.current) {
        clearTimeout(streamReconnectTimeoutRef.current);
        streamReconnectTimeoutRef.current = null;
      }
      activeStreamOwnerRef.current = null;
      isStreamingRef.current = false;
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
      durableStreamAbortControllerRef.current?.abort();
      durableStreamAbortControllerRef.current = null;
    };

    window.addEventListener(APP_SUSPEND_EVENT, suspendActiveStream);
    return () =>
      window.removeEventListener(APP_SUSPEND_EVENT, suspendActiveStream);
  }, []);

  useEffect(() => {
    const recoverVisibleChat = async () => {
      dragCounterRef.current = 0;
      setIsDragging(false);
      const shouldRecoverSuspendedStream = streamSuspendedRef.current;
      lifecycleSuspendedRef.current = false;
      streamSuspendedRef.current = false;
      const reconnectKnownRun = () => {
        if (!activeRunIdRef.current) return;
        setIsGenerating(true);
        setStreamReconnectRevision((revision) => revision + 1);
      };

      const sessionId = fetchedSessionRef.current;
      if (!sessionId) {
        try {
          const projects = await fetchAllProjects({ skipRedirectOn401: true });
          if (fetchedSessionRef.current) return;
          setAvailableProjects(projects);
        } catch {
          /* retry on the next resume event */
        }
        return;
      }
      if (!shouldRecoverSuspendedStream && isStreamingRef.current) return;

      try {
        const res = await fetchWithAuth(
          `${process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000"}/api/chat/sessions/${sessionId}/messages`,
          { credentials: "include", skipRedirectOn401: false },
        );
        if (!res.ok) {
          if (shouldRecoverSuspendedStream) reconnectKnownRun();
          return;
        }
        const data = await res.json();
        if (
          fetchedSessionRef.current !== sessionId ||
          (!shouldRecoverSuspendedStream && isStreamingRef.current)
        )
          return;

        type ResumedSessionMessage = {
          messageId?: string;
          id?: string;
          role?: string;
          content?: string;
          createdAt?: string;
          created_at?: string;
          metadata?: {
            attachmentIds?: string[];
            attachments?: unknown[];
            is_generating?: boolean;
            pipelineSteps?: PipelineStep[];
            run_id?: string;
            last_event_sequence?: number;
          };
        };
        const serverMessages: ResumedSessionMessage[] = Array.isArray(data)
          ? data
          : data.data?.data ||
            data.data?.items ||
            data.data ||
            data.messages ||
            [];
        setMessages(
          serverMessages.map((message) => ({
            id: message.messageId ?? message.id,
            role: message.role,
            content: message.content,
            createdAt: message.createdAt ?? message.created_at,
            attachmentIds: message.metadata?.attachmentIds,
            attachments: normalizeRiskAttachmentList(
              message.metadata?.attachments,
              message.metadata?.attachmentIds,
            ),
            isGenerating: message.metadata?.is_generating,
            runId: message.metadata?.run_id,
          })),
        );
        setIsGenerating(
          serverMessages.some((message) => message.metadata?.is_generating),
        );
        const activeMessage = serverMessages.find(
          (message) =>
            message.metadata?.is_generating && message.metadata?.run_id,
        );
        activeRunIdRef.current = activeMessage?.metadata?.run_id ?? null;
        setActiveRunId(activeMessage?.metadata?.run_id ?? null);
        lastRunEventSequenceRef.current =
          activeMessage?.metadata?.last_event_sequence ?? 0;
        if (activeMessage?.metadata?.pipelineSteps)
          setPipelineSteps(activeMessage.metadata.pipelineSteps);

        const restored: Record<number, PipelineStep[]> = {};
        serverMessages.forEach((message, index) => {
          if (message.role === "assistant" && message.metadata?.pipelineSteps) {
            restored[index] = message.metadata.pipelineSteps;
          }
        });
        if (Object.keys(restored).length > 0) {
          setCompletedPipelines((previous) => ({ ...previous, ...restored }));
        }
        if (activeMessage?.metadata?.run_id) {
          setStreamReconnectRevision((revision) => revision + 1);
        }
      } catch {
        // 세션 재조회가 일시적으로 실패해도 이미 알고 있는 durable run에는
        // 즉시 다시 붙는다. 다음 resume에서는 canonical message를 다시 읽는다.
        if (shouldRecoverSuspendedStream) reconnectKnownRun();
      }
    };

    window.addEventListener(APP_RESUME_EVENT, recoverVisibleChat);
    return () =>
      window.removeEventListener(APP_RESUME_EVENT, recoverVisibleChat);
  }, []);

  const scrollToBottom = (behavior: ScrollBehavior = "smooth") => {
    const el = scrollContainerRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior });
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setIsModeDropdownOpen(false);
      }
      if (
        attachMenuRef.current &&
        !attachMenuRef.current.contains(target) &&
        (!artifactSubmenuRef.current ||
          !artifactSubmenuRef.current.contains(target)) &&
        (!agentSubmenuRef.current ||
          !agentSubmenuRef.current.contains(target)) &&
        (!projectSubmenuRef.current ||
          !projectSubmenuRef.current.contains(target))
      ) {
        closeAttachMenu();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const uploadControllers = uploadAbortControllersRef.current;
    return () => {
      clearTimeout(classicSuggestionCloseTimeoutRef.current);
      uploadControllers.forEach((controller) => controller.abort());
      uploadControllers.clear();
      abortControllerRef.current?.abort();
      durableStreamAbortControllerRef.current?.abort();
      if (streamReconnectTimeoutRef.current) {
        clearTimeout(streamReconnectTimeoutRef.current);
        streamReconnectTimeoutRef.current = null;
      }
      activeStreamOwnerRef.current = null;
      isStreamingRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      const sh = textareaRef.current.scrollHeight;

      let nextIsExpanded = isExpanded;
      if (!input) {
        nextIsExpanded = false;
      } else if (sh > 36 || input.includes("\n")) {
        nextIsExpanded = true;
      }

      if (nextIsExpanded !== isExpanded) {
        setIsExpanded(nextIsExpanded);
      } else {
        textareaRef.current.style.height = `${sh}px`;
      }
    }
  }, [input, isExpanded]);

  // 첨부는 전송 시점까지 브라우저에만 보관하고, 전송 직전에 Core가 완전히 저장/파싱한
  // 파일만 ready 상태로 전환한다. 위험성평가 이외의 채팅에서는 원본 문서를 받지 않는다.
  const addFiles = useCallback(
    (files: File[]) => {
      if (files.length === 0) return;
      const acceptsDocuments = SOURCE_DOCUMENT_AGENT_IDS.has(agentId);
      const entries: PendingAttachment[] = [];
      let rejectedType = false;
      let rejectedImageSize = false;
      let rejectedDocumentSize = false;

      for (const file of files) {
        const isImage = file.type.startsWith("image/");
        const isDocument = isSourceDocumentFile(file);
        if (!isImage && (!acceptsDocuments || !isDocument)) {
          rejectedType = true;
          continue;
        }
        if (isImage && file.size > MAX_SITE_PHOTO_BYTES) {
          rejectedImageSize = true;
          continue;
        }
        if (isDocument && file.size > MAX_SOURCE_DOCUMENT_BYTES) {
          rejectedDocumentSize = true;
          continue;
        }
        entries.push({
          localId: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
          file,
          name: file.name,
          mimeType: file.type,
          kind: isImage ? "site_photo" : "source_document",
          previewUrl: isImage ? URL.createObjectURL(file) : undefined,
          status: "local",
        });
      }

      const existingPhotoCount = enforceRiskSitePhotoLimit
        ? pending.filter((item) => item.kind === "site_photo").length +
          reusedEditAttachmentsRef.current.filter(
            (item) => item.kind === "site_photo",
          ).length +
          selectedArtifacts.filter(isArtifactSelectionImage).length
        : 0;
      let remainingPhotoSlots = enforceRiskSitePhotoLimit
        ? Math.max(0, MAX_SITE_PHOTOS - existingPhotoCount)
        : Number.POSITIVE_INFINITY;
      const existingDocumentCount =
        pending.filter((item) => item.kind === "source_document").length +
        reusedEditAttachmentsRef.current.filter(
          (item) => item.kind === "source_document",
        ).length;
      let remainingDocumentSlots = Math.max(
        0,
        MAX_SOURCE_DOCUMENTS - existingDocumentCount,
      );
      let rejectedImageCount = 0;
      const limitedEntries = entries.filter((entry) => {
        if (entry.kind === "site_photo") {
          if (remainingPhotoSlots === 0) {
            rejectedImageCount += 1;
            return false;
          }
          remainingPhotoSlots -= 1;
          return true;
        }
        if (remainingDocumentSlots === 0) return false;
        remainingDocumentSlots -= 1;
        return true;
      });
      const acceptedDocumentCount = limitedEntries.filter(
        (entry) => entry.kind === "source_document",
      ).length;
      const rejectedDocumentCount =
        entries.filter((entry) => entry.kind === "source_document").length -
        acceptedDocumentCount;
      entries
        .filter((entry) => !limitedEntries.includes(entry))
        .forEach((entry) => {
          if (entry.previewUrl) URL.revokeObjectURL(entry.previewUrl);
        });

      if (rejectedType)
        alert(
          acceptsDocuments
            ? "이미지 또는 PDF, DOC, DOCX, HWP, HWPX, XLS, XLSX 파일만 첨부할 수 있습니다."
            : "이 채팅에서는 이미지만 첨부할 수 있습니다. 원본 문서는 위험성평가에서 첨부해주세요.",
        );
      const sizeErrors = [
        rejectedImageSize ? SITE_PHOTO_SIZE_ERROR : "",
        rejectedDocumentSize ? SOURCE_DOCUMENT_SIZE_ERROR : "",
      ].filter(Boolean);
      if (sizeErrors.length) alert(sizeErrors.join("\n"));
      if (enforceRiskSitePhotoLimit && rejectedImageCount > 0)
        alert(SITE_PHOTO_COUNT_ERROR);
      if (rejectedDocumentCount > 0)
        alert(
          `원본 문서는 메시지당 최대 ${MAX_SOURCE_DOCUMENTS}개까지 첨부할 수 있습니다.`,
        );
      if (limitedEntries.length === 0) return;
      setPending((prev) => [...prev, ...limitedEntries]);
    },
    [agentId, enforceRiskSitePhotoLimit, pending, selectedArtifacts],
  );

  const addArtifactSelection = (artifact: ArtifactSelectionItem): boolean => {
    if (
      enforceRiskSitePhotoLimit &&
      artifact.kind === "file" &&
      isArtifactSelectionImage(artifact)
    ) {
      if (artifact.size > MAX_SITE_PHOTO_BYTES) {
        alert(SITE_PHOTO_SIZE_ERROR);
        return false;
      }
      const photoKeys = new Set<string>();
      for (const item of pending) {
        if (item.kind !== "site_photo") continue;
        photoKeys.add(
          item.fileId ? `file:${item.fileId}` : `local:${item.localId}`,
        );
      }
      for (const item of reusedEditAttachmentsRef.current) {
        if (item.kind === "site_photo") photoKeys.add(`file:${item.fileId}`);
      }
      for (const item of selectedArtifacts) {
        if (item.kind === "file" && isArtifactSelectionImage(item))
          photoKeys.add(`file:${item.fileId}`);
      }
      const artifactKey = `file:${artifact.fileId}`;
      if (!photoKeys.has(artifactKey) && photoKeys.size >= MAX_SITE_PHOTOS) {
        alert(SITE_PHOTO_COUNT_ERROR);
        return false;
      }
    }
    setSelectedArtifacts((previous) =>
      appendUniqueArtifactSelection(previous, artifact),
    );
    return true;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      addFiles(Array.from(e.target.files));
    }
  };

  const addClassicImageFiles = (files: File[]) => {
    const validImages = files.filter((file) => {
      if (!file.type.startsWith("image/")) {
        alert("이미지 파일만 업로드 가능합니다.");
        return false;
      }
      if (file.size > MAX_SITE_PHOTO_BYTES) {
        alert(SITE_PHOTO_SIZE_ERROR);
        return false;
      }
      return true;
    });
    addFiles(validImages);
  };

  const handleClassicImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      addClassicImageFiles(Array.from(e.target.files));
    }
  };

  const handleClassicDocumentChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    if (e.target.files && e.target.files.length > 0) {
      addFiles(Array.from(e.target.files));
    }
  };

  const openClassicSuggestions = useCallback((key: string) => {
    clearTimeout(classicSuggestionCloseTimeoutRef.current);
    setClassicSuggestionKey(key);
  }, []);

  const closeClassicSuggestions = useCallback(() => {
    clearTimeout(classicSuggestionCloseTimeoutRef.current);
    setClassicSuggestionKey(null);
  }, []);

  const deferCloseClassicSuggestions = useCallback(() => {
    clearTimeout(classicSuggestionCloseTimeoutRef.current);
    classicSuggestionCloseTimeoutRef.current = setTimeout(() => {
      setClassicSuggestionKey(null);
    }, 120);
  }, []);

  const updateClassicField = useCallback(
    (key: string, value: string) => {
      setClassicFields((prev) => {
        const next = { ...prev, [key]: value };
        if (key === "processMajor" && prev.processMajor !== value) {
          const nextSubOptions =
            classicAdapter?.processCategories?.[value.trim()] ?? [];
          if (next.processSub && !nextSubOptions.includes(next.processSub)) {
            next.processSub = "";
          }
        }
        return next;
      });
    },
    [classicAdapter],
  );

  const updateClassicDraft = useCallback((key: string, value: string) => {
    setClassicDrafts((prev) => ({ ...prev, [key]: value }));
  }, []);

  const addClassicToken = useCallback((key: string, value: string) => {
    const token = sanitizeClassicToken(value);
    if (!token) return;

    setClassicFields((prev) => {
      const tokens = parseClassicTokens(prev[key]);
      if (tokens.includes(token)) return prev;
      return { ...prev, [key]: joinClassicTokens([...tokens, token]) };
    });
    setClassicDrafts((prev) => ({ ...prev, [key]: "" }));
  }, []);

  const removeClassicToken = useCallback((key: string, value: string) => {
    setClassicFields((prev) => ({
      ...prev,
      [key]: joinClassicTokens(
        parseClassicTokens(prev[key]).filter((token) => token !== value),
      ),
    }));
  }, []);

  // 드래그 앤 드롭은 window 레벨에서 일괄 처리.
  // 이유: React 컴포넌트 바깥(body/html/sidebar 등)에 떨어져도 브라우저 기본 동작(새 탭 열기)을 막고,
  // 어느 위치에 드롭되든 동일한 업로드 플로우로 태우기 위함.
  useEffect(() => {
    const onDragEnter = (e: DragEvent) => {
      if (!e.dataTransfer?.types?.includes("Files")) return;
      e.preventDefault();
      dragCounterRef.current += 1;
      if (dragCounterRef.current === 1) setIsDragging(true);
    };
    const onDragLeave = (e: DragEvent) => {
      if (!e.dataTransfer?.types?.includes("Files")) return;
      e.preventDefault();
      dragCounterRef.current -= 1;
      if (dragCounterRef.current <= 0) {
        dragCounterRef.current = 0;
        setIsDragging(false);
      }
    };
    const onDragOver = (e: DragEvent) => {
      if (!e.dataTransfer?.types?.includes("Files")) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
    };
    const onDrop = (e: DragEvent) => {
      if (!e.dataTransfer?.types?.includes("Files")) return;
      e.preventDefault();
      dragCounterRef.current = 0;
      setIsDragging(false);
      // 일부 브라우저에서 files가 비어 있을 때 items 폴백으로 추출
      let files: File[] = [];
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        files = Array.from(e.dataTransfer.files);
      } else if (e.dataTransfer.items) {
        for (let i = 0; i < e.dataTransfer.items.length; i++) {
          const item = e.dataTransfer.items[i];
          if (item.kind === "file") {
            const f = item.getAsFile();
            if (f) files.push(f);
          }
        }
      }
      if (files.length > 0) addFiles(files);
    };
    window.addEventListener("dragenter", onDragEnter);
    window.addEventListener("dragleave", onDragLeave);
    window.addEventListener("dragover", onDragOver);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragenter", onDragEnter);
      window.removeEventListener("dragleave", onDragLeave);
      window.removeEventListener("dragover", onDragOver);
      window.removeEventListener("drop", onDrop);
    };
  }, [addFiles]);

  const removeAttachment = (idx: number) => {
    if (isUploadingAttachments || isGenerating) return;
    const target = pending[idx];
    if (!target) return;
    uploadAbortControllersRef.current.get(target.localId)?.abort();
    uploadAbortControllersRef.current.delete(target.localId);
    if (target.previewUrl) {
      try {
        URL.revokeObjectURL(target.previewUrl);
      } catch {}
    }
    setPending((previous) =>
      previous.filter((item) => item.localId !== target.localId),
    );

    // 부분 성공 후 아직 메시지에 연결되지 않은 ready 파일을 제거하면 원본 객체도
    // 즉시 삭제해 MinIO/DB에 고아 파일을 남기지 않는다.
    if (target.fileId) {
      const apiUrl = process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000";
      void fetchWithAuth(`${apiUrl}/api/files/${target.fileId}`, {
        method: "DELETE",
        skipRedirectOn401: true,
      })
        .then((response) => {
          if (!response.ok)
            alert(
              "서버에서 첨부 파일을 삭제하지 못했습니다. 파일 목록에서 다시 삭제해주세요.",
            );
        })
        .catch(() =>
          alert(
            "서버에서 첨부 파일을 삭제하지 못했습니다. 파일 목록에서 다시 삭제해주세요.",
          ),
        );
    }
  };

  const uploadPendingAttachments = useCallback(
    async (attachments: PendingAttachment[]) => {
      const updateEntry = (
        localId: string,
        update: Partial<PendingAttachment>,
      ) => {
        setPending((previous) =>
          previous.map((item) =>
            item.localId === localId ? { ...item, ...update } : item,
          ),
        );
      };

      setIsUploadingAttachments(true);
      try {
        const results = await Promise.all(
          attachments.map(async (entry) => {
            if (entry.status === "ready" && entry.fileId) return entry;

            const controller = new AbortController();
            uploadAbortControllersRef.current.set(entry.localId, controller);
            updateEntry(entry.localId, {
              status: "uploading",
              error: undefined,
            });
            const parsingTimer =
              entry.kind === "source_document"
                ? window.setTimeout(
                    () => updateEntry(entry.localId, { status: "parsing" }),
                    250,
                  )
                : undefined;

            try {
              const uploaded = await uploadAgentAttachment(
                entry.file,
                entry.kind,
                controller.signal,
              );
              const readiness = uploaded.readiness;
              if (entry.kind === "source_document" && readiness !== "ready") {
                throw new Error("문서 분석이 완료되지 않았습니다.");
              }

              const readyEntry: PendingAttachment = {
                ...entry,
                fileId: uploaded.fileId,
                status: "ready",
                format: uploaded.format ?? fileExtension(entry.name),
                characterCount: uploaded.characterCount,
                error: undefined,
              };
              updateEntry(entry.localId, readyEntry);
              return readyEntry;
            } catch (error) {
              const failedEntry: PendingAttachment = {
                ...entry,
                status: "failed",
                error:
                  error instanceof DOMException && error.name === "AbortError"
                    ? "업로드가 취소되었습니다."
                    : error instanceof Error
                      ? error.message
                      : "업로드에 실패했습니다.",
              };
              updateEntry(entry.localId, failedEntry);
              return failedEntry;
            } finally {
              if (parsingTimer !== undefined) window.clearTimeout(parsingTimer);
              uploadAbortControllersRef.current.delete(entry.localId);
            }
          }),
        );
        return results;
      } finally {
        setIsUploadingAttachments(false);
      }
    },
    [],
  );

  const retryAttachment = useCallback(
    async (localId: string) => {
      if (isUploadingAttachments) return;
      const target = pending.find((item) => item.localId === localId);
      if (!target || target.status !== "failed") return;
      await uploadPendingAttachments([target]);
    },
    [isUploadingAttachments, pending, uploadPendingAttachments],
  );

  const closeAttachMenu = () => {
    setIsAttachMenuOpen(false);
    setIsArtifactSubmenuOpen(false);
    setIsAgentSubmenuOpen(false);
    setIsProjectSubmenuOpen(false);
    clearTimeout(submenuCloseTimeoutRef.current);
  };

  const changePreferredAgent = async (nextPreferredAgentId: string | null) => {
    if (!isSupervisorConversation || isPreferredAgentSaving) return;
    if (nextPreferredAgentId === preferredAgentId) {
      closeAttachMenu();
      return;
    }

    const previousPreferredAgentId = preferredAgentId;
    const targetSessionId = activeSessionId;
    preferredAgentRevisionRef.current += 1;
    setPreferredAgentId(nextPreferredAgentId);
    closeAttachMenu();
    if (!targetSessionId) return;

    setIsPreferredAgentSaving(true);
    try {
      await updateAgentSession(targetSessionId, {
        preferredAgentId: nextPreferredAgentId,
      });
    } catch (error) {
      if (fetchedSessionRef.current === targetSessionId) {
        setPreferredAgentId(previousPreferredAgentId);
        alert(
          "에이전트 선택을 저장하지 못했습니다. 잠시 후 다시 시도해 주세요.",
        );
      }
      console.error("[preferred-agent] failed to update session", error);
    } finally {
      setIsPreferredAgentSaving(false);
    }
  };

  // Submenu portal position calculation
  const [artifactSubmenuPos, setArtifactSubmenuPos] = useState({
    top: 0,
    left: 0,
  });
  const [agentSubmenuPos, setAgentSubmenuPos] = useState({ top: 0, left: 0 });

  const calcSubmenuPos = (triggerEl: HTMLElement, submenuWidth: number) => {
    const rect = triggerEl.getBoundingClientRect();
    const viewportW = window.innerWidth;
    const viewportH = window.innerHeight;
    // 메인 팝업과 살짝 겹치게 — positive overlap (submenu 왼쪽이 main 팝업 오른쪽 안쪽으로 4px 들어감)
    const OVERLAP = 4;
    let left = rect.right - OVERLAP;
    let top = rect.top;
    // 우측 공간 부족하면 좌측 flyout 시도 (같은 overlap 만큼 안쪽으로)
    if (left + submenuWidth > viewportW - 4) {
      const leftSide = rect.left + OVERLAP - submenuWidth;
      if (leftSide >= 4) {
        left = leftSide;
      } else {
        // 좌/우 모두 공간 없으면 trigger 아래로
        left = Math.max(4, Math.min(viewportW - submenuWidth - 4, rect.left));
        top = rect.bottom + 4;
      }
    }
    if (top + 200 > viewportH) {
      top = Math.max(8, viewportH - 200);
    }
    return { top, left };
  };

  // 모바일 (< md = 768px) 에서는 팝업 width 를 줄여서 메인 popup 과 submenu 가 한 화면에 들어오게 함
  const getSubmenuWidth = (defaultW: number): number => {
    if (typeof window === "undefined") return defaultW;
    return window.innerWidth < 768 ? Math.min(defaultW, 180) : defaultW;
  };

  const openArtifactSubmenu = (triggerEl: HTMLElement) => {
    setIsAgentSubmenuOpen(false);
    setIsProjectSubmenuOpen(false);
    setArtifactSubmenuPos(calcSubmenuPos(triggerEl, getSubmenuWidth(220)));
    setIsArtifactSubmenuOpen(true);
  };

  const openAgentSubmenu = (triggerEl: HTMLElement) => {
    setIsArtifactSubmenuOpen(false);
    setIsProjectSubmenuOpen(false);
    setAgentSubmenuPos(calcSubmenuPos(triggerEl, getSubmenuWidth(220)));
    setIsAgentSubmenuOpen(true);
  };

  const openProjectSubmenu = (triggerEl: HTMLElement) => {
    setIsArtifactSubmenuOpen(false);
    setIsAgentSubmenuOpen(false);
    setProjectSubmenuPos(calcSubmenuPos(triggerEl, getSubmenuWidth(240)));
    setIsProjectSubmenuOpen(true);
  };

  const handleSubmenuTriggerLeave = (closeFn: () => void) => {
    submenuCloseTimeoutRef.current = setTimeout(closeFn, 150);
  };

  const handleSubmenuPortalEnter = () => {
    clearTimeout(submenuCloseTimeoutRef.current);
  };

  const handleSubmenuPortalLeave = (closeFn: () => void) => {
    closeFn();
  };

  const fetchRecentArtifacts = async () => {
    try {
      setRecentArtifacts(await fetchRecentArtifactSelectionItems(5));
    } catch (err) {
      console.error("Failed to fetch recent artifacts", err);
      setRecentArtifacts([]);
    }
  };

  const handleStop = () => {
    const runId = activeRunIdRef.current;
    if (runId) {
      cancelAgentRun(runId).catch(() => {});
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    if (durableStreamAbortControllerRef.current) {
      durableStreamAbortControllerRef.current.abort();
      durableStreamAbortControllerRef.current = null;
    }
  };

  const toggleListening = () => {
    if (isListening) {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      setIsListening(false);
      return;
    }

    const SpeechRecognition =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert(
        "현재 브라우저에서는 음성 인식을 지원하지 않습니다. (Chrome 브라우저를 이용해주세요.)",
      );
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = "ko-KR";
    recognition.continuous = true;
    recognition.interimResults = true;

    baseInputRef.current = input;

    recognition.onstart = () => {
      setIsListening(true);
    };

    recognition.onresult = (event: any) => {
      let finalTranscript = "";
      let interimTranscript = "";

      for (let i = 0; i < event.results.length; ++i) {
        if (event.results[i].isFinal) {
          finalTranscript += event.results[i][0].transcript + " ";
        } else {
          interimTranscript += event.results[i][0].transcript;
        }
      }

      const prefix = baseInputRef.current
        ? baseInputRef.current.trim() + " "
        : "";
      setInput(prefix + finalTranscript + interimTranscript);
    };

    recognition.onerror = (event: any) => {
      console.error("Speech recognition error:", event.error);
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    recognitionRef.current = recognition;
    recognition.start();
  };

  useEffect(() => {
    const queryStr = searchParams.get("q");
    const storedText = sessionStorage.getItem("seexr_pending_text");
    if (queryStr || storedText) {
      setInput(queryStr || storedText || "");
      sessionStorage.removeItem("seexr_pending_text");
    }
    if (queryStr) {
      window.history.replaceState({}, "", "/");
    }

    // 로그인 전 이미지는 기존 동작대로 복원하되, 문서는 Base64로 저장/복원하지 않는다.
    try {
      const pendingFilesStr = sessionStorage.getItem("seexr_pending_files");
      if (pendingFilesStr) {
        const fileDataArray: Array<{
          name: string;
          type: string;
          data: string;
        }> = JSON.parse(pendingFilesStr);
        const restoredFiles: File[] = [];
        for (const fd of fileDataArray.filter((item) =>
          item.type.startsWith("image/"),
        )) {
          const byteString = atob(fd.data.split(",")[1]);
          const mimeString = fd.data.split(",")[0].split(":")[1].split(";")[0];
          const ab = new ArrayBuffer(byteString.length);
          const ia = new Uint8Array(ab);
          for (let i = 0; i < byteString.length; i++)
            ia[i] = byteString.charCodeAt(i);
          restoredFiles.push(
            new File([new Blob([ab], { type: mimeString })], fd.name, {
              type: fd.type,
            }),
          );
        }
        sessionStorage.removeItem("seexr_pending_files");
        if (restoredFiles.length > 0) addFiles(restoredFiles);
      }
    } catch (e) {
      console.warn("Failed to restore pending files:", e);
      sessionStorage.removeItem("seexr_pending_files");
    }
    if (
      sessionStorage.getItem("seexr_source_documents_need_reattach") === "1"
    ) {
      sessionStorage.removeItem("seexr_source_documents_need_reattach");
      window.setTimeout(
        () =>
          alert(
            "보안을 위해 로그인 전 선택한 원본 문서는 저장하지 않았습니다. PDF, DOC, DOCX, HWP, HWPX, XLS 또는 XLSX 파일을 다시 첨부해주세요.",
          ),
        0,
      );
    }
    const storedPreferredAgentId = sessionStorage.getItem(
      "seexr_pending_preferred_agent",
    );
    if (isSupervisorConversation && isAgentHubItemId(storedPreferredAgentId)) {
      setPreferredAgentId(storedPreferredAgentId);
    }
    sessionStorage.removeItem("seexr_pending_preferred_agent");

    const sessionId = searchParams.get("session");

    // Only fetch if it's a completely new session from the URL
    if (sessionId && fetchedSessionRef.current !== sessionId) {
      fetchedSessionRef.current = sessionId;
      setActiveSessionId(sessionId);
      // 이전 세션의 아티팩트가 새 세션 로딩 화면에 노출되지 않도록 즉시 초기화한다.
      setActiveArtifact(null);
      setIsArtifactVisible(false);
      setIsCanvasExpanded(false);
      preferredAgentRevisionRef.current += 1;
      const preferredAgentRestoreRevision = preferredAgentRevisionRef.current;
      if (isSupervisorConversation) setPreferredAgentId(null);

      if (isSupervisorConversation) {
        void loadAgentSession(sessionId)
          .then((session) => {
            if (
              fetchedSessionRef.current !== sessionId ||
              preferredAgentRevisionRef.current !==
                preferredAgentRestoreRevision
            )
              return;
            setPreferredAgentId(
              isAgentHubItemId(session.preferred_agent_id)
                ? session.preferred_agent_id
                : null,
            );
          })
          .catch((error) => {
            console.error(
              "[preferred-agent] failed to restore session selection",
              error,
            );
          });
      }

      const token = document.cookie
        .split("; ")
        .find((row) => row.startsWith("seexr_access="))
        ?.split("=")[1];
      const headers: Record<string, string> = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;

      fetchWithAuth(
        `${process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000"}/api/chat/sessions/${sessionId}/messages`,
        { headers, credentials: "include", skipRedirectOn401: false },
      )
        .then((res) => {
          if (!res.ok && res.status !== 401) {
            throw new Error("Failed to load messages");
          }
          return res.json();
        })
        .then((data) => {
          const msgs = Array.isArray(data)
            ? data
            : data.data?.data ||
              data.data?.items ||
              data.data ||
              data.messages ||
              [];
          let hasGenerating = false;
          const newMessages = msgs.map((m: any) => {
            if (m.metadata?.is_generating) hasGenerating = true;
            return {
              id: m.messageId ?? m.id,
              role: m.role,
              content: m.content,
              createdAt: m.createdAt ?? m.created_at,
              attachmentIds: m.metadata?.attachmentIds,
              attachments: normalizeRiskAttachmentList(
                m.metadata?.attachments,
                m.metadata?.attachmentIds,
              ),
              isGenerating: m.metadata?.is_generating,
              runId: m.metadata?.run_id,
            };
          });
          setMessages(newMessages);
          setIsGenerating(hasGenerating);
          const activeMessage = msgs.find(
            (m: any) => m.metadata?.is_generating && m.metadata?.run_id,
          );
          activeRunIdRef.current = activeMessage?.metadata?.run_id ?? null;
          setActiveRunId(activeMessage?.metadata?.run_id ?? null);
          lastRunEventSequenceRef.current =
            activeMessage?.metadata?.last_event_sequence ?? 0;
          if (activeMessage?.metadata?.pipelineSteps)
            setPipelineSteps(activeMessage.metadata.pipelineSteps);

          // Restore pipeline steps from metadata
          const restored: Record<number, PipelineStep[]> = {};
          msgs.forEach((m: any, i: number) => {
            if (m.role === "assistant" && m.metadata?.pipelineSteps) {
              restored[i] = m.metadata.pipelineSteps;
            }
          });
          if (Object.keys(restored).length > 0) {
            setCompletedPipelines((prev) => ({ ...prev, ...restored }));
          }
          // Scroll to bottom after loading session messages
          setTimeout(() => scrollToBottom("instant"), 50);
        })
        .catch((err) => console.error("Failed to load prev messages", err));
    } else if (!sessionId && fetchedSessionRef.current !== null) {
      // Prevent clearing messages if we just created a session and window.history was updated but searchParams hasn't caught up
      if (new URLSearchParams(window.location.search).get("session")) {
        return;
      }
      fetchedSessionRef.current = null;
      setActiveSessionId(null);
      setMessages([]);
      preferredAgentRevisionRef.current += 1;
      if (isSupervisorConversation) setPreferredAgentId(null);
      setActiveArtifact(null);
      setIsArtifactVisible(false);
      setIsCanvasExpanded(false);
    }
  }, [searchParams]);

  // Poll for updates if generating but no active SSE stream (e.g. after refresh)
  useEffect(() => {
    if (!isGenerating || !activeSessionId || isStreamingRef.current) return;

    const interval = setInterval(async () => {
      try {
        if (isStreamingRef.current) return;
        const token = document.cookie
          .split("; ")
          .find((row) => row.startsWith("seexr_access="))
          ?.split("=")[1];
        const headers: Record<string, string> = {};
        if (token) headers["Authorization"] = `Bearer ${token}`;

        const res = await fetchWithAuth(
          `${process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000"}/api/chat/sessions/${activeSessionId}/messages`,
          { headers, credentials: "include", skipRedirectOn401: false },
        );
        if (res.ok) {
          const data = await res.json();
          const msgs = Array.isArray(data)
            ? data
            : data.data?.data ||
              data.data?.items ||
              data.data ||
              data.messages ||
              [];

          let stillGenerating = false;
          const newMessages = msgs.map((m: any) => {
            if (m.metadata?.is_generating) stillGenerating = true;
            return {
              id: m.messageId ?? m.id,
              role: m.role,
              content: m.content,
              createdAt: m.createdAt ?? m.created_at,
              attachmentIds: m.metadata?.attachmentIds,
              attachments: normalizeRiskAttachmentList(
                m.metadata?.attachments,
                m.metadata?.attachmentIds,
              ),
              isGenerating: m.metadata?.is_generating,
              runId: m.metadata?.run_id,
            };
          });

          setMessages(newMessages);
          setIsGenerating(stillGenerating);
          const activeMessage = msgs.find(
            (m: any) => m.metadata?.is_generating && m.metadata?.run_id,
          );
          activeRunIdRef.current = activeMessage?.metadata?.run_id ?? null;
          setActiveRunId(activeMessage?.metadata?.run_id ?? null);
          lastRunEventSequenceRef.current =
            activeMessage?.metadata?.last_event_sequence ?? 0;
          if (activeMessage?.metadata?.pipelineSteps)
            setPipelineSteps(activeMessage.metadata.pipelineSteps);

          const restored: Record<number, PipelineStep[]> = {};
          msgs.forEach((m: any, i: number) => {
            if (m.role === "assistant" && m.metadata?.pipelineSteps)
              restored[i] = m.metadata.pipelineSteps;
          });
          if (Object.keys(restored).length > 0) {
            setCompletedPipelines((prev) => ({ ...prev, ...restored }));
          }
        }
      } catch (err) {}
    }, 1000);

    return () => clearInterval(interval);
  }, [isGenerating, activeSessionId]);

  // Reattach to the durable Core event journal after navigation/refresh.
  useEffect(() => {
    if (
      lifecycleSuspendedRef.current ||
      !isGenerating ||
      !activeRunId ||
      isStreamingRef.current
    )
      return;
    const controller = new AbortController();
    const streamOwner = Symbol("durable-agent-run-stream");
    let terminal = false;
    durableStreamAbortControllerRef.current = controller;
    activeStreamOwnerRef.current = streamOwner;
    isStreamingRef.current = true;
    void (async () => {
      try {
        const response = await openAgentRunEvents(
          activeRunId,
          lastRunEventSequenceRef.current,
          controller.signal,
        );
        const reader = response.body?.getReader();
        if (!response.ok || !reader) return;
        const decoder = new TextDecoder();
        let buffer = "";
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() ?? "";
          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            const event = JSON.parse(line.slice(6));
            if (event.type === "text-delta" && event.live === true) {
              setMessages((previous) =>
                previous.map((message) => {
                  if (message.runId !== activeRunId) return message;
                  const result = applyLiveAssistantDelta(
                    event,
                    activeRunId,
                    message.content ?? "",
                  );
                  return result.applied
                    ? { ...message, content: result.text }
                    : message;
                }),
              );
              if (event.text && !userScrolledUpRef.current) {
                scrollToBottom("instant");
              }
              continue;
            }
            const durableResult = applyDurableTextEvent(event, activeRunId);
            if (!durableResult.handled && event.type === "text-delta") {
              const text = typeof event.text === "string" ? event.text : "";
              if (text && event.final !== false) {
                setMessages((previous) =>
                  previous.map((message) =>
                    message.runId === activeRunId
                      ? {
                          ...message,
                          content: `${message.content ?? ""}${text}`,
                        }
                      : message,
                  ),
                );
              } else if (text && event.agentId) {
                setPipelineSteps((previous) =>
                  previous.map((step) =>
                    step.agentId === event.agentId
                      ? {
                          ...step,
                          accumulatedText: `${step.accumulatedText ?? ""}${text}`,
                        }
                      : step,
                  ),
                );
              }
              if (text && !userScrolledUpRef.current) {
                scrollToBottom("instant");
              }
            } else if (!durableResult.handled) {
              applyStructuredRunEvent(event);
            }
            if (durableResult.terminal) {
              terminal = true;
              setIsGenerating(false);
              setActiveRunId(null);
              activeRunIdRef.current = null;
            }
          }
        }
      } catch {
        if (!controller.signal.aborted)
          console.warn("[agent-run] reconnect stream interrupted");
      } finally {
        if (durableStreamAbortControllerRef.current === controller) {
          durableStreamAbortControllerRef.current = null;
        }
        if (activeStreamOwnerRef.current === streamOwner) {
          activeStreamOwnerRef.current = null;
          isStreamingRef.current = false;
          if (
            !terminal &&
            !lifecycleSuspendedRef.current &&
            activeRunIdRef.current === activeRunId
          ) {
            streamReconnectTimeoutRef.current = window.setTimeout(() => {
              streamReconnectTimeoutRef.current = null;
              setStreamReconnectRevision((revision) => revision + 1);
            }, 750);
          }
        }
      }
    })();
    return () => {
      controller.abort();
      if (durableStreamAbortControllerRef.current === controller) {
        durableStreamAbortControllerRef.current = null;
      }
      if (activeStreamOwnerRef.current === streamOwner) {
        activeStreamOwnerRef.current = null;
        isStreamingRef.current = false;
      }
    };
  }, [activeRunId, isGenerating, streamReconnectRevision]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (isGenerating || isUploadingAttachments || isPreferredAgentSaving)
      return;
    const queuedAskUserResponse =
      queuedAskUserResponseRef.current &&
      latestAskUserPrompt?.id === queuedAskUserResponseRef.current.promptId
        ? queuedAskUserResponseRef.current
        : null;
    const typedAskUserResponse =
      !queuedAskUserResponse && latestAskUserPrompt && input.trim()
        ? buildAskUserResponse(latestAskUserPrompt, {
            answerText: input.trim(),
          })
        : null;
    const effectiveInput =
      queuedAskUserResponse?.payload ?? typedAskUserResponse ?? input;
    const hasText = effectiveInput.trim().length > 0;
    const hasAttachments =
      pending.length > 0 || reusedEditAttachmentsRef.current.length > 0;
    const hasArtifacts = selectedArtifacts.length > 0;
    const latestAssistantText =
      [...messages]
        .reverse()
        .find(
          (message) =>
            message?.role === "assistant" &&
            typeof message.content === "string",
        )?.content ?? "";
    const wantsTbmRun =
      agentId === "supervisorAgent" &&
      isTbmGenerationRequest(effectiveInput, latestAssistantText);
    const tbmStage = getTbmGenerationStage(effectiveInput, latestAssistantText);
    let tbmSourceRiskArtifactId: string | null = null;
    let tbmSourceScenarioArtifactId: string | null = null;
    // 텍스트가 없어도 첨부/아티팩트 중 하나라도 있으면 전송 허용 (ChatGPT·Claude와 동일)
    if (!hasText && !hasAttachments && !hasArtifacts) return;
    if (
      selectedArtifacts.some(
        (item) =>
          item.kind === "file" &&
          !isArtifactSelectionImage(item) &&
          !(
            SOURCE_DOCUMENT_AGENT_IDS.has(agentId) &&
            isArtifactSelectionSourceDocument(item)
          ),
      )
    ) {
      alert(
        SOURCE_DOCUMENT_AGENT_IDS.has(agentId)
          ? "이미지 또는 지원되는 원본 문서만 참고 자료로 추가할 수 있습니다."
          : "이 에이전트에는 이미지 아티팩트만 추가할 수 있습니다.",
      );
      return;
    }
    const selectedSourceDocumentCount = selectedArtifacts.filter(
      isArtifactSelectionSourceDocument,
    ).length;
    const pendingSourceDocumentCount = pending.filter(
      (item) => item.kind === "source_document",
    ).length;
    const reusedSourceDocumentCount = reusedEditAttachmentsRef.current.filter(
      (item) => item.kind === "source_document",
    ).length;
    if (
      selectedSourceDocumentCount +
        pendingSourceDocumentCount +
        reusedSourceDocumentCount >
      MAX_SOURCE_DOCUMENTS
    ) {
      alert(
        `원본 문서는 최대 ${MAX_SOURCE_DOCUMENTS}개까지 추가할 수 있습니다.`,
      );
      return;
    }
    if (enforceRiskSitePhotoLimit) {
      const photoKeys = new Set<string>();
      for (const item of pending) {
        if (item.kind !== "site_photo") continue;
        photoKeys.add(
          item.fileId ? `file:${item.fileId}` : `local:${item.localId}`,
        );
      }
      for (const item of reusedEditAttachmentsRef.current) {
        if (item.kind === "site_photo") photoKeys.add(`file:${item.fileId}`);
      }
      for (const item of selectedArtifacts) {
        if (item.kind === "file" && isArtifactSelectionImage(item))
          photoKeys.add(`file:${item.fileId}`);
      }
      if (photoKeys.size > MAX_SITE_PHOTOS) {
        alert(SITE_PHOTO_COUNT_ERROR);
        return;
      }
    }

    // Guest gate: 텍스트와 이미지만 임시 보존한다. 원본 문서는 브라우저 저장소에
    // Base64로 넣지 않고 로그인 후 사용자가 다시 첨부하도록 안내한다.
    const authToken = await ensureAccessToken();
    if (!authToken) {
      sessionStorage.setItem("seexr_pending_text", effectiveInput);
      if (isSupervisorConversation && preferredAgentId) {
        sessionStorage.setItem(
          "seexr_pending_preferred_agent",
          preferredAgentId,
        );
      } else {
        sessionStorage.removeItem("seexr_pending_preferred_agent");
      }
      if (pending.length > 0) {
        try {
          const fileDataArray: Array<{
            name: string;
            type: string;
            data: string;
          }> = [];
          for (const p of pending.filter(
            (item) => item.kind === "site_photo",
          )) {
            const base64 = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result as string);
              reader.onerror = reject;
              reader.readAsDataURL(p.file);
            });
            fileDataArray.push({
              name: p.file.name,
              type: p.file.type,
              data: base64,
            });
          }
          if (fileDataArray.length > 0)
            sessionStorage.setItem(
              "seexr_pending_files",
              JSON.stringify(fileDataArray),
            );
          if (pending.some((item) => item.kind === "source_document")) {
            sessionStorage.setItem("seexr_source_documents_need_reattach", "1");
          }
        } catch (e) {
          console.warn("Failed to save files to sessionStorage:", e);
        }
      }
      const currentPath = `${window.location.pathname}${window.location.search}`;
      const shouldReturnToCurrentSession =
        !!activeSessionId ||
        latestAskUserPrompt !== null ||
        messages.length > 0;
      window.location.href = shouldReturnToCurrentSession
        ? `/login?redirect=${encodeURIComponent(currentPath)}`
        : `/login?q=${encodeURIComponent(getUserMessageDisplayText(effectiveInput).trim())}`;
      return;
    }

    if (wantsTbmRun) {
      if (hasAttachments || hasArtifacts) {
        alert(
          "TBM 교육 자료는 이 대화에 저장된 위험성평가를 기준으로 생성합니다. 새 첨부나 참고 아티팩트를 제거한 뒤 다시 요청해 주세요.",
        );
        return;
      }
      const sid = activeSessionId || fetchedSessionRef.current;
      if (!sid) {
        alert(
          "같은 대화에서 먼저 위험성평가를 완료한 뒤 TBM 교육 자료를 요청해 주세요.",
        );
        return;
      }

      const hasCompletedSource = (message: any) =>
        message?.role === "assistant" &&
        typeof message.content === "string" &&
        (tbmStage === "record"
          ? /<tbm_scenario\b[\s\S]*?<topic\b[\s\S]*?<\/topic>[\s\S]*?<\/tbm_scenario>/i.test(
              message.content,
            )
          : /<risk_assessment\b[\s\S]*?<item\b[\s\S]*?<\/item>[\s\S]*?<\/risk_assessment>/i.test(
              message.content,
            ));
      let sourceMessageIdx = -1;
      for (let index = messages.length - 1; index >= 0; index--) {
        if (hasCompletedSource(messages[index])) {
          sourceMessageIdx = index;
          break;
        }
      }
      let sourceMessage =
        sourceMessageIdx >= 0 ? messages[sourceMessageIdx] : null;

      if (!sourceMessage?.id && !sourceMessage?.messageId) {
        try {
          const apiUrl =
            process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000";
          const response = await fetchWithAuth(
            `${apiUrl}/api/chat/sessions/${sid}/messages`,
            {
              credentials: "include",
              skipRedirectOn401: false,
            },
          );
          if (response.ok) {
            const payload = await response.json();
            const rawMessages = Array.isArray(payload)
              ? payload
              : payload.data?.data ||
                payload.data?.items ||
                payload.data ||
                payload.messages ||
                [];
            if (Array.isArray(rawMessages)) {
              const canonicalMessages = rawMessages.map((message: any) => ({
                ...message,
                id: message.messageId ?? message.id,
                createdAt: message.createdAt ?? message.created_at,
                runId: message.metadata?.run_id,
              }));
              for (
                let index = canonicalMessages.length - 1;
                index >= 0;
                index--
              ) {
                if (hasCompletedSource(canonicalMessages[index])) {
                  sourceMessageIdx = index;
                  sourceMessage = canonicalMessages[index];
                  break;
                }
              }
              setMessages(canonicalMessages);
            }
          }
        } catch (error) {
          console.warn("[tbm] failed to refresh the source artifact", error);
        }
      }

      const sourceMessageId = sourceMessage?.id ?? sourceMessage?.messageId;
      if (sourceMessageIdx < 0 || !sourceMessageId) {
        alert(
          tbmStage === "record"
            ? "저장 완료된 TBM 시나리오를 찾지 못했습니다. 시나리오 생성과 편집 저장을 마친 뒤 다시 요청해 주세요."
            : "저장 완료된 위험성평가를 찾지 못했습니다. 위험성평가 생성이 끝난 뒤 다시 요청해 주세요.",
        );
        return;
      }

      try {
        const apiUrl =
          process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000";
        const query = new URLSearchParams({
          sessionId: sid,
          messageId: String(sourceMessageId),
        });
        if (tbmStage === "record") query.set("kind", "scenario");
        const sourceEndpoint =
          tbmStage === "record"
            ? "tbm-acknowledgement"
            : "risk-acknowledgement";
        const response = await fetchWithAuth(
          `${apiUrl}/api/artifacts/${sourceEndpoint}?${query.toString()}`,
          {
            credentials: "include",
            skipRedirectOn401: false,
          },
        );
        const payload = await response.json().catch(() => null);
        if (!response.ok) {
          throw new Error(
            tbmStage === "record"
              ? "TBM 시나리오 저장 정보를 확인하지 못했습니다."
              : "위험성평가 저장 정보를 확인하지 못했습니다.",
          );
        }
        const sourceArtifactReference =
          unwrapApiData<Record<string, unknown>>(payload);
        const artifactId = sourceArtifactReference?.artifactId;
        if (typeof artifactId !== "string" && typeof artifactId !== "number") {
          throw new Error("TBM 원본 아티팩트 ID를 확인하지 못했습니다.");
        }
        if (tbmStage === "record") {
          tbmSourceScenarioArtifactId = String(artifactId);
        } else {
          tbmSourceRiskArtifactId = String(artifactId);
        }
      } catch (error) {
        alert(
          error instanceof Error
            ? error.message
            : "TBM 원본 정보를 확인하지 못했습니다.",
        );
        return;
      }
    }

    if (isListening) {
      if (recognitionRef.current) recognitionRef.current.stop();
      setIsListening(false);
    }

    setShowComposerPlan(false);
    setComposerPlanSteps([]);
    setPipelineSteps([]);

    // 먼저 모든 로컬/실패 첨부를 Core에 올린다. Core가 문서 파싱과 저장을 모두
    // 끝내 ready를 반환하기 전에는 메시지, 세션, Agent stream을 만들지 않는다.
    let snapshot = pending;
    const artifactSnapshot = selectedArtifacts;
    const userInput = effectiveInput;
    if (snapshot.length > 0) {
      snapshot = await uploadPendingAttachments(snapshot);
      if (snapshot.some((item) => item.status !== "ready" || !item.fileId)) {
        if (classicSubmissionRef.current) {
          setIsClassicMode(true);
          setInput("");
        }
        alert(
          "일부 첨부 파일을 처리하지 못했습니다. 성공한 파일은 유지되며, 실패한 파일만 다시 시도하거나 제거할 수 있습니다.",
        );
        return;
      }
    }

    const uploadedAttachmentDescriptors: RiskAttachment[] = snapshot.flatMap(
      (item) =>
        item.fileId
          ? [
              {
                fileId: item.fileId,
                kind: item.kind,
                name: item.name,
                mimeType: item.mimeType,
                size: item.file.size,
                format: item.format,
                characterCount: item.characterCount,
                readiness: "ready" as const,
              },
            ]
          : [],
    );
    const attachmentDescriptors: RiskAttachment[] = [
      ...reusedEditAttachmentsRef.current,
      ...uploadedAttachmentDescriptors,
    ].filter(
      (attachment, index, all) =>
        all.findIndex(
          (candidate) =>
            candidate.fileId === attachment.fileId &&
            candidate.kind === attachment.kind,
        ) === index,
    );
    const uploadedIds: string[] = attachmentDescriptors
      .filter((item) => item.kind === "site_photo")
      .map((item) => item.fileId);
    const tempMsgId = `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

    setMessages((prev) => [
      ...prev,
      {
        _tempId: tempMsgId,
        role: "user",
        content: userInput,
        createdAt: new Date().toISOString(),
        attachments: attachmentDescriptors.length
          ? attachmentDescriptors
          : undefined,
        attachmentIds: uploadedIds.length ? uploadedIds : undefined,
      },
    ]);
    userScrolledUpRef.current = false;
    setTimeout(() => scrollToBottom("smooth"), 50);
    setIsGenerating(true);
    const primaryStreamOwner = Symbol("primary-agent-run-stream");
    activeStreamOwnerRef.current = primaryStreamOwner;
    isStreamingRef.current = true;

    // --- 아티팩트 컨텍스트 조립 ---
    let userMsg = userInput;
    if (artifactSnapshot.length > 0) {
      try {
        const apiUrl =
          process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000";
        let artifactContext = "";
        for (const art of artifactSnapshot) {
          if (art.kind === "file") {
            if (art.fileId && isArtifactSelectionImage(art)) {
              const fileId = String(art.fileId);
              if (!uploadedIds.includes(fileId)) uploadedIds.push(fileId);
              if (
                !attachmentDescriptors.some((item) => item.fileId === fileId)
              ) {
                attachmentDescriptors.push({
                  fileId,
                  kind: "site_photo",
                  name: typeof art.title === "string" ? art.title : undefined,
                  mimeType: art.mimeType,
                  size: typeof art.size === "number" ? art.size : undefined,
                  readiness: "ready",
                });
              }
            } else if (
              art.fileId &&
              SOURCE_DOCUMENT_AGENT_IDS.has(agentId) &&
              isArtifactSelectionSourceDocument(art)
            ) {
              const fileId = String(art.fileId);
              if (
                !attachmentDescriptors.some(
                  (item) =>
                    item.fileId === fileId && item.kind === "source_document",
                )
              ) {
                attachmentDescriptors.push({
                  fileId,
                  kind: "source_document",
                  name: art.title,
                  mimeType: art.mimeType,
                  size: art.size,
                  format: art.format,
                  characterCount: art.characterCount,
                  readiness: "ready",
                });
              }
            }
          } else {
            const artId = art.rawId || art.id.replace("artifact:", "");
            const res = await fetchWithAuth(
              `${apiUrl}/api/artifacts/${artId}`,
              { credentials: "include" },
            );
            if (res.ok) {
              const data = await res.json();
              if (data.artifact) {
                artifactContext += `\n<artifact title="${data.artifact.title}" type="${data.artifact.type}">\n${data.artifact.content}\n</artifact>\n`;
              }
            }
          }
        }
        if (artifactContext) {
          userMsg = `다음은 사용자가 첨부한 참고용 아티팩트 정보입니다:\n${artifactContext}\n\n사용자 메시지:\n${userMsg || "(아티팩트만 첨부됨)"}`;
        }
      } catch (e) {
        console.error("Failed to load artifact content", e);
      }
      // 사진 descriptor와 하위 호환 attachmentIds만 메시지 메타데이터에 반영한다.
      setMessages((prev) =>
        prev.map((m) =>
          m._tempId === tempMsgId && attachmentDescriptors.length
            ? {
                ...m,
                attachments: attachmentDescriptors,
                attachmentIds: uploadedIds.length ? uploadedIds : undefined,
              }
            : m,
        ),
      );
    }
    let streamAccepted = false;
    let runTerminal = false;
    try {
      // 1. Create a session in DB if it's the first message and no session exists
      let currentSessionId = activeSessionId;
      let createdSessionId: string | null = null;
      if (!currentSessionId && messages.length === 0) {
        try {
          currentSessionId = await createAgentSession({
            agentId,
            projectId: selectedProjectId,
            preferredAgentId: isSupervisorConversation
              ? preferredAgentId
              : undefined,
          });
          fetchedSessionRef.current = currentSessionId; // Prevent useEffect from immediately wiping messages
          createdSessionId = currentSessionId;
          setActiveSessionId(currentSessionId);
          window.history.replaceState(
            null,
            "",
            getAgentSessionHref(agentId, currentSessionId),
          );
          window.dispatchEvent(new Event("session-created"));
        } catch (e) {
          console.error("Failed to create session", e);
          throw e instanceof Error
            ? e
            : new Error("세션을 생성하지 못했습니다.");
        }
      }

      // 2. Fetch from Mastra AI via our core proxy — 첨부는 전송 직전 일괄 업로드를 이미 마쳤고
      //    fileId만 payload로 전달한다.
      const siteEvidence = pendingSiteEvidenceRef.current ?? undefined;
      const deviceContext = await getDeviceContextSnapshot();
      const payload = buildAgentRunPayload({
        message: userMsg,
        sessionId: currentSessionId,
        attachments:
          attachmentDescriptors.length > 0
            ? attachmentDescriptors.map(({ fileId, kind }) => ({
                fileId,
                kind,
              }))
            : undefined,
        siteEvidence,
        userLanguage: languageLabel,
        modelMode,
        reasoningStyle,
        deviceContext,
      });

      // Reset pipeline state for new request
      setPipelineSteps([]);

      const ac = new AbortController();
      abortControllerRef.current = ac;

      let res: Response;
      try {
        const hasTbmSource =
          tbmStage === "record"
            ? !!tbmSourceScenarioArtifactId
            : !!tbmSourceRiskArtifactId;
        const runId =
          wantsTbmRun && hasTbmSource && currentSessionId
            ? await createTbmAgentRun(
                {
                  message: userInput,
                  stage: tbmStage,
                  sessionId: currentSessionId,
                  sourceRiskArtifactId: tbmSourceRiskArtifactId ?? undefined,
                  sourceTbmScenarioArtifactId:
                    tbmSourceScenarioArtifactId ?? undefined,
                  userLanguage: languageLabel,
                  modelMode,
                  reasoningStyle,
                  deviceContext,
                },
                ac.signal,
              )
            : await createAgentRun(agentId, payload, ac.signal);
        streamAccepted = true;
        window.dispatchEvent(new Event("projects-updated"));
        activeRunIdRef.current = runId;
        setActiveRunId(runId);
        lastRunEventSequenceRef.current = 0;
        assistantTextAttemptRef.current.set(runId, 1);
        assistantTextOffsetRef.current.set(runId, 0);
        const sessionIdAwaitingTitle = createdSessionId;
        if (sessionIdAwaitingTitle) {
          void waitForGeneratedSessionTitle(
            sessionIdAwaitingTitle,
            loadAgentSession,
          ).then((title) => {
            if (!title) return;
            window.dispatchEvent(
              new CustomEvent("session-title-updated", {
                detail: { sessionId: sessionIdAwaitingTitle, title },
              }),
            );
            window.dispatchEvent(new Event("projects-updated"));
          });
        }
        res = await openAgentRunEvents(runId, 0, ac.signal);
      } catch (fetchErr: any) {
        throw fetchErr;
      }

      if (res.status === 401) {
        document.cookie = "seexr_access=; Max-Age=0; path=/";
        const currentPath = `${window.location.pathname}${window.location.search}`;
        window.location.href = `/login?error=unauthorized&redirect=${encodeURIComponent(currentPath)}`;
        return;
      }
      if (!res.ok) {
        throw new Error(`서버 오류 (${res.status})`);
      }
      streamAccepted = true;

      queuedAskUserResponseRef.current = null;
      reusedEditAttachmentsRef.current = [];
      setInput("");
      setSelectedArtifacts([]);
      const sentLocalIds = new Set(snapshot.map((item) => item.localId));
      setPending((previous) =>
        previous.filter((item) => !sentLocalIds.has(item.localId)),
      );
      snapshot.forEach((item) => {
        if (item.previewUrl) {
          try {
            URL.revokeObjectURL(item.previewUrl);
          } catch {}
        }
      });

      // Core가 분석 요청을 수락한 뒤에만 Classic 폼과 현장 이력 초안을 비운다.
      pendingSiteEvidenceRef.current = null;
      if (classicSubmissionRef.current) {
        classicSubmissionRef.current = false;
        setClassicFields({});
        setClassicDrafts({});
        closeClassicSuggestions();
      }

      const reader = res.body?.getReader();
      if (!reader) throw new Error("Response body is not readable");

      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "",
          runId: activeRunIdRef.current,
          createdAt: new Date().toISOString(),
        },
      ]);

      const decoder = new TextDecoder();
      let streamTextContent = "";
      let sseBuffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        sseBuffer += decoder.decode(value, { stream: true });
        const lines = sseBuffer.split("\n");
        // Keep the last (potentially incomplete) line in the buffer
        sseBuffer = lines.pop() || "";

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const dataStr = line.substring(6).trim();
              if (dataStr === "[DONE]") continue;
              const dataObj = JSON.parse(dataStr);
              if (dataObj.type === "text-delta" && dataObj.live === true) {
                const liveResult = applyLiveAssistantDelta(
                  dataObj,
                  activeRunIdRef.current ?? "",
                  streamTextContent,
                );
                if (liveResult.handled) {
                  streamTextContent = liveResult.text;
                  continue;
                }
              }
              const durableResult = applyDurableTextEvent(
                dataObj,
                activeRunIdRef.current ?? "",
              );
              if (durableResult.assistantText !== undefined) {
                streamTextContent = durableResult.assistantText;
              }
              if (durableResult.terminal) runTerminal = true;
              if (durableResult.handled) continue;
              if (Number.isSafeInteger(dataObj.sequence))
                lastRunEventSequenceRef.current = dataObj.sequence;

              // ── V2 Structured Event Router ───────────────────
              switch (dataObj.type) {
                case "text-delta": {
                  const tdText = dataObj.text ?? "";
                  if (!tdText) break;

                  // If final (or untagged/single agent) → show directly to user
                  if (dataObj.final !== false) {
                    streamTextContent += tdText;
                  } else {
                    // Intermediate agent → accumulate in pipeline step toggle
                    setPipelineSteps((prev) =>
                      prev.map((p) =>
                        p.agentId === dataObj.agentId
                          ? {
                              ...p,
                              accumulatedText:
                                (p.accumulatedText ?? "") + tdText,
                            }
                          : p,
                      ),
                    );
                  }
                  break;
                }

                case "routing-decision":
                  setPipelineSteps(
                    (dataObj.agents ?? []).map((agId: string, idx: number) => ({
                      agentId: agId,
                      agentName: agId,
                      step: idx + 1,
                      totalSteps: dataObj.agents.length,
                      status: "pending" as const,
                      resultSummary:
                        idx === 0 && typeof dataObj.reason === "string"
                          ? dataObj.reason
                          : undefined,
                    })),
                  );
                  break;

                case "agent-start":
                  setPipelineSteps((prev) => {
                    // If we haven't received routing-decision (single agent), create entry
                    const existing = prev.find(
                      (p) => p.agentId === dataObj.agentId,
                    );
                    if (existing) {
                      return prev.map((p) =>
                        p.agentId === dataObj.agentId
                          ? {
                              ...p,
                              agentName: dataObj.agentName ?? p.agentName,
                              status: "running" as const,
                            }
                          : p,
                      );
                    }
                    return [
                      ...prev,
                      {
                        agentId: dataObj.agentId,
                        agentName: dataObj.agentName ?? dataObj.agentId,
                        step: dataObj.step ?? 1,
                        totalSteps: dataObj.totalSteps ?? 1,
                        status: "running" as const,
                      },
                    ];
                  });
                  break;

                case "agent-complete":
                  setPipelineSteps((prev) =>
                    prev.map((p) =>
                      p.agentId === dataObj.agentId
                        ? {
                            ...p,
                            status: "complete" as const,
                            durationMs: dataObj.durationMs,
                          }
                        : p,
                    ),
                  );
                  break;

                case "agent-subtasks":
                  setPipelineSteps((prev) =>
                    prev.map((p) =>
                      p.agentId === dataObj.agentId
                        ? {
                            ...p,
                            subtasks: Array.isArray(dataObj.tasks)
                              ? dataObj.tasks
                                  .filter(
                                    (task: any) => task?.id && task?.title,
                                  )
                                  .map((task: any) => ({
                                    id: String(task.id),
                                    title: String(task.title),
                                    durationMs: Number.isFinite(
                                      Number(task.durationMs),
                                    )
                                      ? Number(task.durationMs)
                                      : undefined,
                                    results: Array.isArray(task.results)
                                      ? task.results
                                      : undefined,
                                    resultSummary:
                                      typeof task.resultSummary === "string"
                                        ? task.resultSummary
                                        : undefined,
                                  }))
                              : [],
                          }
                        : p,
                    ),
                  );
                  break;

                case "tool-call-start":
                  // Track in pipeline step
                  setPipelineSteps((prev) =>
                    prev.map((p) =>
                      p.status === "running"
                        ? {
                            ...p,
                            toolCalls: [
                              ...(p.toolCalls ?? []),
                              {
                                toolCallId: dataObj.toolCallId,
                                toolName: dataObj.toolName,
                                status: "running" as const,
                                inputPreview:
                                  toolEventPreview(dataObj, "input") ||
                                  undefined,
                              },
                            ],
                          }
                        : p,
                    ),
                  );
                  break;

                case "tool-result":
                  // Update tool status in pipeline step
                  setPipelineSteps((prev) =>
                    prev.map((p) => ({
                      ...p,
                      toolCalls: p.toolCalls?.map((tc) =>
                        tc.toolCallId === dataObj.toolCallId
                          ? {
                              ...tc,
                              status: (dataObj.status === "error"
                                ? "error"
                                : "success") as "running" | "success" | "error",
                              resultPreview:
                                toolEventPreview(dataObj, "result") ||
                                undefined,
                            }
                          : tc,
                      ),
                    })),
                  );
                  break;

                case "web-search-results":
                  setPipelineSteps((prev) =>
                    appendWebSearchToSteps(
                      prev,
                      String(dataObj.agentId ?? "browserAgent"),
                      dataObj.search,
                    ),
                  );
                  break;

                case "web-page-reference":
                  setPipelineSteps((prev) =>
                    appendWebReferenceToSteps(
                      prev,
                      String(dataObj.agentId ?? "browserAgent"),
                      dataObj.reference,
                    ),
                  );
                  break;

                case "plan-start": {
                  const nextSteps = Array.isArray(dataObj.steps)
                    ? dataObj.steps
                        .filter((step: any) => step?.id && step?.title)
                        .map((step: any) => ({
                          id: String(step.id),
                          title: String(step.title),
                          status: [
                            "pending",
                            "running",
                            "complete",
                            "error",
                          ].includes(step.status)
                            ? step.status
                            : "pending",
                          detail:
                            typeof step.detail === "string"
                              ? step.detail
                              : undefined,
                        }))
                    : [];
                  setComposerPlanSteps(nextSteps);
                  setShowComposerPlan(nextSteps.length > 0);
                  break;
                }

                case "plan-step": {
                  const step = dataObj.step;
                  if (!step?.id || !step?.title) break;
                  const normalizedStep: ComposerPlanStepType = {
                    id: String(step.id),
                    title: String(step.title),
                    status: [
                      "pending",
                      "running",
                      "complete",
                      "error",
                    ].includes(step.status)
                      ? step.status
                      : "pending",
                    detail:
                      typeof step.detail === "string" ? step.detail : undefined,
                  };
                  setComposerPlanSteps((prev) => {
                    const exists = prev.some(
                      (item) => item.id === normalizedStep.id,
                    );
                    return exists
                      ? prev.map((item) =>
                          item.id === normalizedStep.id
                            ? { ...item, ...normalizedStep }
                            : item,
                        )
                      : [...prev, normalizedStep];
                  });
                  setShowComposerPlan(true);
                  break;
                }

                case "plan-end":
                  setComposerPlanSteps((prev) =>
                    prev.map((step) =>
                      step.status === "running"
                        ? { ...step, status: "complete" as const }
                        : step,
                    ),
                  );
                  break;

                case "error":
                  setPipelineSteps((prev) => {
                    const running = prev.find((p) => p.status === "running");
                    if (running) {
                      return prev.map((p) =>
                        p.agentId === running.agentId
                          ? { ...p, status: "error" as const }
                          : p,
                      );
                    }
                    return prev;
                  });
                  setComposerPlanSteps((prev) =>
                    prev.map((step) =>
                      step.status === "running"
                        ? { ...step, status: "error" as const }
                        : step,
                    ),
                  );
                  break;

                case "stream-start":
                case "stream-end":
                case "pipeline-update":
                  // Structural events — no UI update needed
                  break;

                default:
                  // Ignore unrecognized event types to prevent
                  // structural events from leaking into chat text.
                  break;
              }
            } catch (e) {
              console.warn(
                "[SSE] Failed to parse event:",
                line.substring(0, 100),
                e,
              );
            }
          } else if (line.startsWith("0:")) {
            try {
              streamTextContent += JSON.parse(line.substring(2).trim());
            } catch (e) {}
          }
        }

        setMessages((prev) => {
          const newM = [...prev];
          newM[newM.length - 1].content = streamTextContent;
          return newM;
        });

        // Auto-scroll during streaming (unless user scrolled up)
        if (!userScrolledUpRef.current) scrollToBottom("instant");
      }
    } catch (err: any) {
      const shouldRestoreClassicForm =
        classicSubmissionRef.current && !streamAccepted;
      const shouldRestoreUnacceptedRequest = !streamAccepted;
      if (shouldRestoreClassicForm) {
        setIsClassicMode(true);
        setInput("");
      }
      if (shouldRestoreUnacceptedRequest) {
        setMessages((previous) =>
          previous.filter((message) => message._tempId !== tempMsgId),
        );
      }
      if (err.name === "AbortError") {
        // User-initiated stop or timeout
        console.log("Request aborted");
      } else {
        console.error(err);
      }
      // 요청 수락 전 Classic 실패는 원래 폼을 그대로 복원한다. 그 외에는 대화 안에
      // 오류 상태를 남겨 사용자가 중단/서버 오류를 확인할 수 있게 한다.
      if (shouldRestoreUnacceptedRequest) {
        alert(
          "분석 요청을 시작하지 못했습니다. 입력값과 처리된 첨부를 유지했습니다.",
        );
      } else if (!activeRunIdRef.current)
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          const errorText =
            err?.name === "AbortError"
              ? "(응답 시간이 너무 오래 걸려 중단되었습니다. 잠시 후 다시 시도해주세요.)"
              : "에이전트 서버와 통신 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.";

          if (last?.role === "assistant") {
            // Replace its content if empty
            if (!last.content?.trim()) {
              const updated = [...prev];
              updated[updated.length - 1] = { ...last, content: errorText };
              return updated;
            }
            return prev;
          } else {
            // If the last message is user, append a new assistant message with the error
            return [...prev, { role: "assistant", content: errorText }];
          }
        });
    } finally {
      if (runTerminal) {
        setIsGenerating(false);
        setActiveRunId(null);
        activeRunIdRef.current = null;
      } else if (activeRunIdRef.current && !lifecycleSuspendedRef.current) {
        const reconnectRunId = activeRunIdRef.current;
        setActiveRunId(null);
        window.setTimeout(() => setActiveRunId(reconnectRunId), 0);
      }
      if (activeStreamOwnerRef.current === primaryStreamOwner) {
        activeStreamOwnerRef.current = null;
        isStreamingRef.current = false;
      }
      // Capture pipeline steps synchronously via ref before clearing
      const finalSteps = [...pipelineStepsRef.current];
      // Stream ended — any agent still 'running' never sent agent-complete; mark as done
      const completedSteps = runTerminal
        ? finalSteps.map((step) =>
            step.status === "running"
              ? { ...step, status: "complete" as const }
              : step,
          )
        : finalSteps;
      setPipelineSteps((previous) => {
        if (!runTerminal) return previous;
        if (completedSteps.length > 0) {
          setMessages((prevMsgs) => {
            const lastAssistantIdx = prevMsgs.length - 1;
            if (
              lastAssistantIdx >= 0 &&
              prevMsgs[lastAssistantIdx]?.role === "assistant"
            ) {
              setCompletedPipelines((prev) => ({
                ...prev,
                [lastAssistantIdx]: completedSteps,
              }));
            }
            return prevMsgs;
          });
        }
        return [];
      });
      // Core owns message metadata, title generation, usage and artifact sync.
      // Refresh the canonical messages; the client never PATCHes generation state.
      const sid = fetchedSessionRef.current || activeSessionId;
      if (sid && !lifecycleSuspendedRef.current) {
        fetchWithAuth(
          `${process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000"}/api/chat/sessions/${sid}/messages`,
          { credentials: "include" },
        )
          .then((res) => res.json())
          .then((data) => {
            const msgs = Array.isArray(data)
              ? data
              : data.data?.data ||
                data.data?.items ||
                data.data ||
                data.messages ||
                [];
            setMessages((prev) => {
              const updated = [...prev];
              for (let i = 0; i < updated.length; i++) {
                if (i < msgs.length)
                  updated[i] = {
                    ...updated[i],
                    id: msgs[i].messageId ?? msgs[i].id,
                    content: msgs[i].content ?? updated[i].content,
                    createdAt:
                      msgs[i].createdAt ??
                      msgs[i].created_at ??
                      updated[i].createdAt,
                    runId: msgs[i].metadata?.run_id,
                  };
              }
              return updated;
            });
          })
          .catch(console.error);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // 한글/일본어/중국어 IME 조합(composition) 중 Enter 는 조합 확정용이므로 제출하지 않는다.
    // isComposing 체크를 빠뜨리면 세션 중 전송 시 마지막 조합 문자가 input 에 남는 버그가 생긴다.
    if (e.nativeEvent.isComposing || e.keyCode === 229) return;
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as unknown as FormEvent);
    }
  };

  // 사용자 메시지 편집 — 해당 인덱스 이후 모두 삭제하고 편집한 텍스트로 재전송
  // DB 저장된 메시지면 서버에서도 해당 메시지와 이후 이터레이션을 전부 제거한다.
  const handleSaveUserEdit = async (msgIdx: number, newText: string) => {
    if (!newText.trim()) return;
    const target = messages[msgIdx];
    const sid = activeSessionId || fetchedSessionRef.current;

    // DB 에 저장된 메시지(optimistic tmp- 아님)이고 세션이 있으면 서버 측 truncate 시도
    if (sid && target?.id && !String(target.id).startsWith("tmp-")) {
      try {
        const apiUrl =
          process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000";
        const res = await fetchWithAuth(
          `${apiUrl}/api/chat/sessions/${sid}/messages/from/${target.id}`,
          { method: "DELETE" },
        );
        if (!res.ok) {
          const body = await res.text().catch(() => "");
          console.error("[truncate]", res.status, body);
          alert("편집 저장 실패: 서버에서 이전 메시지를 삭제하지 못했습니다.");
          return;
        }
      } catch (e) {
        console.error("[truncate] network error", e);
        alert("편집 저장 실패: 네트워크 오류");
        return;
      }
    }

    reusedEditAttachmentsRef.current = normalizeRiskAttachmentList(
      target?.attachments,
      target?.attachmentIds,
    );
    setMessages((prev) => prev.slice(0, msgIdx));
    setInput(newText);
    setEditingMsgIdx(null);
    setPendingEditSubmit(true);
  };

  // pendingEditSubmit 이 세팅되고 input 이 반영된 다음 턴에 폼 자동 제출
  useEffect(() => {
    if (!pendingEditSubmit) return;
    if (isGenerating) return;
    if (!input.trim()) return;
    setPendingEditSubmit(false);
    const form = document.querySelector(
      "form[data-composer]",
    ) as HTMLFormElement | null;
    if (form) form.requestSubmit();
  }, [pendingEditSubmit, input, isGenerating]);

  const handleSaveArtifact = async (
    newArtifactContent: string,
    newTitle: string,
  ) => {
    if (!activeArtifact) return;
    const sid = activeSessionId || fetchedSessionRef.current;
    if (!sid) return;

    // Fallback refresh to grab latest ID if missing locally
    let msg: any = messages[activeArtifact.msgIdx];
    if (!msg || !msg.id) {
      console.error("Message ID not found locally, refreshing...");
      try {
        const token = document.cookie
          .split("; ")
          .find((row) => row.startsWith("seexr_access="))
          ?.split("=")[1];
        const headers: Record<string, string> = {};
        if (token) headers["Authorization"] = `Bearer ${token}`;
        const res = await fetchWithAuth(
          `${process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000"}/api/chat/sessions/${sid}/messages`,
          { headers, credentials: "include" },
        );
        const data = await res.json();
        const msgs = Array.isArray(data)
          ? data
          : data.data?.data ||
            data.data?.items ||
            data.data ||
            data.messages ||
            [];
        if (msgs[activeArtifact.msgIdx]) {
          msg = msgs[activeArtifact.msgIdx];
          // Sync IDs back to local messages
          setMessages((prev) =>
            prev.map((m, i) =>
              i < msgs.length && !m.id
                ? { ...m, id: msgs[i].messageId ?? msgs[i].id }
                : m,
            ),
          );
        }
      } catch (e) {
        console.error(e);
      }
    }

    if (!msg || !msg.id) {
      alert("메시지 동기화 중입니다. 변경 내용을 저장할 수 없습니다.");
      return;
    }

    // Replace content inside main message
    const regexMap: Record<string, string> = {
      risk_assessment: "(<risk_assessment>)[\\s\\S]*?(<\\/risk_assessment>)",
      tbm_scenario: "(<tbm_scenario>)[\\s\\S]*?(<\\/tbm_scenario>)",
      tbm_record: "(<tbm_record>)[\\s\\S]*?(<\\/tbm_record>)",
      cost_estimate: "(<cost_estimate>)[\\s\\S]*?(<\\/cost_estimate>)",
      cost_estimate_material_change:
        "(<cost_estimate_material_change>)[\\s\\S]*?(<\\/cost_estimate_material_change>)",
      cases: "(<cases>)[\\s\\S]*?(<\\/cases>)",
      laws: "(<laws>)[\\s\\S]*?(<\\/laws>)",
    };
    const regexStr = regexMap[activeArtifact.type] || regexMap.cases;

    const newMsgContent = msg.content.replace(
      new RegExp(regexStr),
      `$1\n${newArtifactContent}\n$2`,
    );

    // Optimistic UI update
    setMessages((prev) => {
      const clone = [...prev];
      if (clone[activeArtifact.msgIdx]) {
        clone[activeArtifact.msgIdx] = {
          ...clone[activeArtifact.msgIdx],
          content: newMsgContent,
        };
      }
      return clone;
    });

    try {
      const token = document.cookie
        .split("; ")
        .find((row) => row.startsWith("seexr_access="))
        ?.split("=")[1];
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const res = await fetchWithAuth(
        `${process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000"}/api/chat/sessions/${sid}/messages/${msg.id}`,
        {
          method: "PATCH",
          headers,
          credentials: "include",
          body: JSON.stringify({ content: newMsgContent }),
        },
      );
      if (!res.ok) throw new Error("API Save Error");

      // Sync artifact seamlessly to remote artifacts
      const syncResponse = await fetchWithAuth(
        `${process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000"}/api/artifacts/sync`,
        {
          method: "POST",
          headers,
          credentials: "include",
          body: JSON.stringify({
            title: newTitle,
            type: activeArtifact.type,
            content: newArtifactContent,
            session_id: sid,
            message_id: msg.id,
          }),
        },
      );
      if (!syncResponse.ok) throw new Error("Artifact sync failed");

      setActiveArtifact((prev) => (prev ? { ...prev, title: newTitle } : prev));
    } catch (e) {
      console.error("Failed to save artifact:", e);
      alert("저장 중 서버 오류가 발생했습니다.");
      throw e;
    }
  };

  // assistant 메시지에서 사용자에게 보이는 prose만 추출 — XML 아티팩트/도구 블록 제거
  const extractVisibleText = (content: string): string => {
    return content
      .replace(/<think>[\s\S]*?(?:<\/think>|$)/g, "")
      .replace(/<action\b[^>]*>[\s\S]*?(?:<\/action>|$)/gi, "")
      .replace(
        /<artifact\s+title="[^"]+"\s+type="[^"]+">[\s\S]*?<\/artifact>/g,
        "",
      )
      .replace(/<eval_feedback>[\s\S]*?<\/eval_feedback>/g, "")
      .replace(/<query_used>[\s\S]*?(?:<\/query_used>|$)/g, "")
      .replace(/<ask_user>[\s\S]*?(?:<\/ask_user>|$)/g, "")
      .replace(/<risk_assessment>[\s\S]*?(?:<\/risk_assessment>|$)/g, "")
      .replace(/<tbm_scenario>[\s\S]*?(?:<\/tbm_scenario>|$)/g, "")
      .replace(/<tbm_record>[\s\S]*?(?:<\/tbm_record>|$)/g, "")
      .replace(/<cost_estimate>[\s\S]*?(?:<\/cost_estimate>|$)/g, "")
      .replace(
        /<cost_estimate_material_change>[\s\S]*?(?:<\/cost_estimate_material_change>|$)/g,
        "",
      )
      .replace(/<cases>[\s\S]*?(?:<\/cases>|$)/g, "")
      .replace(/<laws>[\s\S]*?(?:<\/laws>|$)/g, "")
      .replace(/```xml\s*<body>\s*/g, "")
      .replace(/<body>/g, "")
      .replace(/<\/body>/g, "")
      .trim();
  };

  const renderAssistantMessage = (content: string, msgIdx: number) => {
    let elements: any[] = [];
    let remainingText = content;
    let keyIdx = 0;

    // 0. Pipeline timeline — single render location for both live and completed
    const isCurrentlyStreaming = isGenerating && msgIdx === messages.length - 1;
    const stepsToShow = isCurrentlyStreaming
      ? pipelineSteps
      : completedPipelines[msgIdx];
    if (stepsToShow && stepsToShow.length > 0) {
      elements.push(
        <PipelineTimeline
          key={`pipeline-${msgIdx}`}
          steps={stepsToShow}
          isLive={isCurrentlyStreaming}
        />,
      );
    }

    // 1. Extract <think>...</think>
    const thinkRegex = /<think>([\s\S]*?)(?:<\/think>|$)/g;
    let thinkMatch;
    const hasAction = /<action\b/i.test(
      stripArtifactMarkupForControlParsing(content),
    );
    const isFinishedThinking = content.includes("</think>");

    while ((thinkMatch = thinkRegex.exec(remainingText)) !== null) {
      if (!isFinishedThinking || hasAction) {
        elements.push(
          <ActionBlock
            key={`think-${keyIdx++}`}
            title="응답 준비 과정"
            content={thinkMatch[1].trim()}
            type="think"
          />,
        );
      }
    }
    remainingText = remainingText.replace(
      /<think>[\s\S]*?(?:<\/think>|$)/g,
      "",
    );

    // 2. Extract <action title="...">...</action>
    // Accept attribute order/spacing/single quotes and an unfinished closing tag.
    // During SSE streaming, treating a partial <action> as markdown/HTML makes it
    // disappear until </action> arrives (or forever if the stream is interrupted).
    const actionRegex = /<action\b([^>]*)>([\s\S]*?)(?:<\/action>|$)/gi;
    const actionParsingText =
      stripArtifactMarkupForControlParsing(remainingText);
    let actionMatch;
    while ((actionMatch = actionRegex.exec(actionParsingText)) !== null) {
      const titleMatch = actionMatch[1].match(/\btitle\s*=\s*(["'])(.*?)\1/i);
      const title = titleMatch?.[2]?.trim() || "에이전트 작업";
      let blockContent = actionMatch[2].trim();

      if (title === "데이터 분석 완료") {
        const trailing = actionParsingText.substring(
          actionMatch.index + actionMatch[0].length,
        );
        if (trailing.includes("<cases>") && trailing.includes("</cases>")) {
          blockContent = blockContent.replace(
            "답변을 생성합니다.",
            "답변 생성을 완료하였습니다.",
          );
        }
      }

      elements.push(
        <ActionBlock
          key={`action-${keyIdx++}`}
          title={title}
          content={blockContent}
          type="action"
        />,
      );
    }
    remainingText = remainingText.replace(
      /<action\b[^>]*>[\s\S]*?(?:<\/action>|$)/gi,
      "",
    );

    // 3. Unwrap <artifact> without deleting its contents. The previous regex
    // removed the entire block, including nested <risk_assessment>, so restored
    // messages briefly appeared and then vanished after structured rendering.
    remainingText = remainingText
      .replace(/<artifact\b[^>]*>/gi, "")
      .replace(/<\/artifact>/gi, "");

    // 4. Extract <eval_feedback>...</eval_feedback>
    const evalRegex = /<eval_feedback>([\s\S]*?)<\/eval_feedback>/g;
    let evalMatch;
    while ((evalMatch = evalRegex.exec(remainingText)) !== null) {
      const inner = evalMatch[1];
      const passedMatch = inner.match(/<passed>([^<]+)<\/passed>/);
      const agentMatch = inner.match(/<target_agent>([^<]+)<\/target_agent>/);
      const reasonMatch = inner.match(/<reason>([\s\S]*?)<\/reason>/);

      const passed = passedMatch ? passedMatch[1].trim() === "true" : false;
      const agent = agentMatch ? agentMatch[1].trim() : "System";
      const reason = reasonMatch ? reasonMatch[1].trim() : "No details";

      elements.push(
        <ActionBlock
          key={`eval-${keyIdx++}`}
          title={`Agent Evaluation (${agent})`}
          content={`${passed ? "✅ Condition Met" : "❌ Needs Adjustment"}\nDetails: ${reason}`}
          type="eval"
        />,
      );
    }
    remainingText = remainingText.replace(
      /<eval_feedback>[\s\S]*?<\/eval_feedback>/g,
      "",
    );

    // 5. Extract <query_used>
    const queryRegex = /<query_used>([\s\S]*?)(?:<\/query_used>|$)/g;
    let queryMatch;
    while ((queryMatch = queryRegex.exec(remainingText)) !== null) {
      const qContent = queryMatch[1];
      const params = [...qContent.matchAll(/<([^>]+)>([^<]+)<\/[^>]+>/g)].map(
        (m) => m[2],
      );
      if (params.length > 0) {
        elements.push(
          <div
            key={`query-${keyIdx++}`}
            className="flex items-center gap-2 mb-6 mt-2"
          >
            <span className="text-[12px] font-black text-gray-400 uppercase tracking-wider">
              Search Criteria
            </span>
            <div className="flex gap-2 flex-wrap">
              {params.map((p, i) => (
                <span
                  key={i}
                  className="px-3 py-1 text-[12px] font-bold text-seexr-primary bg-seexr-solid/10 rounded-full"
                >
                  {p}
                </span>
              ))}
            </div>
          </div>,
        );
      }
    }
    remainingText = remainingText.replace(
      /<query_used>[\s\S]*?(?:<\/query_used>|$)/g,
      "",
    );

    // 6. Extract <ask_user>
    const askUserRegex = /<ask_user>([\s\S]*?)(?:<\/ask_user>|$)/g;
    let askUserMatch;
    let renderedAskUserPrompt = false;
    while ((askUserMatch = askUserRegex.exec(remainingText)) !== null) {
      const prompt = parseAskUserPrompt(askUserMatch[0]);
      if (prompt) {
        renderedAskUserPrompt = true;
        elements.push(
          <div
            key={`ask-user-${keyIdx++}`}
            className="my-3 max-w-[560px] rounded-xl border border-seexr-100 bg-seexr-50/70 px-4 py-3 dark:border-seexr-900/40 dark:bg-seexr-950/20"
          >
            <div className="text-[11px] font-black uppercase tracking-wide text-seexr-600 dark:text-seexr-300">
              추가 정보 확인
            </div>
            <p className="mt-1 text-[14px] font-bold leading-relaxed text-gray-800 dark:text-gray-100">
              {prompt.question}
            </p>
            {prompt.reason && (
              <p className="mt-1 text-[12px] leading-relaxed text-gray-500 dark:text-gray-400">
                {prompt.reason}
              </p>
            )}
          </div>,
        );
      }
    }
    remainingText = remainingText.replace(
      /<ask_user>[\s\S]*?(?:<\/ask_user>|$)/g,
      "",
    );
    const legacyCostApplyPrompt = renderedAskUserPrompt
      ? null
      : parseLegacyCostMaterialApplyPrompt(remainingText);
    if (legacyCostApplyPrompt) {
      elements.push(
        <div
          key={`ask-user-legacy-${keyIdx++}`}
          className="my-3 max-w-[560px] rounded-xl border border-seexr-100 bg-seexr-50/70 px-4 py-3 dark:border-seexr-900/40 dark:bg-seexr-950/20"
        >
          <div className="text-[11px] font-black uppercase tracking-wide text-seexr-600 dark:text-seexr-300">
            추가 정보 확인
          </div>
          <p className="mt-1 text-[14px] font-bold leading-relaxed text-gray-800 dark:text-gray-100">
            {legacyCostApplyPrompt.question}
          </p>
          {legacyCostApplyPrompt.reason && (
            <p className="mt-1 text-[12px] leading-relaxed text-gray-500 dark:text-gray-400">
              {legacyCostApplyPrompt.reason}
            </p>
          )}
        </div>,
      );
    }
    remainingText = remainingText.replace(
      /\*{0,2}["“]?위\s*변경안을\s*적용할까요\?\s*\(예\/아니오\)["”]?\*{0,2}/g,
      "",
    );

    // 7. Extract <risk_assessment>
    const riskRegex = /<risk_assessment>([\s\S]*?)(?:<\/risk_assessment>|$)/g;
    let riskMatch;
    while ((riskMatch = riskRegex.exec(remainingText)) !== null) {
      const riskTitle = extractArtifactTitle("risk_assessment", riskMatch[1]);
      elements.push(
        <ArtifactBlock
          key={`artifact-risk-${keyIdx++}`}
          title={riskTitle}
          type="risk_assessment"
          onClick={() =>
            setActiveArtifact({
              msgIdx,
              type: "risk_assessment",
              title: riskTitle,
            })
          }
        />,
      );
    }
    remainingText = remainingText.replace(
      /<risk_assessment>[\s\S]*?(?:<\/risk_assessment>|$)/g,
      "",
    );

    // 8. Extract the paired TBM artifacts as soon as their XML snapshots arrive.
    const tbmScenarioRegex = /<tbm_scenario>([\s\S]*?)(?:<\/tbm_scenario>|$)/g;
    let tbmScenarioMatch;
    while ((tbmScenarioMatch = tbmScenarioRegex.exec(remainingText)) !== null) {
      const scenarioTitle = extractArtifactTitle(
        "tbm_scenario",
        tbmScenarioMatch[1],
      );
      elements.push(
        <ArtifactBlock
          key={`artifact-tbm-scenario-${keyIdx++}`}
          title={scenarioTitle}
          type="tbm_scenario"
          onClick={() =>
            setActiveArtifact({
              msgIdx,
              type: "tbm_scenario",
              title: scenarioTitle,
            })
          }
        />,
      );
    }
    remainingText = remainingText.replace(
      /<tbm_scenario>[\s\S]*?(?:<\/tbm_scenario>|$)/g,
      "",
    );

    const tbmRecordRegex = /<tbm_record>([\s\S]*?)(?:<\/tbm_record>|$)/g;
    let tbmRecordMatch;
    while ((tbmRecordMatch = tbmRecordRegex.exec(remainingText)) !== null) {
      const recordTitle = extractArtifactTitle("tbm_record", tbmRecordMatch[1]);
      elements.push(
        <ArtifactBlock
          key={`artifact-tbm-record-${keyIdx++}`}
          title={recordTitle}
          type="tbm_record"
          onClick={() =>
            setActiveArtifact({
              msgIdx,
              type: "tbm_record",
              title: recordTitle,
            })
          }
        />,
      );
    }
    remainingText = remainingText.replace(
      /<tbm_record>[\s\S]*?(?:<\/tbm_record>|$)/g,
      "",
    );

    // 9. Extract <cost_estimate>
    const costEstimateRegex =
      /<cost_estimate>([\s\S]*?)(?:<\/cost_estimate>|$)/g;
    let costEstimateMatch;
    while (
      (costEstimateMatch = costEstimateRegex.exec(remainingText)) !== null
    ) {
      const costEstimateTitle = extractArtifactTitle(
        "cost_estimate",
        costEstimateMatch[1],
      );
      elements.push(
        <ArtifactBlock
          key={`artifact-cost-estimate-${keyIdx++}`}
          title={costEstimateTitle}
          type="cost_estimate"
          onClick={() =>
            setActiveArtifact({
              msgIdx,
              type: "cost_estimate",
              title: costEstimateTitle,
            })
          }
        />,
      );
    }
    remainingText = remainingText.replace(
      /<cost_estimate>[\s\S]*?(?:<\/cost_estimate>|$)/g,
      "",
    );

    // 9. Extract <cost_estimate_material_change>
    const materialChangeRegex =
      /<cost_estimate_material_change>([\s\S]*?)(?:<\/cost_estimate_material_change>|$)/g;
    let materialChangeMatch;
    while (
      (materialChangeMatch = materialChangeRegex.exec(remainingText)) !== null
    ) {
      const materialChangeTitle = extractArtifactTitle(
        "cost_estimate_material_change",
        materialChangeMatch[1],
      );
      elements.push(
        <ArtifactBlock
          key={`artifact-cost-material-change-${keyIdx++}`}
          title={materialChangeTitle}
          type="cost_estimate_material_change"
          onClick={() =>
            setActiveArtifact({
              msgIdx,
              type: "cost_estimate_material_change",
              title: materialChangeTitle,
            })
          }
        />,
      );
    }
    remainingText = remainingText.replace(
      /<cost_estimate_material_change>[\s\S]*?(?:<\/cost_estimate_material_change>|$)/g,
      "",
    );

    // 10. Extract <cases>
    const casesRegex = /<cases>([\s\S]*?)(?:<\/cases>|$)/g;
    let casesMatch;
    while ((casesMatch = casesRegex.exec(remainingText)) !== null) {
      const casesTitle = extractArtifactTitle("cases", casesMatch[1]);
      elements.push(
        <ArtifactBlock
          key={`artifact-cases-${keyIdx++}`}
          title={casesTitle}
          type="cases"
          onClick={() =>
            setActiveArtifact({ msgIdx, type: "cases", title: casesTitle })
          }
        />,
      );
    }
    remainingText = remainingText.replace(
      /<cases>[\s\S]*?(?:<\/cases>|$)/g,
      "",
    );

    // 11. Extract <laws>
    const lawsRegex = /<laws>([\s\S]*?)(?:<\/laws>|$)/g;
    let lawsMatch;
    while ((lawsMatch = lawsRegex.exec(remainingText)) !== null) {
      const lawsTitle = extractArtifactTitle("laws", lawsMatch[1]);
      elements.push(
        <ArtifactBlock
          key={`artifact-laws-${keyIdx++}`}
          title={lawsTitle}
          type="laws"
          onClick={() =>
            setActiveArtifact({ msgIdx, type: "laws", title: lawsTitle })
          }
        />,
      );
    }
    remainingText = remainingText.replace(/<laws>[\s\S]*?(?:<\/laws>|$)/g, "");

    // Fallback: regular text → render as markdown
    remainingText = remainingText
      .replace(/```xml\s*<body>\s*/g, "")
      .replace(/<body>/g, "")
      .replace(/<\/body>/g, "");
    if (remainingText.trim()) {
      elements.push(
        <div
          key={`text-${keyIdx}`}
          className="mt-2 text-[15px] leading-relaxed font-medium prose prose-sm dark:prose-invert max-w-none prose-p:my-1.5 prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0.5 prose-strong:text-gray-900 dark:prose-strong:text-gray-100 prose-headings:text-gray-900 dark:prose-headings:text-gray-100"
        >
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={CHAT_MARKDOWN_COMPONENTS}
          >
            {remainingText.trim()}
          </ReactMarkdown>
        </div>,
      );
    }

    const webReferences = collectWebReferences(stepsToShow);
    if (webReferences.length > 0) {
      elements.push(
        <WebReferenceBadges
          key={`web-references-${msgIdx}`}
          references={webReferences}
        />,
      );
    }

    return <div className="w-full flex flex-col">{elements}</div>;
  };

  const queueAskUserResponse = (prompt: AskUserPrompt, payload: string) => {
    queuedAskUserResponseRef.current = { promptId: prompt.id, payload };
    setInput(getUserMessageDisplayText(payload));
    setAskUserDraft({});
    window.setTimeout(() => {
      const form = document.querySelector(
        "form[data-composer]",
      ) as HTMLFormElement | null;
      form?.requestSubmit();
    }, 0);
  };

  const getPromptOptions = (prompt: AskUserPrompt): AskUserOption[] => {
    if (prompt.options.length > 0) return prompt.options;
    if (prompt.mode === "confirm") {
      return [
        { id: "yes", label: "네, 진행해 주세요", value: "yes" },
        { id: "no", label: "아니요", value: "no" },
      ];
    }
    return [];
  };

  const getFieldDisplayValue = (
    field: AskUserField,
    value: string | string[] | boolean | undefined,
  ) => {
    if (Array.isArray(value)) {
      return value
        .map(
          (v) =>
            field.options.find(
              (option) => option.value === v || option.id === v,
            )?.label || v,
        )
        .join(", ");
    }
    if (typeof value === "boolean") return value ? "예" : "아니요";
    if (!value) return "";
    return (
      field.options.find(
        (option) => option.value === value || option.id === value,
      )?.label || value
    );
  };

  const updateAskUserDraft = (
    key: string,
    value: string | string[] | boolean,
  ) => {
    setAskUserDraft((prev) => ({ ...prev, [key]: value }));
  };

  const defaultCostPartOptions: AskUserOption[] = [
    {
      id: "exterior_finish",
      label: "외장 마감재",
      value: "외장 마감재",
      description: "석재, 금속패널, 커튼월, 외벽 타일 등",
    },
    {
      id: "interior_wall_finish",
      label: "내장 벽 마감재",
      value: "내장 벽 마감재",
      description: "도장, 벽지, 타일, 목재 패널 등",
    },
    {
      id: "floor_finish",
      label: "바닥 마감재",
      value: "바닥 마감재",
      description: "타일, 석재, 목재, 비닐, 카펫 등",
    },
    {
      id: "ceiling_finish",
      label: "천장 마감재",
      value: "천장 마감재",
      description: "석고보드, 텍스, 흡음재, 금속 천장재 등",
    },
    {
      id: "window_door",
      label: "창호/문",
      value: "창호/문",
      description: "알루미늄 창호, 시스템 창호, 방화문 등",
    },
    {
      id: "roof_finish",
      label: "지붕/방수",
      value: "지붕/방수",
      description: "방수층, 지붕재, 단열재 등",
    },
    {
      id: "mep_finish",
      label: "설비/전기 자재",
      value: "설비/전기 자재",
      description: "조명, 위생기구, 공조 관련 자재 등",
    },
  ];

  const defaultCostMaterialOptions: AskUserOption[] = [
    { id: "porcelain_tile", label: "포세린 타일", value: "포세린 타일" },
    { id: "ceramic_tile", label: "세라믹 타일", value: "세라믹 타일" },
    { id: "granite", label: "화강석", value: "화강석" },
    { id: "marble", label: "대리석", value: "대리석" },
    { id: "wood_flooring", label: "목재 마루", value: "목재 마루" },
    { id: "vinyl_flooring", label: "비닐계 바닥재", value: "비닐계 바닥재" },
    { id: "paint", label: "친환경 도장", value: "친환경 도장" },
    { id: "aluminum_panel", label: "알루미늄 패널", value: "알루미늄 패널" },
    { id: "curtain_wall", label: "커튼월 유리", value: "커튼월 유리" },
    { id: "custom", label: "직접 입력", value: "직접 입력" },
  ];

  const renderCostMaterialReplacementPanel = (prompt: AskUserPrompt) => {
    const findField = (...ids: string[]) =>
      prompt.fields.find((field) => ids.includes(field.id));
    const locationField = findField("location", "part", "finish_location");
    const originalField = findField("original_material", "current_material");
    const newMaterialField = findField("new_material", "replacement_material");
    const customField = findField("custom_material");
    const scopeField = findField("scope_note", "scope", "quantity_note");
    const locationKey = locationField?.id || "location";
    const originalKey = originalField?.id || "original_material";
    const newMaterialKey = newMaterialField?.id || "new_material";
    const customKey = customField?.id || "custom_material";
    const scopeKey = scopeField?.id || "scope_note";
    const newMaterialOptions = newMaterialField?.options.length
      ? newMaterialField.options
      : defaultCostMaterialOptions;
    const step = (
      typeof askUserDraft.__cost_step === "string"
        ? askUserDraft.__cost_step
        : "location"
    ) as CostWizardStep;
    const getString = (key: string) =>
      typeof askUserDraft[key] === "string"
        ? (askUserDraft[key] as string)
        : "";
    const selectedLocation = getString(locationKey);
    const selectedOriginal = getString(originalKey);
    const selectedNewMaterial = getString(newMaterialKey);
    const customMaterial = getString(customKey).trim();
    const scopeNote = getString(scopeKey).trim();
    const normalizeLocation = (value: string) =>
      value.replace(/\s+/g, "").toLowerCase();
    const matchesSelectedLocation = (material: CostEstimateMaterialItem) => {
      if (!selectedLocation) return false;
      const itemLocation = normalizeLocation(material.location);
      const selected = normalizeLocation(selectedLocation);
      return (
        itemLocation === selected ||
        itemLocation.includes(selected) ||
        selected.includes(itemLocation)
      );
    };
    const materialLocationOptions = Array.from(
      new Set(latestCostEstimateMaterials.map((item) => item.location)),
    ).map((location, index): AskUserOption => ({
      id: `actual_location_${normalizeCostOptionId(location, String(index + 1))}`,
      label: location,
      value: location,
      description: `${latestCostEstimateMaterials.filter((item) => item.location === location).length}개 자재`,
    }));
    const locationOptions =
      materialLocationOptions.length > 0
        ? materialLocationOptions
        : locationField?.options.length
          ? locationField.options
          : defaultCostPartOptions;
    const matchedOriginalMaterials = latestCostEstimateMaterials.filter(
      matchesSelectedLocation,
    );
    const actualOriginalOptions = matchedOriginalMaterials.map(
      (material, index): AskUserOption => ({
        id: `actual_original_${material.id || index + 1}`,
        label: material.materialName,
        value: material.materialName,
        description:
          [
            material.location,
            material.quantity && material.unit
              ? `${material.quantity}${material.unit}`
              : material.quantity || material.unit || "",
          ]
            .filter(Boolean)
            .join(" · ") || undefined,
      }),
    );
    const fallbackOriginalOptions = originalField?.options ?? [];
    const originalOptions = [
      ...(actualOriginalOptions.length > 0
        ? actualOriginalOptions
        : fallbackOriginalOptions),
      {
        id: "unknown",
        label: "잘 모르겠음",
        value: "잘 모르겠음",
        description: "선택한 부위의 대표 마감재를 기준으로 계산",
      },
    ];
    const selectedLocationOption = locationOptions.find(
      (option) =>
        option.value === selectedLocation || option.id === selectedLocation,
    );
    const selectedOriginalOption = originalOptions.find(
      (option) =>
        option.value === selectedOriginal || option.id === selectedOriginal,
    );
    const selectedNewOption = newMaterialOptions.find(
      (option) =>
        option.value === selectedNewMaterial ||
        option.id === selectedNewMaterial,
    );
    const locationLabel = selectedLocationOption?.label || selectedLocation;
    const originalLabel = selectedOriginalOption?.label || selectedOriginal;
    const newMaterialLabel = selectedNewOption?.label || selectedNewMaterial;
    const resolvedNewMaterial =
      customMaterial ||
      (selectedNewMaterial === "직접 입력" ? "" : newMaterialLabel);
    const parseChanges = (): CostMaterialChangeDraft[] => {
      try {
        const parsed = JSON.parse(getString("__cost_changes") || "[]");
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    };
    const changes = parseChanges();
    const setStep = (next: CostWizardStep) => {
      setAskUserDraft((prev) => ({ ...prev, __cost_step: next }));
      window.setTimeout(() => costWizardPanelRef.current?.focus(), 0);
    };
    const goBackStep = () => {
      if (step === "original") setStep("location");
      else if (step === "new_material") setStep("original");
      else if (step === "scope") setStep("new_material");
      else if (step === "summary") setStep("location");
    };
    const resetCurrentSelection = () => {
      setAskUserDraft((prev) => ({
        ...prev,
        [locationKey]: "",
        [originalKey]: "",
        [newMaterialKey]: "",
        [customKey]: "",
        [scopeKey]: "",
        __cost_step: "location",
      }));
    };
    const saveCurrentChange = () => {
      if (!selectedLocation || !resolvedNewMaterial) return;
      const nextChanges: CostMaterialChangeDraft[] = [
        ...changes,
        {
          id: `change_${Date.now()}`,
          location: selectedLocation,
          locationLabel,
          originalMaterial: selectedOriginal,
          originalLabel,
          newMaterial: resolvedNewMaterial,
          scopeNote,
        },
      ];
      setAskUserDraft((prev) => ({
        ...prev,
        __cost_changes: JSON.stringify(nextChanges),
        [locationKey]: "",
        [originalKey]: "",
        [newMaterialKey]: "",
        [customKey]: "",
        [scopeKey]: "",
        __cost_step: "summary",
      }));
      window.setTimeout(() => costWizardPanelRef.current?.focus(), 0);
    };
    const removeCostMaterialChange = (changeId: string) => {
      const nextChanges = changes.filter((change) => change.id !== changeId);
      setAskUserDraft((prev) => ({
        ...prev,
        __cost_changes: JSON.stringify(nextChanges),
      }));
    };
    const submitCostMaterialChanges = () => {
      const finalChanges = changes.length > 0 ? changes : [];
      if (finalChanges.length === 0) return;
      const answerText = finalChanges
        .map((change, index) => {
          const original =
            change.originalLabel && change.originalLabel !== "잘 모르겠음"
              ? `${change.originalLabel}을 `
              : "";
          const scope = change.scopeNote
            ? ` 적용 범위: ${change.scopeNote}`
            : "";
          return `${index + 1}. ${change.locationLabel}: ${original}${change.newMaterial}로 변경.${scope}`;
        })
        .join("\n");
      const first = finalChanges[0];
      queueAskUserResponse(
        prompt,
        buildAskUserResponse(prompt, {
          answerText: `다음 자재 변경안으로 공사비를 다시 계산해줘.\n${answerText}`,
          fieldValues: {
            changes: JSON.stringify(finalChanges),
            change_count: String(finalChanges.length),
            [locationKey]: first.locationLabel,
            [originalKey]: first.originalLabel || "",
            [newMaterialKey]: first.newMaterial,
            [scopeKey]: first.scopeNote || "",
          },
        }),
      );
    };

    const optionConfig =
      step === "location"
        ? {
            title: "무엇을 바꿀까요?",
            options: locationOptions,
            value: selectedLocation,
            onPick: (option: AskUserOption) => {
              setAskUserDraft((prev) => ({
                ...prev,
                [locationKey]: option.value,
                [originalKey]: "",
                [newMaterialKey]: "",
                [customKey]: "",
                [scopeKey]: "",
                __cost_step: "original",
              }));
              window.setTimeout(() => costWizardPanelRef.current?.focus(), 0);
            },
          }
        : step === "original"
          ? {
              title: "기존의 어떤 자재인가요?",
              options: originalOptions,
              value: selectedOriginal,
              onPick: (option: AskUserOption) => {
                updateAskUserDraft(originalKey, option.value);
                setStep("new_material");
              },
            }
          : step === "new_material"
            ? {
                title: "무엇으로 바꿀까요?",
                options: newMaterialOptions,
                value: selectedNewMaterial,
                onPick: (option: AskUserOption) => {
                  updateAskUserDraft(newMaterialKey, option.value);
                  setStep(option.value === "직접 입력" ? "scope" : "scope");
                },
              }
            : null;
    const visibleOptions = optionConfig ? optionConfig.options : [];
    const optionGridClass =
      visibleOptions.length <= 4
        ? "grid-cols-1"
        : visibleOptions.length <= 8
          ? "grid-cols-2"
          : "grid-cols-3";
    const canSaveCurrent = !!selectedLocation && !!resolvedNewMaterial;
    const stepNumber =
      step === "location"
        ? 1
        : step === "original"
          ? 2
          : step === "new_material"
            ? 3
            : step === "scope"
              ? 4
              : 5;

    const handleWizardKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement | null;
      if (target && ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName))
        return;
      if (/^[1-4]$/.test(event.key) && optionConfig) {
        const option = visibleOptions[Number(event.key) - 1];
        if (option) {
          event.preventDefault();
          optionConfig.onPick(option);
        }
      }
    };

    return (
      <div
        ref={costWizardPanelRef}
        tabIndex={0}
        onKeyDown={handleWizardKeyDown}
        className="mx-2 mt-2 max-h-[288px] overflow-hidden rounded-[24px] p-2.5 outline-none ring-0"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            {step !== "location" && (
              <button
                type="button"
                onClick={goBackStep}
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-gray-500 transition hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-[#202022]"
                aria-label="이전 단계"
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M15 18l-6-6 6-6" />
                </svg>
              </button>
            )}
            <div className="min-w-0">
              <div className="text-[10px] font-black uppercase tracking-wide text-gray-500 dark:text-gray-400">
                개산견적 조정
              </div>
              <p className="truncate text-[13px] font-black text-gray-900 dark:text-gray-100">
                {step === "summary"
                  ? "변경안을 확인하세요"
                  : optionConfig?.title}
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <span
                key={n}
                className={`h-1.5 w-5 rounded-full ${n <= stepNumber ? "bg-gray-900 dark:bg-white" : "bg-gray-200 dark:bg-[#2a2a2e]"}`}
              />
            ))}
          </div>
        </div>

        {step !== "summary" && (
          <div className="mt-2 flex min-h-[28px] flex-wrap items-center gap-1.5 text-[11px] font-bold text-gray-500 dark:text-gray-400">
            {locationLabel && (
              <span className="rounded-full bg-gray-100 px-2 py-1 text-gray-700 dark:bg-[#202022] dark:text-gray-200">
                {locationLabel}
              </span>
            )}
            {originalLabel && (
              <span className="rounded-full bg-gray-100 px-2 py-1 text-gray-700 dark:bg-[#202022] dark:text-gray-200">
                {originalLabel}
              </span>
            )}
            {resolvedNewMaterial && (
              <span className="rounded-full bg-gray-100 px-2 py-1 text-gray-700 dark:bg-[#202022] dark:text-gray-200">
                {resolvedNewMaterial}
              </span>
            )}
          </div>
        )}

        {optionConfig && (
          <div className="mt-2 max-h-[174px] overflow-y-auto pr-1">
            <div className={`grid ${optionGridClass} gap-1.5`}>
              {visibleOptions.map((option, index) => {
                const selected =
                  option.value === optionConfig.value ||
                  option.id === optionConfig.value;
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => optionConfig.onPick(option)}
                    className={`flex min-h-[46px] items-center gap-2 rounded-xl border px-2.5 py-2 text-left transition ${
                      selected
                        ? "border-gray-900 bg-gray-900 text-white shadow-sm dark:border-white dark:bg-white dark:text-gray-900"
                        : "border-transparent bg-gray-100 text-gray-800 hover:bg-gray-200 dark:bg-[#202022] dark:text-gray-100 dark:hover:bg-[#2a2a2e]"
                    }`}
                  >
                    <span
                      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-black ${selected ? "bg-white text-gray-900 dark:bg-gray-900 dark:text-white" : "bg-white text-gray-600 dark:bg-[#161618] dark:text-gray-300"}`}
                    >
                      {index + 1}
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate text-[12px] font-black">
                        {option.label}
                      </span>
                      {option.description && (
                        <span
                          className={`block truncate text-[10px] ${selected ? "text-white/75" : "text-gray-400"}`}
                        >
                          {option.description}
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {step === "scope" && (
          <div className="mt-2">
            {selectedNewMaterial === "직접 입력" && (
              <input
                value={customMaterial}
                onChange={(event) =>
                  updateAskUserDraft(customKey, event.target.value)
                }
                placeholder={
                  customField?.placeholder || "바꿀 자재를 입력하세요"
                }
                className="mb-2 h-9 w-full rounded-xl border-0 bg-gray-100 px-3 text-[13px] font-semibold outline-none focus:ring-2 focus:ring-gray-200 dark:bg-[#202022] dark:focus:ring-[#2a2a2e]"
              />
            )}
            <textarea
              value={scopeNote}
              onChange={(event) =>
                updateAskUserDraft(scopeKey, event.target.value)
              }
              placeholder={
                scopeField?.placeholder ||
                "적용 범위/수량을 적어주세요. 예: 1층 로비 바닥만"
              }
              rows={2}
              className="h-[70px] w-full resize-none rounded-xl border-0 bg-gray-100 px-3 py-2 text-[13px] font-semibold outline-none focus:ring-2 focus:ring-gray-200 dark:bg-[#202022] dark:focus:ring-[#2a2a2e]"
            />
            <div className="mt-2 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={saveCurrentChange}
                disabled={!canSaveCurrent}
                className="rounded-full bg-gray-900 px-4 py-1.5 text-[12px] font-bold text-white transition hover:bg-black disabled:opacity-40 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-200"
              >
                변경안 저장
              </button>
            </div>
          </div>
        )}

        {step === "summary" && (
          <div className="mt-2">
            <div className="max-h-[108px] space-y-1.5 overflow-y-auto pr-1">
              {changes.map((change, index) => (
                <div
                  key={change.id}
                  className="flex items-start gap-2 rounded-xl bg-gray-100 px-3 py-2 text-[12px] font-bold text-gray-800 dark:bg-[#202022] dark:text-gray-100"
                >
                  <div className="min-w-0 flex-1">
                    <span className="mr-1 text-gray-500">{index + 1}.</span>
                    {change.locationLabel}{" "}
                    {change.originalLabel &&
                    change.originalLabel !== "잘 모르겠음"
                      ? `${change.originalLabel} -> `
                      : "-> "}
                    {change.newMaterial}
                    {change.scopeNote && (
                      <span className="ml-1 text-gray-400">
                        ({change.scopeNote})
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => removeCostMaterialChange(change.id)}
                    className="-mr-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[12px] font-black text-gray-400 transition hover:bg-gray-200 hover:text-gray-700 dark:hover:bg-[#2a2a2e] dark:hover:text-gray-100"
                    aria-label={`${change.locationLabel} 변경안 삭제`}
                  >
                    x
                  </button>
                </div>
              ))}
              {changes.length === 0 && (
                <div className="rounded-xl bg-gray-100 px-3 py-4 text-center text-[12px] font-bold text-gray-400 dark:bg-[#202022]">
                  저장된 변경안이 없습니다.
                </div>
              )}
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={resetCurrentSelection}
                className="rounded-full bg-gray-100 px-3 py-2 text-[12px] font-black text-gray-700 hover:bg-gray-200 dark:bg-[#202022] dark:text-gray-200 dark:hover:bg-[#2a2a2e]"
              >
                다른 부위 추가
              </button>
              <button
                type="button"
                onClick={submitCostMaterialChanges}
                disabled={changes.length === 0}
                className="rounded-full bg-gray-900 px-3 py-2 text-[12px] font-black text-white transition hover:bg-black disabled:opacity-40 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-200"
              >
                {prompt.submitLabel || "변경 견적 요청"}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderAskUserInteractivePanel = (prompt: AskUserPrompt) => {
    if (prompt.mode === "cost_material_replacement") {
      return renderCostMaterialReplacementPanel(prompt);
    }

    const promptOptions = getPromptOptions(prompt);
    const selectedIds = Array.isArray(askUserDraft.__options)
      ? askUserDraft.__options
      : [];
    const selectedOptions = promptOptions.filter((option) =>
      selectedIds.includes(option.id),
    );
    const toggleOption = (option: AskUserOption) => {
      const next = selectedIds.includes(option.id)
        ? selectedIds.filter((id) => id !== option.id)
        : [...selectedIds, option.id];
      updateAskUserDraft("__options", next);
    };
    const submitSelectedOptions = () => {
      if (selectedOptions.length === 0) return;
      queueAskUserResponse(
        prompt,
        buildAskUserResponse(prompt, {
          answerText: selectedOptions.map((option) => option.label).join(", "),
          selections: selectedOptions,
        }),
      );
    };
    const requiredFieldsReady = prompt.fields.every((field) => {
      if (!field.required) return true;
      const value = askUserDraft[field.id];
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === "boolean") return true;
      return typeof value === "string" && value.trim().length > 0;
    });
    const submitFieldValues = () => {
      if (!requiredFieldsReady) return;
      const answerParts = prompt.fields
        .map((field) => {
          const displayValue = getFieldDisplayValue(
            field,
            askUserDraft[field.id],
          );
          return displayValue ? `${field.label}: ${displayValue}` : "";
        })
        .filter(Boolean);
      queueAskUserResponse(
        prompt,
        buildAskUserResponse(prompt, {
          answerText: answerParts.join(", ") || "입력 완료",
          fieldValues: Object.fromEntries(
            prompt.fields.map((field) => [
              field.id,
              askUserDraft[field.id] ?? "",
            ]),
          ),
        }),
      );
    };

    return (
      <div className="mx-3 mt-3 rounded-2xl border border-seexr-100 bg-seexr-50/70 p-3.5 dark:border-seexr-900/40 dark:bg-seexr-950/20">
        <div className="flex items-start gap-2.5">
          <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-seexr-solid text-white">
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 15a4 4 0 0 1-4 4H7l-4 4v-4a4 4 0 0 1-4-4V7a4 4 0 0 1 4-4h10" />
              <path d="M16 3h5v5" />
              <path d="m15 9 6-6" />
            </svg>
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[11px] font-black uppercase tracking-wide text-seexr-600 dark:text-seexr-300">
              추가 정보 확인
            </div>
            <p className="mt-1 text-[14px] font-bold leading-relaxed text-gray-900 dark:text-gray-100">
              {prompt.question}
            </p>
            {prompt.reason && (
              <p className="mt-1 text-[12px] leading-relaxed text-gray-500 dark:text-gray-400">
                {prompt.reason}
              </p>
            )}
          </div>
        </div>

        {(prompt.mode === "single_select" || prompt.mode === "confirm") &&
          promptOptions.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {promptOptions.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() =>
                    queueAskUserResponse(
                      prompt,
                      buildAskUserResponse(prompt, {
                        answerText: option.label,
                        selections: [option],
                      }),
                    )
                  }
                  className="rounded-full border border-seexr-200 bg-white px-3 py-2 text-left text-[13px] font-bold text-gray-800 shadow-sm transition hover:border-seexr-400 hover:bg-seexr-50 dark:border-seexr-800/60 dark:bg-[#1f2430] dark:text-gray-100 dark:hover:border-seexr-500"
                  title={option.description}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}

        {prompt.mode === "multi_select" && promptOptions.length > 0 && (
          <div className="mt-3 flex flex-col gap-3">
            <div className="flex flex-wrap gap-2">
              {promptOptions.map((option) => {
                const selected = selectedIds.includes(option.id);
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => toggleOption(option)}
                    className={`rounded-full border px-3 py-2 text-left text-[13px] font-bold shadow-sm transition ${
                      selected
                        ? "border-seexr-primary bg-seexr-solid text-white"
                        : "border-seexr-200 bg-white text-gray-800 hover:border-seexr-400 hover:bg-seexr-50 dark:border-seexr-800/60 dark:bg-[#1f2430] dark:text-gray-100"
                    }`}
                    title={option.description}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              onClick={submitSelectedOptions}
              disabled={selectedOptions.length === 0}
              className="self-end rounded-full bg-gray-900 px-4 py-2 text-[13px] font-bold text-white transition enabled:hover:bg-black disabled:cursor-not-allowed disabled:opacity-40 dark:bg-white dark:text-gray-900 dark:enabled:hover:bg-gray-200"
            >
              {prompt.submitLabel}
            </button>
          </div>
        )}

        {prompt.mode === "form" && prompt.fields.length > 0 && (
          <div className="mt-3 flex flex-col gap-2.5">
            {prompt.fields.map((field) => {
              const value = askUserDraft[field.id];
              if (field.type === "select") {
                return (
                  <label
                    key={field.id}
                    className="flex flex-col gap-1.5 text-[12px] font-bold text-gray-600 dark:text-gray-300"
                  >
                    {field.label}
                    {field.required ? " *" : ""}
                    <select
                      value={typeof value === "string" ? value : ""}
                      onChange={(e) =>
                        updateAskUserDraft(field.id, e.target.value)
                      }
                      className="h-10 rounded-xl border border-seexr-100 bg-white px-3 text-[14px] font-semibold text-gray-900 outline-none focus:border-seexr-400 dark:border-seexr-900/50 dark:bg-[#1f2430] dark:text-gray-100"
                    >
                      <option value="">선택</option>
                      {field.options.map((option) => (
                        <option key={option.id} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                );
              }
              if (field.type === "multi_select") {
                const values = Array.isArray(value) ? value : [];
                return (
                  <div key={field.id} className="flex flex-col gap-1.5">
                    <div className="text-[12px] font-bold text-gray-600 dark:text-gray-300">
                      {field.label}
                      {field.required ? " *" : ""}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {field.options.map((option) => {
                        const selected = values.includes(option.value);
                        const nextValues = selected
                          ? values.filter((v) => v !== option.value)
                          : [...values, option.value];
                        return (
                          <button
                            key={option.id}
                            type="button"
                            onClick={() =>
                              updateAskUserDraft(field.id, nextValues)
                            }
                            className={`rounded-full border px-3 py-1.5 text-[12px] font-bold transition ${
                              selected
                                ? "border-seexr-primary bg-seexr-solid text-white"
                                : "border-seexr-200 bg-white text-gray-800 hover:border-seexr-400 dark:border-seexr-800/60 dark:bg-[#1f2430] dark:text-gray-100"
                            }`}
                          >
                            {option.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              }
              if (field.type === "boolean") {
                return (
                  <div
                    key={field.id}
                    className="flex items-center justify-between gap-3 rounded-xl border border-seexr-100 bg-white px-3 py-2 dark:border-seexr-900/50 dark:bg-[#1f2430]"
                  >
                    <span className="text-[13px] font-bold text-gray-700 dark:text-gray-200">
                      {field.label}
                      {field.required ? " *" : ""}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        updateAskUserDraft(field.id, !(value === true))
                      }
                      className={`h-6 w-11 rounded-full p-0.5 transition ${value === true ? "bg-seexr-solid" : "bg-gray-300 dark:bg-gray-700"}`}
                    >
                      <span
                        className={`block h-5 w-5 rounded-full bg-white transition ${value === true ? "translate-x-5" : "translate-x-0"}`}
                      />
                    </button>
                  </div>
                );
              }
              return (
                <label
                  key={field.id}
                  className="flex flex-col gap-1.5 text-[12px] font-bold text-gray-600 dark:text-gray-300"
                >
                  {field.label}
                  {field.required ? " *" : ""}
                  {field.type === "textarea" ? (
                    <textarea
                      value={typeof value === "string" ? value : ""}
                      onChange={(e) =>
                        updateAskUserDraft(field.id, e.target.value)
                      }
                      placeholder={field.placeholder}
                      rows={2}
                      className="min-h-[72px] resize-none rounded-xl border border-seexr-100 bg-white px-3 py-2 text-[14px] font-semibold text-gray-900 outline-none focus:border-seexr-400 dark:border-seexr-900/50 dark:bg-[#1f2430] dark:text-gray-100"
                    />
                  ) : (
                    <input
                      type={field.type === "number" ? "number" : "text"}
                      value={typeof value === "string" ? value : ""}
                      onChange={(e) =>
                        updateAskUserDraft(field.id, e.target.value)
                      }
                      placeholder={field.placeholder}
                      className="h-10 rounded-xl border border-seexr-100 bg-white px-3 text-[14px] font-semibold text-gray-900 outline-none focus:border-seexr-400 dark:border-seexr-900/50 dark:bg-[#1f2430] dark:text-gray-100"
                    />
                  )}
                </label>
              );
            })}
            <button
              type="button"
              onClick={submitFieldValues}
              disabled={!requiredFieldsReady}
              className="self-end rounded-full bg-gray-900 px-4 py-2 text-[13px] font-bold text-white transition enabled:hover:bg-black disabled:cursor-not-allowed disabled:opacity-40 dark:bg-white dark:text-gray-900 dark:enabled:hover:bg-gray-200"
            >
              {prompt.submitLabel}
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderComposer = () => (
    <div className="w-full flex flex-col max-sm:gap-5 items-center gap-6">
      {/*
        AgentPlanPanel 렌더링 비활성화:
        원래는 새 채팅 요청의 작업 계획과 단계별 진행 상태를 입력창 위에 표시했다.

        {showComposerPlan && composerPlanSteps.length > 0 && (
          <AgentPlanPanel steps={composerPlanSteps} />
        )}
      */}
      <form
        data-composer
        data-seexr-notice-composer
        onSubmit={handleSubmit}
        className={`relative flex flex-col w-full z-1 max-w-[720px] backdrop-blur-3xl transition-all duration-300 overflow-visible ${
          shouldShowAskUserPanel
            ? `rounded-[32px] bg-white/70 dark:bg-[#161618]/70 shadow-[0_4px_24px_rgba(0,0,0,0.04)] focus-within:shadow-[0_8px_40px_rgba(0,0,0,0.12)] dark:focus-within:shadow-[0_0_15px_rgb(var(--seexr-rgb-500)/0.15)] ${composerAgentId ? "border-2 border-seexr-primary/50 dark:border-seexr-primary/35" : "border border-gray-200 dark:border-[#2a2a2e]"}`
            : `rounded-[32px] bg-white/70 dark:bg-[#161618]/70 shadow-[0_4px_24px_rgba(0,0,0,0.04)] focus-within:shadow-[0_8px_40px_rgba(0,0,0,0.12)] dark:focus-within:shadow-[0_0_15px_rgb(var(--seexr-rgb-500)/0.15)] ${composerAgentId ? "border-2 border-seexr-primary/50 dark:border-seexr-primary/35" : "border border-gray-200 dark:border-[#2a2a2e]"}`
        }`}
      >
        {/* Chips row: agent + project. Project chip only shows when a project is actually selected. */}
        {(composerAgentId || selectedProject) && (
          <div className="flex items-center gap-2 px-4 pt-3 pb-0.5 flex-wrap">
            {composerAgentId && (
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-seexr-solid/10 border border-seexr-primary/20">
                <div className="w-2 h-2 rounded-full bg-seexr-solid" />
                <span className="text-[12px] font-bold text-seexr-primary capitalize">
                  {AGENT_LABEL_KEYS[composerAgentId]
                    ? t(AGENT_LABEL_KEYS[composerAgentId])
                    : composerAgentId.replace(/-/g, " ")}
                </span>
                {composerAgentHubItem?.beta && (
                  <span className="rounded-full border border-seexr-200/60 bg-seexr-50/50 px-1.5 py-0.5 text-[9px] font-black uppercase leading-none tracking-wide text-seexr-primary backdrop-blur-md dark:border-seexr-400/20 dark:bg-seexr-400/10 dark:text-seexr-primary">
                    {t("sidebar.beta")}
                  </span>
                )}
                {(isSupervisorConversation || messages.length === 0) && (
                  <button
                    type="button"
                    onClick={() => {
                      if (isSupervisorConversation) {
                        void changePreferredAgent(null);
                      } else {
                        router.push("/");
                      }
                    }}
                    disabled={
                      isSupervisorConversation && isPreferredAgentSaving
                    }
                    className="ml-0.5 w-4 h-4 rounded-full cursor-pointer hover:bg-seexr-solid/20 flex items-center justify-center transition-colors"
                    aria-label={
                      isSupervisorConversation
                        ? "선택한 에이전트 제거"
                        : "에이전트 페이지 닫기"
                    }
                  >
                    <svg
                      width="10"
                      height="10"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      className="text-seexr-primary/70"
                    >
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                )}
              </div>
            )}

            {selectedProject && (
              <div className="group inline-flex items-center gap-1.5 px-2 py-1 rounded-full hover:bg-gray-100 dark:hover:bg-[#2a2a2e]/70 transition-colors">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="text-gray-500 dark:text-gray-400 shrink-0"
                >
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                </svg>
                <span className="text-[12px] font-semibold text-gray-600 dark:text-gray-400 truncate max-w-[160px]">
                  {selectedProject.name}
                </span>
                {!activeSessionId && messages.length === 0 && (
                  <button
                    type="button"
                    onClick={() => setSelectedProjectId(null)}
                    className="ml-0.5 w-4 h-4 rounded-full cursor-pointer hover:bg-gray-200 dark:hover:bg-[#3a3a3e] flex items-center justify-center transition-colors"
                    aria-label="Clear project"
                  >
                    <svg
                      width="10"
                      height="10"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      className="text-gray-400 dark:text-gray-500"
                    >
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {shouldShowAskUserPanel && latestAskUserPrompt && (
          <div
            className={
              latestAskUserPrompt.mode === "cost_material_replacement"
                ? "fade-in slide-up"
                : "fade-in"
            }
          >
            {renderAskUserInteractivePanel(latestAskUserPrompt)}
          </div>
        )}

        {/* Thumbnails preview area — 전송 전에는 blob 미리보기, 전송 시점 업로드 중에는 스피너, 이후 fileId 발급 */}
        {!shouldShowAskUserPanel &&
          (pending.length > 0 || selectedArtifacts.length > 0) && (
            <div className="flex gap-3 px-5 pt-4 pb-2 overflow-x-auto custom-scrollbar border-b border-gray-100 dark:border-[#2a2a2e]/50">
              {selectedArtifacts.map((art, i) => (
                <div
                  key={art.id}
                  className="relative group shrink-0 w-[52px] h-[52px] rounded-xl overflow-hidden shadow-sm border border-gray-200 dark:border-[#2a2a2e] flex flex-col items-center justify-center bg-gray-50 dark:bg-[#2a2a2e]"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    className="text-seexr-primary mb-1"
                  >
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                  <div className="w-full text-[8px] font-bold px-1 text-center truncate text-gray-700 dark:text-gray-300">
                    {art.title}
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedArtifacts((prev) =>
                        prev.filter((_, idx) => idx !== i),
                      )
                    }
                    className="absolute top-1 right-1 w-5 h-5 rounded-full cursor-pointer bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-sm"
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                    >
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
              ))}
              {pending.map((p, i) =>
                p.kind === "site_photo" ? (
                  <div
                    key={p.localId}
                    className="relative group shrink-0 w-[52px] h-[52px] rounded-xl overflow-hidden shadow-sm border border-gray-200 dark:border-[#2a2a2e]"
                  >
                    {p.status === "ready" && p.fileId ? (
                      <AuthImage
                        fileId={p.fileId}
                        className="w-full h-full object-cover"
                      />
                    ) : p.previewUrl ? (
                      <img
                        src={p.previewUrl}
                        className="w-full h-full object-cover"
                        alt="attachment"
                      />
                    ) : null}
                    {(p.status === "uploading" || p.status === "parsing") && (
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      </div>
                    )}
                    {p.status === "failed" && (
                      <button
                        type="button"
                        onClick={() => retryAttachment(p.localId)}
                        className="absolute inset-0 bg-red-500/75 flex items-center justify-center text-white text-[10px] font-bold"
                        title={p.error}
                      >
                        재시도
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => removeAttachment(i)}
                      className="absolute top-1 right-1 w-5 h-5 rounded-full cursor-pointer bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-sm"
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                      >
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <div
                    key={p.localId}
                    className="group flex w-[240px] shrink-0 items-center gap-2.5 rounded-xl border border-gray-200 bg-white px-3 py-2 shadow-sm dark:border-[#2a2a2e] dark:bg-[#202022]"
                  >
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-seexr-solid/10 text-seexr-primary">
                      {p.status === "uploading" || p.status === "parsing" ? (
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                      ) : (
                        <svg
                          width="17"
                          height="17"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                        </svg>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[11px] font-bold text-gray-800 dark:text-gray-200">
                        {p.name}
                      </p>
                      <p
                        className={`mt-0.5 text-[9px] font-semibold ${p.status === "failed" ? "text-red-500" : p.status === "ready" ? "text-emerald-600 dark:text-emerald-400" : "text-gray-400"}`}
                        title={p.error}
                      >
                        {p.status === "failed"
                          ? p.error || "문서 처리에 실패했습니다."
                          : [
                              p.format?.toUpperCase() ||
                                fileExtension(p.name).toUpperCase(),
                              formatFileSize(p.file.size),
                              attachmentStatusLabel(p),
                            ]
                              .filter(Boolean)
                              .join(" · ")}
                      </p>
                    </div>
                    {p.status === "failed" && (
                      <button
                        type="button"
                        onClick={() => retryAttachment(p.localId)}
                        disabled={isUploadingAttachments}
                        className="text-[9px] font-bold text-seexr-primary disabled:opacity-50"
                      >
                        재시도
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => removeAttachment(i)}
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-gray-400 opacity-70 hover:bg-gray-100 hover:text-red-500 group-hover:opacity-100 dark:hover:bg-[#2a2a2e]"
                      aria-label="문서 제거"
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                      >
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </div>
                ),
              )}
            </div>
          )}

        <div
          className={`${shouldShowAskUserPanel ? "hidden" : "grid"} grid-cols-[auto_1fr_auto] gap-x-1.5 transition-all duration-200 pl-3 pr-2.5 py-2.5 w-full items-end`}
          style={{
            gridTemplateAreas: isExpanded
              ? "'textarea textarea textarea' 'left . right'"
              : "'left textarea right'",
            rowGap: isExpanded ? "0.375rem" : undefined,
          }}
        >
          {/* Left: Attachment Button + Popup Menu */}
          <div
            style={{ gridArea: "left" }}
            className={`flex shrink-0 relative ${isExpanded ? "mb-0.5" : "mb-0.5"}`}
            ref={attachMenuRef}
          >
            <button
              type="button"
              onClick={() => {
                if (isAttachMenuOpen) {
                  closeAttachMenu();
                } else {
                  setIsAttachMenuOpen(true);
                  fetchRecentArtifacts();
                }
              }}
              className="flex items-center justify-center w-[36px] h-[36px] rounded-full cursor-pointer bg-gray-100 hover:bg-gray-200 dark:bg-[#202022] dark:hover:bg-[#2a2a2e] text-gray-500 transition-colors"
            >
              <svg width="20" height="20" viewBox="0 0 48 48" fill="none">
                <path
                  d="M22.5 25.5V40H25.5V25.5H40V22.5H25.5V8H22.5V22.5H8V25.5H22.5Z"
                  fill="currentColor"
                />
              </svg>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={
                SOURCE_DOCUMENT_AGENT_IDS.has(agentId)
                  ? `image/png,image/jpeg,image/webp,image/gif,${SOURCE_DOCUMENT_ACCEPT}`
                  : "image/png,image/jpeg,image/webp,image/gif"
              }
              className="hidden"
              onChange={handleFileChange}
            />

            {/* Level 1 Popup */}
            {isAttachMenuOpen && (
              <div className="absolute bottom-[calc(100%+8px)] left-0 w-[170px] md:w-[200px] bg-white dark:bg-[#202022] rounded-2xl shadow-xl border border-gray-100 dark:border-[#2a2a2e] py-1.5 z-[100] animate-in fade-in zoom-in-95 duration-200">
                {/* 사진 및 파일 추가 */}
                <div
                  onClick={() => {
                    closeAttachMenu();
                    setTimeout(() => {
                      if (fileInputRef.current) {
                        fileInputRef.current.value = "";
                        fileInputRef.current.click();
                      }
                    }, 50);
                  }}
                  className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-[#2a2a2e] cursor-pointer"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    className="text-gray-500 shrink-0"
                  >
                    <path
                      d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span className="text-[14px] font-bold text-gray-800 dark:text-gray-200">
                    {SOURCE_DOCUMENT_AGENT_IDS.has(agentId)
                      ? "사진 또는 원본 문서 추가"
                      : "사진 추가"}
                  </span>
                </div>

                {/* 최근 아티팩트 */}
                <div
                  ref={artifactTriggerRef}
                  onMouseEnter={(e) => {
                    clearTimeout(submenuCloseTimeoutRef.current);
                    openArtifactSubmenu(e.currentTarget);
                  }}
                  onMouseLeave={() =>
                    handleSubmenuTriggerLeave(() =>
                      setIsArtifactSubmenuOpen(false),
                    )
                  }
                >
                  <div
                    onClick={(e) => {
                      if (isArtifactSubmenuOpen) {
                        setIsArtifactSubmenuOpen(false);
                      } else {
                        openArtifactSubmenu(e.currentTarget.parentElement!);
                      }
                    }}
                    className="flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-[#2a2a2e] cursor-pointer"
                  >
                    <div className="flex items-center gap-3">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        className="text-gray-500 shrink-0"
                      >
                        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                      </svg>
                      <span className="text-[14px] font-bold text-gray-800 dark:text-gray-200">
                        최근 아티팩트
                      </span>
                    </div>
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      className="text-gray-400 shrink-0"
                    >
                      <path
                        d="M9 18l6-6-6-6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </div>

                {/* Supervisor 대화에서는 매 요청 전에 선호 에이전트를 바꿀 수 있다. */}
                {(messages.length === 0 || isSupervisorConversation) && (
                  <>
                    <hr className="my-1 border-gray-100 dark:border-[#2a2a2e]" />
                    <div
                      ref={agentTriggerRef}
                      onMouseEnter={(e) => {
                        clearTimeout(submenuCloseTimeoutRef.current);
                        openAgentSubmenu(e.currentTarget);
                      }}
                      onMouseLeave={() =>
                        handleSubmenuTriggerLeave(() =>
                          setIsAgentSubmenuOpen(false),
                        )
                      }
                    >
                      <div
                        onClick={(e) => {
                          if (isAgentSubmenuOpen) {
                            setIsAgentSubmenuOpen(false);
                          } else {
                            openAgentSubmenu(e.currentTarget.parentElement!);
                          }
                        }}
                        className="flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-[#2a2a2e] cursor-pointer"
                      >
                        <div className="flex items-center gap-3">
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            className="text-gray-500 shrink-0"
                          >
                            <path d="M12 8V4H8" />
                            <rect width="16" height="12" x="4" y="8" rx="2" />
                            <path d="M2 14h2" />
                            <path d="M20 14h2" />
                            <path d="M15 13v2" />
                            <path d="M9 13v2" />
                          </svg>
                          <span className="text-[14px] font-bold text-gray-800 dark:text-gray-200">
                            에이전트
                          </span>
                        </div>
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          className="text-gray-400 shrink-0"
                        >
                          <path
                            d="M9 18l6-6-6-6"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    </div>

                    {messages.length === 0 && (
                      /* 프로젝트 선택 (대화 시작 전에만) */
                      <div
                        ref={projectTriggerRef}
                        onMouseEnter={(e) => {
                          clearTimeout(submenuCloseTimeoutRef.current);
                          openProjectSubmenu(e.currentTarget);
                        }}
                        onMouseLeave={() =>
                          handleSubmenuTriggerLeave(() =>
                            setIsProjectSubmenuOpen(false),
                          )
                        }
                      >
                        <div
                          onClick={(e) => {
                            if (isProjectSubmenuOpen) {
                              setIsProjectSubmenuOpen(false);
                            } else {
                              openProjectSubmenu(
                                e.currentTarget.parentElement!,
                              );
                            }
                          }}
                          className="flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-[#2a2a2e] cursor-pointer"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              className="text-gray-500 shrink-0"
                            >
                              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                            </svg>
                            <span className="text-[14px] font-bold text-gray-800 dark:text-gray-200 truncate">
                              {t("nav.projects") || "프로젝트"}
                            </span>
                          </div>
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            className="text-gray-400 shrink-0"
                          >
                            <path
                              d="M9 18l6-6-6-6"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Center: Textarea */}
          <div
            style={{ gridArea: "textarea" }}
            className={`min-w-0 flex items-center pr-1 ${isExpanded ? "w-full px-1" : "h-full py-1.5"}`}
          >
            <textarea
              ref={setTextareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              onPaste={(e) => {
                // 클립보드에 파일(스크린샷 등)이 포함돼 있으면 즉시 업로드 플로우로 태움.
                // 순수 텍스트 붙여넣기는 기본 동작을 그대로 둠.
                const items = e.clipboardData?.items;
                if (!items || items.length === 0) return;
                const pastedFiles: File[] = [];
                for (let i = 0; i < items.length; i++) {
                  const it = items[i];
                  if (it.kind === "file") {
                    const f = it.getAsFile();
                    if (f) pastedFiles.push(f);
                  }
                }
                if (pastedFiles.length > 0) {
                  e.preventDefault();
                  addFiles(pastedFiles);
                }
              }}
              className={`w-full max-h-[72px] min-h-[24px] resize-none bg-transparent text-[16px] md:text-[15px] leading-[24px] font-medium outline-none text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 custom-scrollbar ${isExpanded ? "mb-1 mt-0.5" : "mt-0.5"}`}
              placeholder={
                shouldShowAskUserPanel
                  ? "직접 입력해서 답변하기"
                  : messages.length === 0 && !isSpecializedAgent
                    ? `예: ${getSafetyPromptExample(safetyHeadline)}`
                    : messages.length === 0 &&
                        getServicePromptPlaceholder(agentId)
                      ? `예: ${getServicePromptPlaceholder(agentId)}`
                      : t("chat.ask_anything")
              }
              rows={1}
              autoFocus={messages.length === 0}
            />
          </div>

          {/* Right: Model Selector & Submit */}
          <div
            style={{ gridArea: "right" }}
            className={`flex shrink-0 items-center gap-1.5 ${isExpanded ? "mb-0.5" : "mb-0.5"}`}
          >
            <div className="relative" ref={dropdownRef}>
              <div
                onClick={() => setIsModeDropdownOpen(!isModeDropdownOpen)}
                className="flex items-center h-[36px] px-3.5 rounded-full bg-transparent hover:bg-black/5 dark:hover:bg-white/5 cursor-pointer transition-colors shrink-0 select-none"
              >
                <p className="text-[14px] font-bold text-gray-600 dark:text-gray-400 mr-1.5 whitespace-nowrap">
                  {modelMode === "fast" ? "빠른 답변" : "전문가 모드"}
                </p>
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  className="text-gray-500 dark:text-gray-400"
                >
                  <path
                    d="M6 9l6 6 6-6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>

              {/* Popup Dropdown */}
              {isModeDropdownOpen && (
                <div className="absolute bottom-[calc(100%+8px)] right-0 w-[160px] bg-white dark:bg-[#202022] rounded-2xl shadow-xl border border-gray-100 dark:border-[#2a2a2e] py-1.5 z-[100] animate-in fade-in zoom-in-95 duration-200"
                >
                  <ReasoningStyleSelector />
                  <div className="mx-3 border-t border-gray-100 dark:border-[#2a2a2e]" />
                  <div
                    onClick={() => {
                      setModelMode("fast");
                      setIsModeDropdownOpen(false);
                    }}
                    className="flex items-center px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-[#2a2a2e] cursor-pointer"
                  >
                    <span className="text-[14px] font-bold text-gray-800 dark:text-gray-200">
                      빠른 답변
                    </span>
                    {modelMode === "fast" && (
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        className="ml-auto text-seexr-primary"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </div>
                  <div
                    onClick={() => {
                      setModelMode("pro");
                      setIsModeDropdownOpen(false);
                    }}
                    className="flex items-center px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-[#2a2a2e] cursor-pointer"
                  >
                    <span className="text-[14px] font-bold text-gray-800 dark:text-gray-200">
                      전문가 모드
                    </span>
                    {modelMode === "pro" && (
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        className="ml-auto text-seexr-primary"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </div>
                </div>
              )}
            </div>

            {(() => {
              const hasContent =
                input.trim().length > 0 ||
                pending.length > 0 ||
                selectedArtifacts.length > 0;
              const canSubmit =
                hasContent &&
                !isListening &&
                !isUploadingAttachments &&
                !isPreferredAgentSaving;
              return isGenerating ? (
                <button
                  type="button"
                  onClick={handleStop}
                  className="w-[36px] h-[36px] flex items-center justify-center rounded-full cursor-pointer bg-gray-900 dark:bg-white text-white dark:text-gray-900 transition-all hover:scale-105 hover:bg-black dark:hover:bg-gray-200"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect
                      x="7"
                      y="7"
                      width="10"
                      height="10"
                      fill="currentColor"
                    />
                  </svg>
                </button>
              ) : (
                <button
                  type={canSubmit ? "submit" : "button"}
                  onClick={
                    canSubmit || isUploadingAttachments
                      ? undefined
                      : toggleListening
                  }
                  disabled={isUploadingAttachments || isPreferredAgentSaving}
                  className={`w-[36px] h-[36px] flex items-center justify-center rounded-full cursor-pointer transition-all hover:scale-105 ${
                    isListening
                      ? "bg-red-500 hover:bg-red-600 text-white shadow-md animate-pulse"
                      : `bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-black dark:hover:bg-gray-200 ${!hasContent && !isListening ? "opacity-50 hover:opacity-100" : ""}`
                  }`}
                >
                  {isUploadingAttachments || isPreferredAgentSaving ? (
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  ) : canSubmit ? (
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="12" y1="19" x2="12" y2="5" />
                      <polyline points="5 12 12 5 19 12" />
                    </svg>
                  ) : isListening ? (
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect
                        x="6"
                        y="6"
                        width="12"
                        height="12"
                        fill="currentColor"
                      />
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 48 48" fill="none">
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M24 4C20.134 4 17 7.13401 17 11V25C17 28.866 20.134 32 24 32C27.866 32 31 28.866 31 25V11C31 7.13401 27.866 4 24 4ZM20 11C20 8.79086 21.7909 7 24 7C26.2091 7 28 8.79086 28 11V25C28 27.2091 26.2091 29 24 29C21.7909 29 20 27.2091 20 25V11ZM12 22.5V25C12 31.6274 17.3726 37 24 37C30.6274 37 36 31.6274 36 25V22.5H39V25C39 32.9058 32.7613 39.4015 25.5 39.9507V43H31V46H17V43H22.5V39.9507C15.2387 39.4015 9 32.9058 9 25V22.5H12Z"
                        fill="currentColor"
                      />
                    </svg>
                  )}
                </button>
              );
            })()}
          </div>
        </div>
      </form>
    </div>
  );

  const renderPortalSubmenus = () =>
    typeof document === "undefined" ? null : (
      <>
        {/* Portal: Artifact Submenu */}
        {isArtifactSubmenuOpen &&
          createPortal(
            <div
              ref={artifactSubmenuRef}
              onMouseEnter={handleSubmenuPortalEnter}
              onMouseLeave={() =>
                handleSubmenuPortalLeave(() => setIsArtifactSubmenuOpen(false))
              }
              className="w-[180px] md:w-[220px] bg-white dark:bg-[#202022] rounded-2xl shadow-xl border border-gray-100 dark:border-[#2a2a2e] py-1.5 animate-in fade-in zoom-in-95 duration-200"
              style={{
                position: "fixed",
                top: artifactSubmenuPos.top,
                left: artifactSubmenuPos.left,
                zIndex: 9999,
              }}
            >
              <div
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsArtifactSelectorOpen(true);
                  setIsArtifactSubmenuOpen(false);
                  closeAttachMenu();
                }}
                className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-[#2a2a2e] cursor-pointer"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  className="text-gray-500 shrink-0"
                >
                  <path
                    d="M5 12h14M12 5l7 7-7 7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="text-[14px] font-bold text-gray-800 dark:text-gray-200">
                  아티팩트에서 추가
                </span>
              </div>
              <hr className="my-1 border-gray-100 dark:border-[#2a2a2e]" />
              {recentArtifacts === null ? (
                <div className="flex items-center justify-center py-3">
                  <div className="w-4 h-4 border-2 border-seexr-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : recentArtifacts.length === 0 ? (
                <div className="px-4 py-2.5 text-[13px] text-gray-400 font-medium">
                  아티팩트 없음
                </div>
              ) : (
                recentArtifacts.map((artifact) => (
                  <div
                    key={artifact.id}
                    onClick={() => {
                      if (!addArtifactSelection(artifact)) return;
                      setIsArtifactSubmenuOpen(false);
                      closeAttachMenu();
                    }}
                    className="flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 dark:hover:bg-[#2a2a2e] cursor-pointer"
                  >
                    <RecentArtifactThumbnail item={artifact} />
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="text-[13px] font-bold text-gray-800 dark:text-gray-200 truncate">
                        {artifact.title}
                      </span>
                      <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider truncate">
                        {(artifact.kind === "artifact"
                          ? artifact.type
                          : artifact.mimeType.split("/")[1] || "file"
                        ).replace("_", " ")}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>,
            document.body,
          )}
        {/* Portal: Agent Submenu */}
        {isAgentSubmenuOpen &&
          createPortal(
            <div
              ref={agentSubmenuRef}
              onMouseEnter={handleSubmenuPortalEnter}
              onMouseLeave={() =>
                handleSubmenuPortalLeave(() => setIsAgentSubmenuOpen(false))
              }
              className="w-[180px] md:w-[220px] bg-white dark:bg-[#202022] rounded-2xl shadow-xl border border-gray-100 dark:border-[#2a2a2e] py-1.5 animate-in fade-in zoom-in-95 duration-200"
              style={{
                position: "fixed",
                top: agentSubmenuPos.top,
                left: agentSubmenuPos.left,
                zIndex: 9999,
              }}
            >
              {AGENT_HUB_ITEMS.map((agent) => {
                const isSelected = isSupervisorConversation
                  ? preferredAgentId === agent.id
                  : agentId === agent.id;
                const content = (
                  <>
                    <span
                      className={`min-w-0 truncate text-[13px] font-bold ${isSelected ? "text-seexr-primary" : "text-gray-600 dark:text-gray-300"}`}
                    >
                      {t(agent.labelKey)}
                    </span>
                    {agent.beta && (
                      <span className="shrink-0 rounded-full border border-seexr-200/60 bg-seexr-50/50 px-1.5 py-0.5 text-[9px] font-black uppercase leading-none tracking-wide text-seexr-primary backdrop-blur-md dark:border-seexr-400/20 dark:bg-seexr-400/10 dark:text-seexr-primary">
                        {t("sidebar.beta")}
                      </span>
                    )}
                    {isSelected && (
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        className="text-seexr-primary ml-auto shrink-0"
                      >
                        <path
                          d="M20 6L9 17l-5-5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </>
                );

                return isSupervisorConversation ? (
                  <button
                    key={agent.id}
                    type="button"
                    onClick={() => void changePreferredAgent(agent.id)}
                    disabled={isPreferredAgentSaving}
                    className={`flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-50 disabled:cursor-wait disabled:opacity-60 dark:hover:bg-[#2a2a2e] ${isSelected ? "bg-seexr-solid/5" : ""}`}
                  >
                    {content}
                  </button>
                ) : (
                  <Link
                    key={agent.id}
                    href={agent.href}
                    onClick={closeAttachMenu}
                    className={`flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-[#2a2a2e] cursor-pointer ${isSelected ? "bg-seexr-solid/5" : ""}`}
                  >
                    {content}
                  </Link>
                );
              })}
            </div>,
            document.body,
          )}
        {/* Portal: Project Submenu */}
        {isProjectSubmenuOpen &&
          createPortal(
            <div
              ref={projectSubmenuRef}
              onMouseEnter={handleSubmenuPortalEnter}
              onMouseLeave={() =>
                handleSubmenuPortalLeave(() => setIsProjectSubmenuOpen(false))
              }
              className="w-[180px] md:w-[240px] max-h-[300px] overflow-y-auto bg-white dark:bg-[#202022] rounded-2xl shadow-xl border border-gray-100 dark:border-[#2a2a2e] py-1.5 animate-in fade-in zoom-in-95 duration-200"
              style={{
                position: "fixed",
                top: projectSubmenuPos.top,
                left: projectSubmenuPos.left,
                zIndex: 9999,
              }}
            >
              <button
                type="button"
                onClick={() => {
                  setSelectedProjectId(null);
                  closeAttachMenu();
                }}
                className={`flex items-center gap-3 w-full px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-[#2a2a2e] cursor-pointer text-left ${!selectedProjectId ? "bg-seexr-solid/5" : ""}`}
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  className={
                    !selectedProjectId ? "text-seexr-primary" : "text-gray-400"
                  }
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
                <span
                  className={`text-[13px] font-bold ${!selectedProjectId ? "text-seexr-primary" : "text-gray-600 dark:text-gray-300"}`}
                >
                  {t("chat.project_selector_none") || "프로젝트 없음"}
                </span>
                {!selectedProjectId && (
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    className="text-seexr-primary ml-auto shrink-0"
                  >
                    <path
                      d="M20 6L9 17l-5-5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>
              {availableProjects.length === 0 ? (
                <div className="px-4 py-3 text-[12px] text-gray-400 font-medium">
                  {t("chat.project_modal_no_projects") ||
                    "프로젝트가 없습니다. Projects 페이지에서 먼저 만들어주세요."}
                </div>
              ) : (
                availableProjects.map((p) => {
                  const isSelected = selectedProjectId === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => {
                        setSelectedProjectId(p.id);
                        closeAttachMenu();
                      }}
                      className={`flex items-center gap-3 w-full px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-[#2a2a2e] cursor-pointer text-left ${isSelected ? "bg-seexr-solid/5" : ""}`}
                    >
                      <ProjectAvatar
                        projectId={p.id}
                        avatarFileId={p.avatar_file_id}
                        name={p.name}
                        className="w-5 h-5 rounded-md"
                        fallbackClassName={`text-[10px] ${isSelected ? "bg-seexr-solid/15 text-seexr-primary" : "bg-gray-100 dark:bg-[#1f1f22] text-gray-500"}`}
                      />
                      <span
                        className={`text-[13px] font-bold truncate ${isSelected ? "text-seexr-primary" : "text-gray-700 dark:text-gray-200"}`}
                      >
                        {p.name}
                      </span>
                      {isSelected && (
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3"
                          className="text-seexr-primary ml-auto shrink-0"
                        >
                          <path
                            d="M20 6L9 17l-5-5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </button>
                  );
                })
              )}
            </div>,
            document.body,
          )}
      </>
    );

  const CLASSIC_CONFIGS: Record<string, ClassicFormConfig> = {
    "accident-search": {
      title: t("sidebar.accident_search"),
      fields: [
        {
          key: "workTypeMain",
          label: "공사 종류",
          placeholder: "예: 건축, 토목",
          type: "select",
          options: ["건축", "토목", "전기", "기계", "화학", "기타"],
        },
        {
          key: "accidentCause",
          label: "사고 원인",
          placeholder: "예: 추락, 끼임, 충돌",
          type: "select",
          options: [
            "추락",
            "끼임",
            "충돌",
            "감전",
            "화재",
            "붕괴",
            "질식",
            "폭발",
            "기타",
          ],
        },
        { key: "region", label: "지역", placeholder: "예: 서울, 인천" },
        {
          key: "accidentName",
          label: "사고명 키워드",
          placeholder: "예: 비계 붕괴",
        },
        {
          key: "publicPrivateType",
          label: "공공/민간",
          type: "select",
          placeholder: "선택",
          options: ["공공", "민간"],
        },
      ],
    },
    "legal-search": {
      title: t("sidebar.legal_search"),
      fields: [
        {
          key: "keyword",
          label: "검색 키워드",
          placeholder: "예: 추락 방지, 안전난간",
        },
        {
          key: "category",
          label: "카테고리",
          type: "select",
          placeholder: "선택",
          options: [
            "위험성평가",
            "안전관리계획서",
            "중대재해",
            "건설산업",
            "시설물안전",
            "건축안전",
            "소방안전",
          ],
        },
        {
          key: "lawType",
          label: "법령 구분",
          type: "select",
          placeholder: "선택",
          options: ["법률", "대통령령", "고용노동부령", "국토교통부령"],
        },
        { key: "actName", label: "법령명", placeholder: "예: 산업안전보건법" },
      ],
    },
    "conceptual-estimate": {
      title: t("sidebar.conceptual_estimate"),
      fields: [
        {
          key: "projectType",
          label: "공사 종류",
          placeholder: "예: 근린생활시설 신축, 사무실 리모델링",
        },
        {
          key: "scope",
          label: "공사 범위",
          placeholder: "예: 골조, 마감, 전기/기계설비 포함",
        },
        {
          key: "area",
          label: "연면적/규모",
          placeholder: "예: 1,200m2, 지상 4층",
        },
        { key: "location", label: "지역", placeholder: "예: 서울 강남구" },
        {
          key: "quality",
          label: "마감 수준",
          type: "select",
          placeholder: "선택",
          options: ["기본", "표준", "고급", "프리미엄"],
        },
        {
          key: "description",
          label: "추가 조건",
          placeholder: "공기, 제외 항목, 기준년도, 특이사항을 입력하세요",
        },
      ],
    },
  };

  const classicConfig: ClassicFormConfig | undefined =
    classicAdapter ?? CLASSIC_CONFIGS[agentId];
  const classicImageAttachments = pending
    .map((attachment, index) => ({ attachment, index }))
    .filter(({ attachment }) => attachment.kind === "site_photo");
  const classicDocumentAttachments = pending
    .map((attachment, index) => ({ attachment, index }))
    .filter(({ attachment }) => attachment.kind === "source_document");

  const isClassicSubmitDisabled = () => {
    if (!classicConfig) return true;
    const requiredFields = classicConfig.fields.filter(
      (field) => field.required && field.type !== "image",
    );
    if (requiredFields.length > 0) {
      return requiredFields.some((field) => !classicFields[field.key]?.trim());
    }
    return (
      !Object.values(classicFields).some((v) => v.trim()) &&
      pending.length === 0
    );
  };

  const handleClassicSubmit = () => {
    const filledFields = Object.entries(classicFields).filter(([_, v]) =>
      v.trim(),
    );
    if (
      filledFields.length === 0 &&
      classicImageAttachments.length === 0 &&
      classicDocumentAttachments.length === 0
    )
      return;

    let prompt = "";
    if (classicAdapter) {
      const result = classicAdapter.buildSubmission({
        fields: classicFields,
        imageCount: classicImageAttachments.length,
        documentCount: classicDocumentAttachments.length,
      });
      if ("error" in result) {
        alert(result.error);
        return;
      }
      prompt = result.submission.prompt;
      pendingSiteEvidenceRef.current = result.submission.siteEvidence ?? null;
    } else if (agentId === "accident-search") {
      const parts: string[] = [];
      if (classicFields.workTypeMain)
        parts.push(`${classicFields.workTypeMain} 공사`);
      if (classicFields.accidentCause)
        parts.push(`${classicFields.accidentCause} 사고`);
      if (classicFields.region) parts.push(`${classicFields.region} 지역`);
      if (classicFields.publicPrivateType)
        parts.push(`${classicFields.publicPrivateType} 현장`);
      if (classicFields.accidentName)
        parts.push(`"${classicFields.accidentName}" 관련`);
      prompt = `${parts.join(", ")}에 해당하는 유사 사고 사례를 검색해줘.`;
    } else if (agentId === "legal-search") {
      const parts: string[] = [];
      if (classicFields.keyword) parts.push(`"${classicFields.keyword}"`);
      if (classicFields.actName) parts.push(`${classicFields.actName}`);
      if (classicFields.category)
        parts.push(`(${classicFields.category} 분야)`);
      if (classicFields.lawType) parts.push(`${classicFields.lawType} 기준`);
      prompt =
        parts.length > 0 ? `${parts.join(" ")} 관련 법령 조문을 검색해줘.` : "";
    } else if (agentId === "conceptual-estimate") {
      const parts: string[] = [];
      if (classicFields.projectType)
        parts.push(`공사 종류: ${classicFields.projectType}`);
      if (classicFields.scope) parts.push(`공사 범위: ${classicFields.scope}`);
      if (classicFields.area) parts.push(`연면적/규모: ${classicFields.area}`);
      if (classicFields.location) parts.push(`지역: ${classicFields.location}`);
      if (classicFields.quality)
        parts.push(`마감 수준: ${classicFields.quality}`);
      if (classicFields.description)
        parts.push(`추가 조건: ${classicFields.description}`);
      prompt = `다음 조건에 대해 개산견적을 작성해줘. 공종별 내역, 주요 수량 가정, 단가 산정 근거, 포함/제외 항목, 견적 범위와 불확실성을 함께 정리해줘.\n${parts.join("\n")}`;
    }

    if (prompt) {
      classicSubmissionRef.current = true;
      setInput(prompt);
      setIsClassicMode(false);
      closeClassicSuggestions();
      // Trigger submit after state update
      setTimeout(() => {
        const form = document.querySelector(
          "form[data-composer]",
        ) as HTMLFormElement;
        if (form) form.requestSubmit();
      }, 50);
    }
  };

  if (messages.length === 0) {
    return (
      <div
        data-seexr-notice-landing={!activeSessionId && !routeSessionId && !isGenerating ? "true" : undefined}
        className="flex flex-col h-full w-full px-4 md:px-8"
      >
        <div className="flex flex-col flex-1">
          {/* Headline area */}
          <div className="flex-1 md:flex-none md:mt-auto flex items-center justify-center">
            <div className="md:mb-10 w-full max-w-[720px] flex flex-col items-center">
              {!isSpecializedAgent && (
                <h1 className="text-2xl leading-9 font-semibold text-gray-900 dark:text-gray-100 text-center text-pretty">
                  {safetyHeadline}
                </h1>
              )}
            </div>
          </div>
          {/* Composer / Classic Form area */}
          <div className="shrink-0 md:flex-none md:mb-auto pb-4 md:pb-8 max-md:pt-6 flex justify-center max-md:bg-gradient-to-t max-md:from-background">
            {isClassicMode && classicConfig ? (
              <div className="w-full max-w-[720px] flex flex-col items-center gap-4">
                {/* Toggle */}
                <div className="flex items-center gap-2 bg-gray-100 dark:bg-[#1f1f22] rounded-full p-1 border border-gray-200 dark:border-[#2a2a2e]">
                  <button
                    onClick={() => {
                      setIsClassicMode(false);
                      closeClassicSuggestions();
                    }}
                    className="px-4 py-1.5 rounded-full cursor-pointer text-[13px] font-bold transition-all text-gray-500 dark:text-gray-400"
                  >
                    Chat
                  </button>
                  <button className="px-4 py-1.5 rounded-full cursor-pointer text-[13px] font-bold transition-all bg-white dark:bg-[#2a2a2e] text-gray-900 dark:text-gray-100 shadow-sm">
                    Classic
                  </button>
                </div>
                <div className="w-full bg-white dark:bg-[#161618] rounded-[24px] border border-gray-200 dark:border-[#2a2a2e] shadow-sm p-6 animate-in fade-in duration-200">
                  <h2 className="text-[16px] font-black text-gray-900 dark:text-gray-100 tracking-tight mb-5">
                    {classicConfig.title}
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {classicConfig.fields.map((field) => {
                      const fieldValue = classicFields[field.key] || "";
                      const isWideField =
                        field.fullWidth ||
                        field.type === "textarea" ||
                        field.type === "image" ||
                        field.type === "document";
                      const isProcessField =
                        field.type === "processMajor" ||
                        field.type === "processSub";
                      const selectedProcessMajor = (
                        classicFields.processMajor || ""
                      ).trim();
                      const processCategories =
                        classicConfig.processCategories ?? {};
                      const selectedProcessSubOptions =
                        processCategories[selectedProcessMajor] ?? [];
                      const processOptions =
                        field.type === "processMajor"
                          ? Object.keys(processCategories)
                          : selectedProcessSubOptions;
                      const processSuggestions = isProcessField
                        ? getClassicSuggestions(
                            processOptions,
                            fieldValue,
                            fieldValue ? 12 : 10,
                          )
                        : [];
                      const isSuggestionOpen =
                        classicSuggestionKey === field.key;
                      const tokenValues = parseClassicTokens(fieldValue);
                      const tokenDraft = classicDrafts[field.key] || "";
                      const isMaterialField = field.key === "material";
                      const tokenOptions =
                        classicConfig.multiBadgeOptions?.[field.key] ?? [];
                      const tokenSuggestions =
                        field.type === "multiBadge"
                          ? getClassicSuggestions(
                              tokenOptions.filter(
                                (option) =>
                                  !tokenValues.includes(
                                    sanitizeClassicToken(option),
                                  ),
                              ),
                              tokenDraft,
                              tokenDraft ? 12 : 10,
                            )
                          : [];
                      const processPlaceholder =
                        field.type === "processSub" &&
                        selectedProcessSubOptions.length === 0
                          ? "공정 대분류를 먼저 선택하세요"
                          : field.placeholder;
                      const badgeClass = isMaterialField
                        ? "border-emerald-200/70 bg-emerald-50 text-emerald-700 dark:border-emerald-400/20 dark:bg-emerald-400/10 dark:text-emerald-200"
                        : "border-seexr-200/70 bg-seexr-50 text-seexr-primary dark:border-seexr-400/20 dark:bg-seexr-400/10 dark:text-seexr-primary";

                      return (
                        <div
                          key={field.key}
                          className={`flex flex-col gap-1.5 ${isWideField ? "sm:col-span-2" : ""}`}
                        >
                          <label className="text-[12px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {field.label}
                            {field.required && (
                              <span className="ml-1 text-seexr-primary">*</span>
                            )}
                          </label>
                          {field.type === "select" ? (
                            <select
                              value={fieldValue}
                              onChange={(e) =>
                                updateClassicField(field.key, e.target.value)
                              }
                              className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-[#2a2a2e] bg-white dark:bg-[#1a1a1c] text-[14px] font-medium text-gray-900 dark:text-gray-100 outline-none focus:border-seexr-primary/50 focus:ring-2 focus:ring-seexr-primary/20 transition-all"
                            >
                              <option value="">{field.placeholder}</option>
                              {field.options?.map((opt) => (
                                <option key={opt} value={opt}>
                                  {opt}
                                </option>
                              ))}
                            </select>
                          ) : field.type === "textarea" ? (
                            <textarea
                              value={fieldValue}
                              onChange={(e) =>
                                updateClassicField(field.key, e.target.value)
                              }
                              placeholder={field.placeholder}
                              rows={4}
                              className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-[#2a2a2e] bg-white dark:bg-[#1a1a1c] text-[14px] leading-relaxed font-medium text-gray-900 dark:text-gray-100 outline-none focus:border-seexr-primary/50 focus:ring-2 focus:ring-seexr-primary/20 transition-all placeholder:text-gray-400 resize-none"
                            />
                          ) : field.type === "image" ? (
                            <div className="rounded-2xl border border-dashed border-gray-200 dark:border-[#2a2a2e] bg-gray-50/60 dark:bg-[#1a1a1c]/70 p-4">
                              <input
                                ref={classicImageInputRef}
                                type="file"
                                multiple
                                accept="image/*"
                                className="hidden"
                                onChange={handleClassicImageChange}
                              />
                              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="text-[13px] font-semibold text-gray-700 dark:text-gray-300">
                                    현장 사진을 첨부하세요
                                  </p>
                                  <p className="mt-0.5 text-[12px] font-medium text-gray-400">
                                    {field.placeholder}
                                  </p>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (classicImageInputRef.current) {
                                      classicImageInputRef.current.value = "";
                                      classicImageInputRef.current.click();
                                    }
                                  }}
                                  className="h-[36px] shrink-0 rounded-full border border-seexr-primary/20 bg-seexr-solid/10 px-4 text-[13px] font-bold text-seexr-primary hover:bg-seexr-solid/15 transition-colors"
                                >
                                  이미지 선택
                                </button>
                              </div>
                              {classicImageAttachments.length > 0 && (
                                <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-2">
                                  {classicImageAttachments.map(
                                    ({ attachment, index }) => (
                                      <div
                                        key={attachment.localId}
                                        className="relative group aspect-[4/3] overflow-hidden rounded-xl border border-gray-200 dark:border-[#2a2a2e] bg-gray-100 dark:bg-[#202022]"
                                      >
                                        {attachment.previewUrl ? (
                                          <img
                                            src={attachment.previewUrl}
                                            alt={attachment.name}
                                            className="h-full w-full object-cover"
                                          />
                                        ) : (
                                          <div className="flex h-full w-full items-center justify-center px-2 text-center text-[10px] font-semibold text-gray-400">
                                            {attachment.name}
                                          </div>
                                        )}
                                        {(attachment.status === "uploading" ||
                                          attachment.status === "parsing") && (
                                          <div className="absolute inset-0 flex items-center justify-center bg-black/45">
                                            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                          </div>
                                        )}
                                        {attachment.status === "failed" && (
                                          <button
                                            type="button"
                                            onClick={() =>
                                              retryAttachment(
                                                attachment.localId,
                                              )
                                            }
                                            className="absolute inset-0 flex items-center justify-center bg-red-500/75 text-[10px] font-bold text-white"
                                            title={attachment.error}
                                          >
                                            재시도
                                          </button>
                                        )}
                                        <button
                                          type="button"
                                          onClick={() =>
                                            removeAttachment(index)
                                          }
                                          className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-black/55 text-white opacity-0 transition-opacity group-hover:opacity-100"
                                          aria-label="이미지 제거"
                                        >
                                          <svg
                                            width="12"
                                            height="12"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="3"
                                            strokeLinecap="round"
                                          >
                                            <line
                                              x1="18"
                                              y1="6"
                                              x2="6"
                                              y2="18"
                                            />
                                            <line
                                              x1="6"
                                              y1="6"
                                              x2="18"
                                              y2="18"
                                            />
                                          </svg>
                                        </button>
                                      </div>
                                    ),
                                  )}
                                </div>
                              )}
                            </div>
                          ) : field.type === "document" ? (
                            <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50/60 p-4 dark:border-[#2a2a2e] dark:bg-[#1a1a1c]/70">
                              <input
                                ref={classicDocumentInputRef}
                                type="file"
                                multiple
                                accept={SOURCE_DOCUMENT_ACCEPT}
                                className="hidden"
                                onChange={handleClassicDocumentChange}
                              />
                              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                <div className="min-w-0">
                                  <p className="text-[13px] font-semibold text-gray-700 dark:text-gray-300">
                                    현장 작업 개요나 작업 허가서를 첨부하세요
                                  </p>
                                  <p className="mt-0.5 text-[12px] font-medium text-gray-400">
                                    {field.placeholder}
                                  </p>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (classicDocumentInputRef.current) {
                                      classicDocumentInputRef.current.value =
                                        "";
                                      classicDocumentInputRef.current.click();
                                    }
                                  }}
                                  className="h-[36px] shrink-0 rounded-full border border-seexr-primary/20 bg-seexr-solid/10 px-4 text-[13px] font-bold text-seexr-primary transition-colors hover:bg-seexr-solid/15"
                                >
                                  문서 선택
                                </button>
                              </div>
                              {classicDocumentAttachments.length > 0 && (
                                <div className="mt-4 flex flex-col gap-2">
                                  {classicDocumentAttachments.map(
                                    ({ attachment, index }) => (
                                      <div
                                        key={attachment.localId}
                                        className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-3 py-2.5 dark:border-[#2a2a2e] dark:bg-[#202022]"
                                      >
                                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-seexr-solid/10 text-seexr-primary">
                                          <svg
                                            width="18"
                                            height="18"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          >
                                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                            <polyline points="14 2 14 8 20 8" />
                                          </svg>
                                        </div>
                                        <div className="min-w-0 flex-1">
                                          <p className="truncate text-[12px] font-bold text-gray-800 dark:text-gray-200">
                                            {attachment.name}
                                          </p>
                                          <p
                                            className={`mt-0.5 text-[10px] font-semibold ${attachment.status === "failed" ? "text-red-500" : attachment.status === "ready" ? "text-emerald-600 dark:text-emerald-400" : "text-gray-400"}`}
                                          >
                                            {attachment.status === "failed"
                                              ? attachment.error ||
                                                "문서 처리에 실패했습니다."
                                              : [
                                                  fileExtension(
                                                    attachment.name,
                                                  ).toUpperCase(),
                                                  formatFileSize(
                                                    attachment.file.size,
                                                  ),
                                                  attachmentStatusLabel(
                                                    attachment,
                                                  ),
                                                ]
                                                  .filter(Boolean)
                                                  .join(" · ")}
                                          </p>
                                        </div>
                                        {attachment.status === "failed" && (
                                          <button
                                            type="button"
                                            onClick={() =>
                                              retryAttachment(
                                                attachment.localId,
                                              )
                                            }
                                            disabled={isUploadingAttachments}
                                            className="rounded-full px-2 py-1 text-[10px] font-bold text-seexr-primary hover:bg-seexr-solid/10 disabled:opacity-50"
                                          >
                                            재시도
                                          </button>
                                        )}
                                        <button
                                          type="button"
                                          onClick={() =>
                                            removeAttachment(index)
                                          }
                                          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-red-500 dark:hover:bg-[#2a2a2e]"
                                          aria-label="문서 제거"
                                        >
                                          <svg
                                            width="13"
                                            height="13"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2.5"
                                            strokeLinecap="round"
                                          >
                                            <line
                                              x1="18"
                                              y1="6"
                                              x2="6"
                                              y2="18"
                                            />
                                            <line
                                              x1="6"
                                              y1="6"
                                              x2="18"
                                              y2="18"
                                            />
                                          </svg>
                                        </button>
                                      </div>
                                    ),
                                  )}
                                </div>
                              )}
                            </div>
                          ) : isProcessField ? (
                            <div className="relative">
                              <input
                                type="text"
                                value={fieldValue}
                                onChange={(e) =>
                                  updateClassicField(field.key, e.target.value)
                                }
                                onFocus={() =>
                                  openClassicSuggestions(field.key)
                                }
                                onBlur={deferCloseClassicSuggestions}
                                placeholder={processPlaceholder}
                                className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-[#2a2a2e] bg-white dark:bg-[#1a1a1c] text-[14px] font-medium text-gray-900 dark:text-gray-100 outline-none focus:border-seexr-primary/50 focus:ring-2 focus:ring-seexr-primary/20 transition-all placeholder:text-gray-400"
                              />
                              <ClassicSuggestionPanel
                                isOpen={isSuggestionOpen}
                                options={processSuggestions}
                                selectedValue={fieldValue}
                                onSelect={(option) => {
                                  updateClassicField(field.key, option);
                                  closeClassicSuggestions();
                                }}
                              />
                            </div>
                          ) : field.type === "multiBadge" ? (
                            <div className="relative">
                              <div className="flex min-h-[44px] flex-wrap items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-2.5 py-2 text-[13px] transition-all focus-within:border-seexr-primary/50 focus-within:ring-2 focus-within:ring-seexr-primary/20 dark:border-[#2a2a2e] dark:bg-[#1a1a1c]">
                                {tokenValues.map((token) => (
                                  <span
                                    key={token}
                                    className={`inline-flex min-h-[26px] items-center gap-1 rounded-full border px-2.5 py-1 text-[12px] font-bold ${badgeClass}`}
                                  >
                                    {token}
                                    <button
                                      type="button"
                                      onClick={() =>
                                        removeClassicToken(field.key, token)
                                      }
                                      className="ml-0.5 text-current opacity-60 hover:opacity-100"
                                      aria-label={`${token} 제거`}
                                    >
                                      ×
                                    </button>
                                  </span>
                                ))}
                                <input
                                  type="text"
                                  value={tokenDraft}
                                  onChange={(e) =>
                                    updateClassicDraft(
                                      field.key,
                                      e.target.value,
                                    )
                                  }
                                  onFocus={() =>
                                    openClassicSuggestions(field.key)
                                  }
                                  onBlur={() => {
                                    deferCloseClassicSuggestions();
                                    if ((classicDrafts[field.key] || "").trim())
                                      addClassicToken(
                                        field.key,
                                        classicDrafts[field.key],
                                      );
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === ",") {
                                      e.preventDefault();
                                      addClassicToken(field.key, tokenDraft);
                                    } else if (
                                      e.key === "Backspace" &&
                                      !tokenDraft &&
                                      tokenValues.length > 0
                                    ) {
                                      removeClassicToken(
                                        field.key,
                                        tokenValues[tokenValues.length - 1],
                                      );
                                    }
                                  }}
                                  placeholder={
                                    tokenValues.length === 0
                                      ? field.placeholder
                                      : ""
                                  }
                                  className="min-w-[120px] flex-1 bg-transparent px-1 py-1 text-[14px] font-medium text-gray-900 outline-none placeholder:text-gray-400 dark:text-gray-100"
                                />
                              </div>
                              <ClassicSuggestionPanel
                                isOpen={isSuggestionOpen}
                                options={tokenSuggestions}
                                onSelect={(option) => {
                                  addClassicToken(field.key, option);
                                  openClassicSuggestions(field.key);
                                }}
                              />
                            </div>
                          ) : field.type === "number" ? (
                            <input
                              type="number"
                              min={classicConfig.numberLimits?.[field.key]?.min}
                              max={classicConfig.numberLimits?.[field.key]?.max}
                              step={1}
                              value={fieldValue}
                              onChange={(e) =>
                                updateClassicField(field.key, e.target.value)
                              }
                              placeholder={field.placeholder}
                              className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-[#2a2a2e] bg-white dark:bg-[#1a1a1c] text-[14px] font-medium text-gray-900 dark:text-gray-100 outline-none focus:border-seexr-primary/50 focus:ring-2 focus:ring-seexr-primary/20 transition-all placeholder:text-gray-400"
                            />
                          ) : (
                            <input
                              type="text"
                              value={fieldValue}
                              onChange={(e) =>
                                updateClassicField(field.key, e.target.value)
                              }
                              placeholder={field.placeholder}
                              className="w-full px-3.5 py-2.5 rounded-xl border border-gray-200 dark:border-[#2a2a2e] bg-white dark:bg-[#1a1a1c] text-[14px] font-medium text-gray-900 dark:text-gray-100 outline-none focus:border-seexr-primary/50 focus:ring-2 focus:ring-seexr-primary/20 transition-all placeholder:text-gray-400"
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-6 flex justify-end">
                    <button
                      onClick={handleClassicSubmit}
                      disabled={
                        isClassicSubmitDisabled() || isUploadingAttachments
                      }
                      className="h-[40px] px-6 rounded-full cursor-pointer bg-seexr-solid text-white font-bold text-[14px] hover:bg-seexr-hover transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
                    >
                      분석 시작
                    </button>
                  </div>
                </div>
              </div>
            ) : isSpecializedAgent && classicConfig ? (
              <div className="w-full max-w-[720px] flex flex-col items-center gap-4">
                {/* Toggle */}
                <div className="flex items-center gap-2 bg-gray-100 dark:bg-[#1f1f22] rounded-full p-1 border border-gray-200 dark:border-[#2a2a2e]">
                  <button className="px-4 py-1.5 rounded-full cursor-pointer text-[13px] font-bold transition-all bg-white dark:bg-[#2a2a2e] text-gray-900 dark:text-gray-100 shadow-sm">
                    Chat
                  </button>
                  <button
                    onClick={() => setIsClassicMode(true)}
                    className="px-4 py-1.5 rounded-full cursor-pointer text-[13px] font-bold transition-all text-gray-500 dark:text-gray-400"
                  >
                    Classic
                  </button>
                </div>
                {renderComposer()}
              </div>
            ) : (
              renderComposer()
            )}
          </div>
        </div>
        {renderPortalSubmenus()}

        {imageViewer && (
          <ChatImageViewer
            images={imageViewer.images}
            index={imageViewer.index}
            onClose={() => setImageViewer(null)}
            onIndexChange={(i) =>
              setImageViewer((prev) => (prev ? { ...prev, index: i } : prev))
            }
          />
        )}

        {isArtifactSelectorOpen && (
          <ArtifactSelectorModal
            onClose={() => setIsArtifactSelectorOpen(false)}
            onSelect={(art) => {
              if (!addArtifactSelection(art)) return;
              setIsArtifactSelectorOpen(false);
            }}
          />
        )}
      </div>
    );
  }

  let currentArtifactContent = "";
  let currentArtifactAttachmentIds: string[] = [];
  let currentArtifactSourceDocuments: Array<
    RiskAttachment & { kind: "source_document" }
  > = [];
  if (displayedArtifact && messages[displayedArtifact.msgIdx]) {
    const msgContent = messages[displayedArtifact.msgIdx].content;
    const regexMap2: Record<string, string> = {
      risk_assessment:
        "<risk_assessment>([\\s\\S]*?)(?:<\\/risk_assessment>|$)",
      tbm_scenario: "<tbm_scenario>([\\s\\S]*?)(?:<\\/tbm_scenario>|$)",
      tbm_record: "<tbm_record>([\\s\\S]*?)(?:<\\/tbm_record>|$)",
      cost_estimate: "<cost_estimate>([\\s\\S]*?)(?:<\\/cost_estimate>|$)",
      cost_estimate_material_change:
        "<cost_estimate_material_change>([\\s\\S]*?)(?:<\\/cost_estimate_material_change>|$)",
      cases: "<cases>([\\s\\S]*?)(?:<\\/cases>|$)",
      laws: "<laws>([\\s\\S]*?)(?:<\\/laws>|$)",
    };
    const regexStr = regexMap2[displayedArtifact.type] || regexMap2.cases;
    const match = msgContent.match(new RegExp(regexStr));
    if (match) currentArtifactContent = match[1];

    const ownMessage = messages[displayedArtifact.msgIdx];
    const ownAttachments = normalizeRiskAttachmentList(
      ownMessage?.attachments,
      ownMessage?.attachmentIds,
    );
    currentArtifactAttachmentIds = ownAttachments
      .filter((item) => item.kind === "site_photo")
      .map((item) => item.fileId);
    currentArtifactSourceDocuments = ownAttachments.filter(
      isSourceDocumentAttachment,
    );

    if (
      currentArtifactAttachmentIds.length === 0 ||
      currentArtifactSourceDocuments.length === 0
    ) {
      for (let idx = displayedArtifact.msgIdx - 1; idx >= 0; idx--) {
        const candidate = messages[idx];
        if (candidate?.role !== "user") continue;
        const candidateAttachments = normalizeRiskAttachmentList(
          candidate.attachments,
          candidate.attachmentIds,
        );
        if (currentArtifactAttachmentIds.length === 0) {
          currentArtifactAttachmentIds = candidateAttachments
            .filter((item) => item.kind === "site_photo")
            .map((item) => item.fileId);
        }
        if (currentArtifactSourceDocuments.length === 0) {
          currentArtifactSourceDocuments = candidateAttachments.filter(
            isSourceDocumentAttachment,
          );
        }
        break;
      }
    }
  }

  return (
    <div className="flex h-full w-full overflow-hidden relative">
      {/* 파일 드롭 오버레이 — 드래그 중에만 노출, pointer-events-none으로 드롭 이벤트가 루트로 전달되게 함 */}
      {isDragging && (
        <div className="absolute inset-0 z-[200] pointer-events-none flex items-center justify-center bg-seexr-solid/10 dark:bg-seexr-solid/20 backdrop-blur-sm animate-in fade-in duration-150">
          <div className="border-2 border-dashed border-seexr-primary rounded-3xl px-10 py-8 flex flex-col items-center gap-3 bg-white/95 dark:bg-[#161618]/95 shadow-2xl">
            <div className="w-12 h-12 rounded-full bg-seexr-solid/10 flex items-center justify-center">
              <svg
                width="26"
                height="26"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.25"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-seexr-primary"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17 8 12 3 7 8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
            </div>
            <div className="flex flex-col items-center gap-0.5">
              <span className="text-[15px] font-black text-gray-900 dark:text-gray-100">
                파일을 놓아서 업로드
              </span>
              <span className="text-[12px] font-medium text-gray-500 dark:text-gray-400">
                {SOURCE_DOCUMENT_AGENT_IDS.has(agentId)
                  ? "이미지 · PDF · DOC · HWP/HWPX · XLS/XLSX 지원"
                  : "이미지 파일 지원"}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Left Chat Area */}
      <div
        className={`flex flex-col ${displayedArtifact ? (isCanvasExpanded ? "hidden" : "max-md:hidden md:w-[50%]") : "w-full"} ${isDiscardingStaleArtifact ? "transition-none" : "transition-all duration-300"} min-h-0 h-full relative`}
      >
        {/* Scroll Container - Spans full height including under the sticky composer */}
        <div
          ref={scrollContainerRef}
          onScroll={() => {
            const el = scrollContainerRef.current;
            if (!el) return;
            // User scrolled up if not near bottom (within 150px)
            userScrolledUpRef.current =
              el.scrollHeight - el.scrollTop - el.clientHeight > 150;
          }}
          className="flex-1 overflow-y-auto custom-scrollbar w-full flex flex-col relative"
        >
          {/* Messages Area */}
          <div className="flex-1 flex flex-col gap-6 w-full max-w-3xl mx-auto px-4 py-8 max-md:pt-[80px]">
            {(() => {
              // 편집 버튼은 가장 마지막 유저 발화에만 노출 — 한 번만 계산
              // 어시스턴트 액션 바는 마지막 응답에 한해 항상 노출(hover 무관)
              let lastUserMsgIdx = -1;
              let lastAssistantMsgIdx = -1;
              for (let k = messages.length - 1; k >= 0; k--) {
                if (lastUserMsgIdx === -1 && messages[k]?.role === "user")
                  lastUserMsgIdx = k;
                if (
                  lastAssistantMsgIdx === -1 &&
                  messages[k]?.role === "assistant"
                )
                  lastAssistantMsgIdx = k;
                if (lastUserMsgIdx !== -1 && lastAssistantMsgIdx !== -1) break;
              }
              return messages.map((msg, i) => {
                const messageAttachments = normalizeRiskAttachmentList(
                  msg.attachments,
                  msg.attachmentIds,
                );
                const photoAttachments = messageAttachments.filter(
                  (attachment) => attachment.kind === "site_photo",
                );
                const documentAttachments = messageAttachments.filter(
                  (attachment) => attachment.kind === "source_document",
                );
                // 과거 로컬 낙관적 메시지 형태도 새로고침 전까지 안전하게 표시한다.
                const pendingAtts: Array<{
                  localId: string;
                  previewUrl?: string;
                  name: string;
                  mimeType: string;
                }> = Array.isArray(msg.pendingAttachments)
                  ? msg.pendingAttachments
                  : [];
                const imgs: ImgSrc[] =
                  photoAttachments.length > 0
                    ? photoAttachments.map((attachment) => ({
                        kind: "file" as const,
                        fileId: attachment.fileId,
                      }))
                    : pendingAtts
                        .filter((p) => p.previewUrl)
                        .map((p) => ({
                          kind: "url" as const,
                          url: p.previewUrl!,
                        }));
                const nonImagePending =
                  messageAttachments.length > 0
                    ? []
                    : pendingAtts.filter((p) => !p.previewUrl);
                const thumbClass =
                  "w-[88px] h-[88px] rounded-xl object-cover border border-gray-200 dark:border-[#2a2a2e] shadow-sm cursor-zoom-in hover:opacity-90 transition-opacity";
                const userDisplayContent =
                  msg.role === "user"
                    ? getUserMessageDisplayText(msg.content || "")
                    : "";
                return (
                  <div
                    key={i}
                    className={`flex ${msg.role === "user" ? "justify-end" : "justify-start w-full"}`}
                  >
                    {msg.role === "user" ? (
                      <div
                        className={`flex flex-col items-end gap-2 group/turn-messages ${editingMsgIdx === i ? "w-full" : "max-w-[85%]"}`}
                      >
                        {editingMsgIdx === i ? (
                          <UserMessageEditor
                            initialText={userDisplayContent}
                            onSave={(newText) => handleSaveUserEdit(i, newText)}
                            onCancel={() => setEditingMsgIdx(null)}
                          />
                        ) : (
                          <>
                            {(imgs.length > 0 ||
                              documentAttachments.length > 0 ||
                              nonImagePending.length > 0) && (
                              <div className="flex flex-wrap gap-2 justify-end">
                                {imgs.map((img, idx) => (
                                  <button
                                    key={
                                      img.kind === "url"
                                        ? `p-${idx}`
                                        : img.fileId
                                    }
                                    type="button"
                                    onClick={() =>
                                      setImageViewer({
                                        images: imgs,
                                        index: idx,
                                      })
                                    }
                                    className="block"
                                  >
                                    {img.kind === "url" ? (
                                      <img
                                        src={img.url}
                                        alt=""
                                        className={thumbClass}
                                      />
                                    ) : (
                                      <AuthImage
                                        fileId={img.fileId}
                                        className={thumbClass}
                                      />
                                    )}
                                  </button>
                                ))}
                                {nonImagePending.map((p) => (
                                  <div
                                    key={p.localId}
                                    className="w-[88px] h-[88px] rounded-xl border border-gray-200 dark:border-[#2a2a2e] shadow-sm flex flex-col items-center justify-center bg-gray-50 dark:bg-[#1f1f22] px-2"
                                  >
                                    <svg
                                      width="22"
                                      height="22"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth="2"
                                      className="text-seexr-primary mb-1"
                                    >
                                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                                      <polyline points="14 2 14 8 20 8" />
                                    </svg>
                                    <span className="text-[10px] font-semibold text-gray-700 dark:text-gray-300 truncate w-full text-center">
                                      {p.name}
                                    </span>
                                  </div>
                                ))}
                                {documentAttachments.map((attachment) => (
                                  <DocumentAttachmentCard
                                    key={attachment.fileId}
                                    attachment={attachment}
                                  />
                                ))}
                              </div>
                            )}
                            {pendingAtts.length > 0 &&
                              messageAttachments.length === 0 && (
                                <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400 font-semibold">
                                  <span className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                                  업로드 중...
                                </span>
                              )}
                            {msg.attachmentUploadFailed && (
                              <span className="text-[11px] text-red-500 font-bold">
                                첨부 업로드 실패
                              </span>
                            )}
                            {userDisplayContent && (
                              <div
                                className="flex items-center gap-1 flex-row-reverse"
                                onPointerEnter={() => setHoveredUserMsgIdx(i)}
                                onPointerLeave={() =>
                                  setHoveredUserMsgIdx((current) =>
                                    current === i ? null : current,
                                  )
                                }
                              >
                                <div className="px-5 py-3.5 rounded-3xl bg-gray-100 dark:bg-[#1f1f22] text-gray-900 dark:text-gray-100 shadow-sm border border-transparent dark:border-[#2a2a2e]">
                                  <p className="text-[15px] leading-relaxed font-medium whitespace-pre-wrap">
                                    {userDisplayContent}
                                  </p>
                                </div>
                                {!(
                                  isGenerating && i === messages.length - 1
                                ) && (
                                  <UserMessageActions
                                    text={userDisplayContent}
                                    createdAt={msg.createdAt ?? msg.created_at}
                                    visible={hoveredUserMsgIdx === i}
                                    canEdit={
                                      i === lastUserMsgIdx && !isGenerating
                                    }
                                    onEdit={() =>
                                      !isGenerating && setEditingMsgIdx(i)
                                    }
                                  />
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    ) : (
                      <div
                        className="w-full text-gray-800 dark:text-gray-200 pt-2 flex flex-col"
                        onPointerEnter={() => setHoveredAssistantMsgIdx(i)}
                        onPointerLeave={() =>
                          setHoveredAssistantMsgIdx((current) =>
                            current === i ? null : current,
                          )
                        }
                      >
                        {renderAssistantMessage(msg.content, i)}
                        {!(isGenerating && i === messages.length - 1) &&
                          msg.content && (
                            <AssistantMessageActions
                              text={extractVisibleText(msg.content)}
                              rawContent={msg.content}
                              createdAt={msg.createdAt ?? msg.created_at}
                              visible={hoveredAssistantMsgIdx === i}
                              isLatest={
                                i === lastAssistantMsgIdx && !isGenerating
                              }
                            />
                          )}
                      </div>
                    )}
                  </div>
                );
              });
            })()}
            {isGenerating && pipelineSteps.length === 0 && (
              <div className="flex justify-start w-full pt-2 pb-6">
                <div className="flex items-center gap-2">
                  <span
                    className="w-2 h-2 rounded-full bg-gray-400 animate-bounce"
                    style={{ animationDelay: "0ms" }}
                  ></span>
                  <span
                    className="w-2 h-2 rounded-full bg-gray-400 animate-bounce"
                    style={{ animationDelay: "150ms" }}
                  ></span>
                  <span
                    className="w-2 h-2 rounded-full bg-gray-400 animate-bounce"
                    style={{ animationDelay: "300ms" }}
                  ></span>
                </div>
              </div>
            )}
          </div>

          {/* Chat Input Area - Sticky at bottom */}
          <div className="sticky bottom-0 w-full shrink-0 pt-6 pb-4 px-4 bg-gradient-to-t from-background via-background via-70% to-transparent z-10">
            <div className="w-full max-w-3xl mx-auto">
              {renderComposer()}
              <p className="text-center text-[12px] text-gray-400 mt-4 font-semibold">
                {t("chat.warning")}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Artifact Canvas Area */}
      {displayedArtifact && (
        <>
          {/* Mobile Backdrop */}
          <div
            className={`md:hidden fixed inset-0 z-[55] bg-black/40 backdrop-blur-sm transition-opacity duration-400 ease-[cubic-bezier(0.2,0.8,0.2,1)] ${isArtifactVisible ? "opacity-100" : "pointer-events-none opacity-0"}`}
            onClick={closeArtifact}
          />
          <div
            style={
              isDesktopArtifact
                ? {
                    position: "fixed",
                    top: 0,
                    right: 0,
                    width: isCanvasExpanded
                      ? "calc(100vw - var(--sidebar-width-desktop, 72px))"
                      : "calc((100vw - var(--sidebar-width-desktop, 72px)) / 2)",
                    height: "100dvh",
                    zIndex: 100,
                  }
                : undefined
            }
            className={`
              ${isCanvasExpanded ? "w-full" : "md:w-[50%] md:min-w-[400px]"}
              md:border-l md:border-gray-200 dark:md:border-[#2a2a2e] flex flex-col h-full bg-white dark:bg-[#161618]
              max-md:fixed max-md:inset-x-0 max-md:bottom-0 max-md:h-[calc(100dvh_-_70px)] max-md:z-[60] max-md:rounded-t-[28px] max-md:shadow-[0_-8px_40px_rgba(0,0,0,0.15)] dark:max-md:shadow-[0_-8px_40px_rgba(0,0,0,0.5)] max-md:overflow-hidden max-md:border max-md:border-b-0 max-md:border-gray-200/50 dark:max-md:border-gray-700/50
              max-md:transition-transform max-md:duration-400 max-md:ease-[cubic-bezier(0.2,0.8,0.2,1)]
              ${isArtifactVisible ? "max-md:translate-y-0" : "pointer-events-none max-md:translate-y-full"}
            `}
          >
            <div className="flex-1 flex flex-col w-full h-full relative z-40 overflow-hidden text-clip bg-[#FAFAFA] dark:bg-[#111112]">
              <ArtifactCanvas
                key={`${displayedArtifact.msgIdx}:${displayedArtifact.type}:${messages[displayedArtifact.msgIdx]?.id ?? "pending"}`}
                title={displayedArtifact.title}
                type={displayedArtifact.type}
                xmlContent={currentArtifactContent}
                sessionId={activeSessionId}
                messageId={messages[displayedArtifact.msgIdx]?.id}
                onClose={closeArtifact}
                isExpanded={isCanvasExpanded}
                onToggleExpand={() => setIsCanvasExpanded(!isCanvasExpanded)}
                onSave={handleSaveArtifact}
                fallbackAttachmentFileIds={currentArtifactAttachmentIds}
                sourceDocuments={currentArtifactSourceDocuments}
                sessionArtifacts={sessionArtifacts}
                activeArtifactKey={`${displayedArtifact.msgIdx}:${displayedArtifact.type}`}
                RiskAssessmentReport={riskAssessmentReportComponent}
                onSelectArtifact={(key) => {
                  const artifact = sessionArtifacts.find(
                    (item) => item.key === key,
                  );
                  if (artifact)
                    setActiveArtifact({
                      msgIdx: artifact.msgIdx,
                      type: artifact.type,
                      title: artifact.title,
                    });
                }}
              />
            </div>
          </div>
        </>
      )}

      {renderPortalSubmenus()}

      {imageViewer && (
        <ChatImageViewer
          images={imageViewer.images}
          index={imageViewer.index}
          onClose={() => setImageViewer(null)}
          onIndexChange={(i) =>
            setImageViewer((prev) => (prev ? { ...prev, index: i } : prev))
          }
        />
      )}

      {isArtifactSelectorOpen && (
        <ArtifactSelectorModal
          onClose={() => setIsArtifactSelectorOpen(false)}
          onSelect={(art) => {
              if (!addArtifactSelection(art)) return;
              setIsArtifactSelectorOpen(false);
            }}
        />
      )}
    </div>
  );
}
